// ============================================================================
// THE DATA CONTRACT — demo.ts
//
// A deterministic in-memory LifeOS. This is what runs with zero configuration:
// clone, npm install, npm run dev, and the app is fully populated behind a
// "Demo data" badge. No Supabase account, no Cloudflare account, no keys.
//
// DETERMINISTIC IS THE WHOLE POINT. There is no Date.now(), no Math.random(),
// and no new Date() without an explicit seed date anywhere below. Everything is
// derived from SEED_TODAY and seededUnit(). If that ever stops being true, every
// future visual-regression baseline fails on every run and someone deletes them.
//
// CONTENT SOURCE: docs/design/SCREEN-CONTENT.md. The courses are the owner's real
// Fall 2026 enrolment. Meeting times, room numbers, instructor names, grades and
// every deadline are PLACEHOLDERS — docs/design/SCREEN-CONTENT.md says so, and
// docs/IMPLEMENTATION-PLAN.md says real ones arrive with the Canvas connector at
// V2. Nothing in the app depends on these strings.
// ============================================================================

import {
  addDays,
  courseSlug,
  daysBetween,
  lectureCitation,
  lectureRef,
  monthName,
  msToTimestamp,
  seededUnit,
  startOfWeek,
  toDate,
  toISODate,
  weekdayName,
  weekdayOf,
  type Announcement,
  type Assignment,
  type CaptureToken,
  type ClassOccurrence,
  type Connector,
  type ConnectionsScenario,
  type Course,
  type DataStatus,
  type DesktopWorkerStatus,
  type StorageNote,
  type Flashcard,
  type ISODate,
  type LectureSummary,
  type PracticeQuestion,
  type CourseFile,
  type SyllabusDocument,
  type SyllabusExtract,
  type InterestSave,
  type InterestTopic,
  type ISOTimestamp,
  type SaveSource,
  type SaveSummary,
  type JournalEntry,
  type LearningItem,
  type LearningResource,
  type LearningStreak,
  type LearningTrack,
  type Lecture,
  type SyncResult,
  type SyncStatusRow,
  type Task,
  type Transcript,
  type TranscriptSegment,
  type TranscriptSpeaker,
  type Weekday,
} from '@app/shared'

// Calendar (docs/design/calendar-brief.md). Its own import line rather than more names in the
// block above: parallel lanes are editing this file and a one-identifier edit inside a shared
// import list is a merge conflict for no benefit.
import {
  dueLabel,
  type CalendarAllDayEvent,
  type CalendarBlock,
  type CalendarDueItem,
  type CalendarRange,
  type CalendarWritableSource,
} from '@app/shared'

import type {
  AnnouncementFilter,
  AssignmentFilter,
  Clock,
  DataAPI,
  DateRange,
  JournalDayContext,
  JournalEntryInput,
  InterestSaveDetail,
  InterestsOverview,
  SearchDomain,
  SearchHit,
  SearchHitKind,
  SearchRelatedItem,
  SearchResults,
  JournalHeatDay,
  JournalMonth,
  JournalVoiceMemo,
  JournalStats,
  LearningLibraryEntry,
  LearningPace,
  LearningPacePoint,
  LearningSession,
  LearningSessionInput,
  LearningTrackDetail,
  LearningTrackLibrary,
  LearningTrackSummary,
  LearningUnit,
  LearningUnitItem,
  LearningUnitState,
  LectureTranscript,
  SaveFilter,
  StudySet,
  TaskFilter,
  TaskInput,
  TaskPatch,
} from './types'
// Calendar's two write shapes. Its own line, for the merge reason stated above the @app/shared
// calendar import.
import type { CalendarBlockInput, CalendarBlockPatch } from './types'
// The phone lecture upload (`/record`). Same reason for the separate line.
import type { CapturedLecture } from './types'
// Notifications · Web Push (NOTIF-1b). Its own line, same convention.
import type {
  NotificationFilter,
  NotificationRecord,
  NotificationSettings,
  NotificationSettingsPatch,
  PushDevice,
  PushSubscribeInput,
  PushTestResult,
} from './types'
// The durable upload queue (REC-1b). `UploadApiError` is a value, so it is a plain import.
import { UploadApiError } from './types'
import type { UploadBlobResult, UploadRegistration, UploadState } from './types'
// The phone's own uploader's credential (REC-1, 2026-09-24). Its own line, same convention.
import type { CreatedCaptureToken } from './types'
// Mail · Google accounts for read-only Gmail (Day page, Lane 1a). Same convention.
import type { MailAccount, MailDisconnectResult } from './types'
// Mail · what the sync read (Lane 1b). Same convention.
import type { MailMessage, MailMessageFilter, MailMessagePage } from './types'
// Mail · triage, grading and rules (Day page prototype). Same convention.
import type {
  MailCorrectionReason,
  MailItemState,
  MailRule,
  MailRuleInput,
  MailTriage,
  MailTriageItem,
  MailTriageRunResult,
  MailVerdict,
} from './types'
// Mail · the designed Day page (Lane 4a). Same convention.
import type { DayScenario, MailAudit, MailTriageAccount, MailTriageQuery } from './types'
// Mail · next steps (Lane 6). Same convention.
import type { MailProposal, MailProposalDecision, MailProposalKind, MailProposalStatus } from './types'

/**
 * The one sentence all four upload refusals say, so they cannot drift apart.
 *
 * Demo mode is not a degraded account — it is a dataset with no R2 bucket and no GPU behind it.
 * Saying so is honest; accepting a file and dropping it is not, and neither is claiming a
 * transcription that no machine will ever run.
 */
const NO_UPLOAD_PATH =
  'Demo mode has no upload path: a recording goes to R2 and is transcribed by the desktop PC, ' +
  'and neither exists without an account. The recordings listed here are sample data.'

// ── The seed ────────────────────────────────────────────────────────────────

/** Demo "today": Wednesday, 16 September 2026. Verified weekday, not recalled. */
export const SEED_TODAY: ISODate = '2026-09-16'

/** Demo "now": 08:41. Puts the 10:00 lecture at "in 1h 19m" on the Today screen. */
export const SEED_CLOCK_MINUTES = 8 * 60 + 41

/**
 * Fall 2026 at Berkeley. PLACEHOLDER dates until the Canvas connector lands (V2):
 * instruction begins Wednesday 26 Aug 2026, Labor Day (first Monday of September,
 * computed: 2026-09-07) is a campus holiday with no instruction.
 */
export const TERM = {
  name: 'Fall 2026',
  instructionStarts: '2026-08-26' as ISODate,
  instructionEnds: '2026-12-11' as ISODate,
  holidays: ['2026-09-07'] as ISODate[],
}

const isHoliday = (date: ISODate) => TERM.holidays.includes(date)

// ── Courses (real enrolment; times/rooms/instructors are placeholders) ──────
//
// ORDER IS THE HANDOFF'S, NOT THE ALPHABET'S. LifeOS School.dc.html option 6a lays the 2×2 grid
// out as COGSCI 175 · NEU C61 · LINGUIS C142 · COGSCI 1, and the owner learns that grid by
// position over a semester. The API returns them in this order and the screen does not re-sort:
// a grid that reshuffles itself when a grade changes is a grid you have to read every time.
//
// Grades, letters and the hidden state are the mockup's own (6a, and the grade-visible header
// specimen under 6b). The LETTER is stored, never derived: Canvas returns
// `computed_current_grade` alongside the score, and working 'A−' out of 92.1 would be inventing
// a per-course grading scale.

const COURSES: Course[] = [
  {
    id: 'course-cogsci175',
    code: 'COGSCI 175',
    name: 'Mind, Machine, and Meaning',
    instructor: 'Prof. M. Rhodes',
    units: 3,
    term: TERM.name,
    meetings: [
      { kind: 'lecture', weekday: 2, startMinutes: 840, endMinutes: 930, location: 'Wheeler 212' },
      { kind: 'lecture', weekday: 4, startMinutes: 840, endMinutes: 930, location: 'Wheeler 212' },
    ],
    // The hidden-grade state, designed on purpose: when an instructor sets
    // hide_final_grades the Canvas API returns nothing, and the UI must say so
    // rather than render a blank (docs/design/SCREEN-CONTENT.md).
    // A hidden grade and an ungraded course are NOT the same row: `gradeHidden` says a score
    // exists and is withheld, `gradeSoFar: null` alone would say nothing is graded yet. The UI
    // has a different sentence for each (packages/shared/src/school.ts → CourseGrade).
    gradeSoFar: null,
    gradeLetter: null,
    gradeHidden: true,
    unreadAnnouncements: 2,
  },
  {
    id: 'course-neuc61',
    code: 'NEU C61',
    name: 'Brain, Mind, and Behavior',
    instructor: 'Prof. H. Bassi',
    units: 3,
    term: TERM.name,
    meetings: [
      { kind: 'lecture', weekday: 1, startMinutes: 720, endMinutes: 780, location: 'VLSB 2050' },
      { kind: 'lecture', weekday: 3, startMinutes: 720, endMinutes: 780, location: 'VLSB 2050' },
      { kind: 'lecture', weekday: 5, startMinutes: 720, endMinutes: 780, location: 'VLSB 2050' },
      { kind: 'discussion', weekday: 5, startMinutes: 540, endMinutes: 600, location: 'Dwinelle 235' },
    ],
    gradeSoFar: 88.4,
    gradeLetter: 'B+',
    gradeHidden: false,
    unreadAnnouncements: 1,
  },
  {
    id: 'course-linguisc142',
    code: 'LINGUIS C142',
    name: 'Language and Thought',
    instructor: 'Prof. S. Renner',
    units: 3,
    term: TERM.name,
    meetings: [
      { kind: 'lecture', weekday: 1, startMinutes: 930, endMinutes: 1020, location: 'Barrows 118' },
      { kind: 'lecture', weekday: 3, startMinutes: 930, endMinutes: 1020, location: 'Barrows 118' },
    ],
    gradeSoFar: 94.0,
    gradeLetter: 'A',
    gradeHidden: false,
    unreadAnnouncements: 0,
  },
  {
    id: 'course-cogsci1',
    code: 'COGSCI 1',
    name: 'Introduction to Cognitive Science',
    instructor: 'Prof. J. Whitney',
    units: 4,
    term: TERM.name,
    meetings: [
      { kind: 'lecture', weekday: 1, startMinutes: 600, endMinutes: 660, location: 'Dwinelle 155' },
      { kind: 'lecture', weekday: 3, startMinutes: 600, endMinutes: 660, location: 'Dwinelle 155' },
      { kind: 'lecture', weekday: 5, startMinutes: 600, endMinutes: 660, location: 'Dwinelle 155' },
    ],
    gradeSoFar: 92.1,
    gradeLetter: 'A−',
    gradeHidden: false,
    unreadAnnouncements: 1,
  },
]

function classOccurrences(date: ISODate): ClassOccurrence[] {
  if (date < TERM.instructionStarts || date > TERM.instructionEnds) return []
  if (isHoliday(date)) return []
  const wd: Weekday = weekdayOf(date)
  const out: ClassOccurrence[] = []
  for (const c of COURSES) {
    for (const m of c.meetings) {
      if (m.weekday !== wd) continue
      out.push({
        courseId: c.id,
        courseCode: c.code,
        courseName: c.name,
        kind: m.kind,
        date,
        startMinutes: m.startMinutes,
        endMinutes: m.endMinutes,
        location: m.location,
      })
    }
  }
  return out.sort((a, b) => a.startMinutes - b.startMinutes)
}

// ── Lectures ────────────────────────────────────────────────────────────────
//
// Generated from each course's own lecture meetings so the lecture NUMBER always
// matches its DATE — a citation that says "Lecture 5" and a calendar that
// disagrees is exactly the kind of incoherence demo data is supposed to not have.

interface LectureSeed {
  status: Lecture['status']
  durationSec: number | null
  title: string
}

/** Keyed `${courseId}|${date}`. Anything not listed is 'not_recorded'. */
const LECTURE_SEEDS: Record<string, LectureSeed> = {
  'course-cogsci175|2026-09-03': {
    // PROCESSING. The first transcript of this one came back with the room mic on the wrong
    // input, so it was re-queued on the evening of Sep 15. The desktop worker picked it up at
    // 22:10 and went offline at 22:14, which is why it is still marked `processing` and not
    // `audio_uploaded`: the job was started and never finished. A pipeline with four states has
    // four states because this actually happens.
    status: 'processing',
    durationSec: 5160,
    title: 'Minds as symbol systems',
  },
  'course-cogsci175|2026-09-08': {
    status: 'transcribed',
    durationSec: 5100,
    title: 'Representation without a homunculus',
  },
  'course-cogsci175|2026-09-10': {
    status: 'transcribed',
    durationSec: 5220,
    title: 'The Chinese Room and symbol grounding',
  },
  'course-cogsci175|2026-09-15': {
    // AUDIO UPLOADED. Recorded yesterday, uploaded, still in the queue — the state today's
    // journal entry describes in its own words ("Uploaded the recording; it's still sitting in
    // the queue"). This is the ONE the pc_worker row counts as waiting.
    status: 'audio_uploaded',
    durationSec: 5040,
    title: 'Extended mind: Otto, Inga, and the notebook',
  },
  'course-neuc61|2026-09-14': {
    status: 'transcribed',
    durationSec: 3240,
    title: 'Synaptic plasticity, part 2',
  },
  'course-linguisc142|2026-09-14': {
    status: 'transcribed',
    durationSec: 4740,
    title: 'Whorf, and what a strong claim would have to look like',
  },
  // All four pipeline states are seeded on COGSCI 175 alone, because the course page renders
  // them in one list and a state with no row is a state whose UI never gets designed:
  //   not_recorded    Aug 27 and Sep 1 — the term started before the habit did
  //   transcribed     Sep 8, Sep 10     — Sep 10 is the study-mode exemplar
  //   processing      Sep 3   — started, never finished; see above
  //   audio_uploaded  Sep 15  — queued behind the offline worker
  // `transcribed` and "has a transcript" mean the same thing in this dataset, and demo.test.ts
  // holds them together: a lecture chipped "transcribed" whose transcript comes back null is a
  // dead link dressed as a finished job.
  // The pc_worker sync row's "lectures waiting" counts BOTH of the unfinished ones, which is
  // what demo.test.ts asserts: two numbers that disagree is how demo data stops being trusted.
}

function buildLectures(): Lecture[] {
  const out: Lecture[] = []
  for (const course of COURSES) {
    const lectureDays = new Set(
      course.meetings.filter((m) => m.kind === 'lecture').map((m) => m.weekday),
    )
    let index = 0
    for (let d = TERM.instructionStarts; d <= SEED_TODAY; d = addDays(d, 1)) {
      if (isHoliday(d)) continue
      if (!lectureDays.has(weekdayOf(d))) continue
      index += 1
      const seed = LECTURE_SEEDS[`${course.id}|${d}`]
      out.push({
        id: `lecture-${course.id}-${d}`,
        courseId: course.id,
        courseCode: course.code,
        index,
        title: seed?.title ?? `${course.code} · Lecture ${index}`,
        date: d,
        durationSec: seed?.durationSec ?? null,
        status: seed?.status ?? 'not_recorded',
        audioLocation:
          seed?.status === 'transcribed'
            ? 'archived_on_pc'
            : seed?.status === 'audio_uploaded' || seed?.status === 'processing'
              ? 'cloud'
              : 'none',
      })
    }
  }
  return out
}

const LECTURES = buildLectures()

// ── Recordings, as the /record screen sees them ─────────────────────────────
//
// Two facts this list carries that `buildLectures()` structurally cannot:
//
//   1. A recording with NO COURSE. The desktop worker's drop folder knows a filename and nothing
//      else, so everything backfilled through it lands unfiled — and "assign it later" is only an
//      honest offer if the demo world contains something to assign. This row is that something.
//   2. MUTABILITY. Assigning a course is the one write this screen makes; a demo that computed
//      the list fresh every read would swallow it, and the owner would be looking at a control
//      that does nothing. So the array is built once and the assign method edits it in place.
//
// Nothing here is invented beyond that: the rest is the same seeded lectures the School screens
// draw, narrowed to the ones that actually have audio.
const CAPTURED_LECTURES: CapturedLecture[] = [
  {
    id: 'lecture-unfiled-drop-folder',
    courseCode: null,
    title: 'Voice 013.m4a',
    index: null,
    date: addDays(SEED_TODAY, -1),
    durationSec: 4_140,
    status: 'audio_uploaded',
    audioLocation: 'cloud',
    hasTranscript: false,
  },
  ...LECTURES.filter((lecture) => lecture.status !== 'not_recorded')
    .map(
      (lecture): CapturedLecture => ({
        id: lecture.id,
        courseCode: lecture.courseCode,
        title: lecture.title,
        index: lecture.index,
        date: lecture.date,
        durationSec: lecture.durationSec,
        status: lecture.status,
        audioLocation: lecture.audioLocation,
        hasTranscript: lecture.status === 'transcribed',
      }),
    )
    .sort((a, b) => (a.date < b.date ? 1 : -1)),
]

// ── Transcripts ─────────────────────────────────────────────────────────────
//
// THE CITATION CONVENTION (docs/research/backend-validation.md §4): transcripts are
// stored per SEGMENT with start_ms/end_ms, never as one blob. Without it a chat
// answer can only say "Lecture 5" instead of "Lecture 5 · 12:31", and the whole
// acceptance test ("what did the professor say about the midterm") degrades.
//
// The segments below are an EXCERPT of a longer transcript — indexes start at 41,
// which is why `index` is stored rather than inferred from array position.

// ── WHO WAS SPEAKING, IN BOTH OF ITS STATES ─────────────────────────────────
//
// Exactly one seed below is DIARIZED: its segments carry the labels the desktop worker actually
// writes (`professor`, `speaker-2` — never a person's name, because nothing identifies anyone)
// and it has a `speakers` summary. The other three are NOT, and their `speaker` is null with a
// null summary. That pairing is deliberate and is the whole reason demo mode carries this data:
// every transcript recorded before 2026-09-02 is in the second state, and a fixture that was
// uniformly labelled would let the first honest-degradation bug ship to production.
//
// Null here means "nobody worked out who was speaking". It renders as NOTHING — no header, no
// "unknown speaker" filler. Hidden ≠ missing.
interface TranscriptSeed {
  lectureId: string
  wordCount: number
  /** `speaker` is null on an undiarized recording — never a placeholder name. */
  segments: Array<[index: number, startMs: number, endMs: number, speaker: string | null, text: string]>
  /**
   * The voices, ordered by talk time descending, or null when nothing worked them out. The
   * dominant voice is called `professor` by rule, not by recognition — see @app/shared's
   * `TranscriptSpeaker`, and the "(likely)" the transcript panel prints.
   */
  speakers?: TranscriptSpeaker[]
}

const TRANSCRIPT_SEEDS: TranscriptSeed[] = [
  {
    lectureId: 'lecture-course-cogsci175-2026-09-10',
    wordCount: 9412,
    segments: [
      [41, 724_000, 736_400, 'professor',
        "So the question Searle is pressing isn't whether the machine is fast enough. It's whether anything in the room understands Chinese, and he wants you to notice that the man in the room certainly doesn't."],
      [42, 736_400, 751_900, 'professor',
        "The systems reply says: fine, the man doesn't understand, but the man plus the rulebook plus the paper — the whole system — does. Searle's answer is to have the man memorise the rulebook, so now the system is inside one head, and he still says he understands nothing."],
      [43, 751_900, 768_200, 'speaker-2',
        "But if he memorised every rule, how would we ever tell the difference from the outside?"],
      [44, 768_200, 784_600, 'professor',
        "You wouldn't, and that's exactly the fork in the road. Either understanding is fixed by behaviour, in which case the Turing test settles it, or understanding is something over and above behaviour, in which case we owe an account of what that something is."],
      [45, 784_600, 801_300, 'professor',
        "Harnad's version of the worry is sharper and it's the one I want on the midterm. He calls it the symbol grounding problem: the symbols in the system are only meaningful because we, on the outside, interpret them."],
      [46, 801_300, 818_700, 'professor',
        "Learning Chinese from a Chinese-only dictionary is his image for it. Every definition sends you to more symbols. There is no exit from the symbol circle unless some of the symbols are tied to something that isn't a symbol — perception, action, a body."],
      [47, 818_700, 833_100, 'professor',
        "That is why embodied cognition shows up in this course right after this lecture and not somewhere decorative at the end. It's a proposed answer to grounding, not a separate topic."],
      [48, 833_100, 850_000, 'professor',
        "For Thursday, read the Clark and Chalmers piece. And for the essay: I don't want a summary of the Chinese Room. I want you to say what would have to be true for the systems reply to work, and then whether you believe it."],
    ],
    // THE ONLY DIARIZED SEED. Talk times are of detected speech, so the shares sum to 1 — silence
    // belongs to nobody and is not in the denominator. 88% for the lecturer with one student
    // audible near the microphone is what a real recording of this class looks like.
    speakers: [
      { label: 'professor', role: 'professor', talkSec: 4064.5, share: 0.883 },
      { label: 'speaker-2', role: 'other', talkSec: 538.6, share: 0.117 },
    ],
  },
  {
    // The second COGSCI 175 transcript. It exists because `transcribed` has to mean a transcript
    // is actually there: two lectures wear the chip, so two lectures have segments.
    lectureId: 'lecture-course-cogsci175-2026-09-08',
    wordCount: 8760,
    segments: [
      [22, 1_204_000, 1_219_600, null,
        "A representation, on the classical story, is a symbol that stands for something. Fine. But stands for it TO WHOM? That is the question that will not go away this semester."],
      [23, 1_219_600, 1_236_200, null,
        "If you answer 'to an inner observer who reads the symbol', you have put a little person inside the head, and now you have to explain that person. Dennett's word for this is the homunculus, and it is a debt, not an explanation."],
      [24, 1_236_200, 1_249_800, null,
        "So is the answer that there is no reader, just more processing?"],
      [25, 1_249_800, 1_268_400, null,
        "That is the standard move, yes — you discharge the homunculus by replacing it with a committee of stupider ones, and you keep going until the ones at the bottom are so stupid that a machine could be them."],
      [26, 1_268_400, 1_287_000, null,
        "Hold onto how much that move assumes. It works only if meaning can be built out of parts that have none. Thursday we will meet the person who thinks it cannot."],
    ],
  },
  {
    // NEU C61, so the overview screen has transcription status on more than one course.
    lectureId: 'lecture-course-neuc61-2026-09-14',
    wordCount: 6180,
    segments: [
      [12, 902_000, 918_400, null,
        "Long-term potentiation is not a metaphor for learning. It is a measurable change in how much depolarisation you get for the same presynaptic input, and it lasts hours."],
      [13, 918_400, 933_700, null,
        "The requirement is coincidence. Glutamate binds, but the magnesium block on the NMDA receptor only clears if the postsynaptic cell is already depolarised. Two things at once, or nothing happens."],
      [14, 933_700, 949_200, null,
        "Is that where the Hebb line comes from — cells that fire together, wire together?"],
      [15, 949_200, 964_800, null,
        "That is exactly where it comes from, and it is why the receptor is often called a coincidence detector. Hebb wrote it down in 1949 with no idea what the molecule would turn out to be."],
      [16, 964_800, 981_300, null,
        "For the problem set: I want the calcium step spelled out. Most of the marks people lose are for stopping at 'calcium enters' as if that explained anything."],
    ],
  },
  {
    lectureId: 'lecture-course-linguisc142-2026-09-14',
    wordCount: 7420,
    segments: [
      [8, 486_000, 502_500, null,
        "The strong Sapir–Whorf claim is that language determines thought. Almost nobody defends it, and the reason is that it is not clear what would count as evidence for it."],
      [9, 502_500, 519_000, null,
        "The weak claim — that language influences habitual thought — is defensible, testable, and much less exciting, which is why it keeps getting reported as the strong one."],
      [10, 519_000, 534_800, null,
        "Isn't the colour-term research supposed to settle this?"],
      [11, 534_800, 552_400, null,
        "It settles a version of it. Speakers of languages that cut the blue-green space differently are measurably faster at discriminating across their own boundary. That is an influence on perception under time pressure, not a different world."],
      [12, 552_400, 569_100, null,
        "So for the outline: pick the weak claim, name the experiment you would run, and say what result would make you abandon it. That last part is the one people skip."],
    ],
  },
]

/**
 * Whisper's confidence in a demo segment — derived from its index rather than typed into every
 * tuple above, so the seed data stays about what was SAID.
 *
 * The values sit between -0.45 and -0.15, which is the band a clean lecture recording actually
 * produces (negative log-probabilities; closer to 0 is surer). Spread by a small integer hash so
 * the set does not read as one repeated number, and deterministic because the whole demo dataset
 * is — no Math.random, no clock (demo.test.ts fails the file if either appears).
 *
 * ONE IN NINE COMES BACK NULL, and that is the point of the function existing at all. Null means
 * "the transcriber reported no confidence" — every transcript recorded before the column existed
 * looks like this — and demo mode has to exercise that branch, or the first honest-degradation
 * bug ships to production because the fixture was uniformly perfect. Null is never 0 here: 0
 * would mean perfect certainty.
 */
function demoAvgLogprob(index: number): number | null {
  if (index % 9 === 0) return null
  const spread = ((index * 37) % 31) / 30 // 0…1, deterministic, no obvious pattern by eye
  return Math.round((-0.45 + spread * 0.3) * 10_000) / 10_000
}

interface DemoTranscript {
  transcript: Transcript
  segments: TranscriptSegment[]
}

function buildTranscripts(): Map<string, DemoTranscript> {
  const byLecture = new Map<string, DemoTranscript>()
  for (const seed of TRANSCRIPT_SEEDS) {
    const lecture = LECTURES.find((l) => l.id === seed.lectureId)
    if (!lecture) continue
    const transcriptId = `transcript-${lecture.id}`
    byLecture.set(lecture.id, {
      transcript: {
        id: transcriptId,
        source: 'lecture',
        lectureId: lecture.id,
        wordCount: seed.wordCount,
        language: 'en',
        // The transcriber is named on the lecture page ('9,412 words · Whisper large-v3'),
        // because a transcript is a machine-made artifact like every other one on this screen.
        model: 'Whisper large-v3',
        // Undefined in the seed becomes null here, never an empty array: an empty list would be
        // the claim "there were no voices in this lecture", which is not what a missing
        // diarization pass says.
        speakers: seed.speakers ?? null,
        // Transcribed the same evening — the pipeline's promise is "record, walk home, it's there".
        createdAt: `${lecture.date}T19:12:00-07:00`,
      },
      segments: seed.segments.map(([index, startMs, endMs, speaker, text]) => ({
        id: `${transcriptId}-seg-${index}`,
        transcriptId,
        index,
        startMs,
        endMs,
        speaker,
        text,
        avgLogprob: demoAvgLogprob(index),
      })),
    })
  }
  return byLecture
}

const TRANSCRIPTS = buildTranscripts()

// ── Journal ─────────────────────────────────────────────────────────────────

/** Rotating prompt pool. The prompt for a day is chosen by hashing the date — stable forever. */
export const JOURNAL_PROMPTS = [
  "What's taking up mental space right now?",
  'What did you learn today?',
  'What went better than expected?',
  'What are you avoiding, and why?',
  'Who did you talk to today, and what stuck?',
  'What would make tomorrow easier?',
]

export function promptFor(date: ISODate): string {
  const i = Math.floor(seededUnit(`prompt:${date}`) * JOURNAL_PROMPTS.length)
  return JOURNAL_PROMPTS[Math.min(i, JOURNAL_PROMPTS.length - 1)]!
}

/**
 * [date, text, mood, energy, tags]. Gaps are deliberate — real weeks have holes.
 *
 * The voice-memo count is NOT in this tuple. It is derived from VOICE_MEMOS below, because two
 * places holding the same number is how a card ends up saying "×2" over a day with one memo.
 */
const JOURNAL_SEEDS: Array<[ISODate, string, number | null, number | null, string[]]> = [
  // ONE YEAR BEFORE THE SEEDED CLOCK. The "On this day · 1 year ago" card on both Journal screens
  // reads the same calendar date a year back, and a generated day would land there by luck — so
  // the two dates any reviewer will actually open are written by hand. The empty version of that
  // card is a real state and still renders; it just is not the one the screenshots show.
  ['2025-09-15',
    "First week of anything at Berkeley and I have already been lost in Dwinelle twice. Everyone else seems to have a system and I have a tote bag.\n\nMoved the desk under the window, which makes the room feel about forty percent less like storage. Still do not know a single person in this building.",
    3, 3, ['berkeley']],
  ['2025-09-16',
    "Told myself I would write every day this year, which is exactly the kind of thing I say in September. Writing it down anyway so that in a year I can see whether I meant it.\n\nKept thinking about whether I picked cognitive science because I like it or because it sounded like an answer at dinner. No conclusion, which is probably the honest one.",
    3, 2, ['berkeley']],
  ['2026-09-03',
    "Second week of 175 and I already like it more than I expected. Okonkwo spends the whole hour on one argument instead of racing through five, which is the opposite of how COGSCI 1 goes. Bought the wrong edition of the reader. Fixing that tomorrow.",
    4, 3, ['cogsci175']],
  ['2026-09-04',
    "Slept badly, skipped the gym, and never opened the algebra set — first day I have missed since starting. Writing it down so it stays one day and not three.",
    2, 2, ['algebra']],
  ['2026-09-05',
    "Saturday. Spent most of it testing video models — Wan and the new Kling on the same prompt. The motion falls apart at exactly the same place every time, right when something has to turn around. Saved four threads about it and I will absolutely forget I did.",
    4, 4, ['video-models']],
  ['2026-09-07',
    "Labor Day, no classes. Did the Python item and then kept going for another hour because nothing else was pulling at me. Functions finally clicked — what helped was writing the docstring first and the body second.",
    5, 4, ['python']],
  ['2026-09-08',
    "Recorded the 175 lecture for the first time. Felt slightly criminal until I remembered I asked. The whole class was about whether representation needs an interpreter and I want to come back to the last ten minutes.",
    4, 3, ['cogsci175']],
  ['2026-09-09',
    "Mostly admin. Canvas notifications set up, LINGUIS reading response half-written. Nothing to report, which is itself the report.",
    3, null, []],
  ['2026-09-10',
    "Chinese Room lecture. The symbol grounding framing is the part I actually needed — the idea that every definition sends you to more symbols and there's no exit unless something is tied to perception. Also he basically told us what the essay question is.",
    5, 4, ['cogsci175']],
  ['2026-09-11',
    "Long day. NEU discussion in the morning, three hours of algebra in the afternoon because I had let two days slide. Behind on the plan by about three days now and pretending otherwise is how this ends.",
    3, 2, ['algebra']],
  // Saturday. Added with the Journal port so the current streak reaches back past the weekend —
  // the index's stats row reports a streak, and a four-day one made the whole panel look like a
  // placeholder. The Sunday-the-6th gap is left alone: the archive must render no-entry days.
  ['2026-09-12',
    "Bouldering in the morning, then four hours on the LINGUIS outline that turned into two usable paragraphs. The blue V3 at Ironworks still ends the same way — I get to the third move and my feet are already wrong by the time my hands know it.",
    4, 3, ['bouldering']],
  ['2026-09-13',
    "Sunday reset. Groceries, laundry, and I re-read what I wrote on the 5th about the video models. Having it written down is the entire difference — I would have re-derived that observation from scratch in a month.",
    4, 4, []],
  ['2026-09-14',
    "Maya texted about the sublet and I still haven't answered, which is now a whole thing in my head taking up more room than the actual reply would take to write.",
    3, 3, ['maya']],
  ['2026-09-15',
    "175 on the extended mind. Otto and his notebook, which is a strange thing to hear described in a lecture hall while typing notes into a phone. Uploaded the recording; it's still sitting in the queue.\n\nAlso cleared the NEU C61 problem set that had been sitting since Wednesday. Three problems, no answer key, stuck for eleven minutes on the second one and then it just opened.\n\nThe sublet text is still unsent. I keep writing versions that explain too much — she asked a one-line question and I keep answering a paragraph she didn't ask for. Tomorrow: one sentence, before lecture.",
    4, 3, ['cogsci175', 'neuc61']],
  // Today's entry, verbatim from option 3c of the Home mockup — the composer's copy is the
  // product's voice, so it is data, not filler. Mood 4 of 5 and energy unset match the
  // mockup's dot rows exactly.
  [SEED_TODAY,
    "Mostly the COGSCI 175 reading response I'm 14 hours late on. I keep re-reading Clark & Chalmers instead of writing the response, which feels like studying but isn't. Also: Maya asked about the sublet two days ago and I've drafted the same text four times.",
    4, null, ['cogsci175']],
]

// ── The journal backfill ────────────────────────────────────────────────────
//
// The Journal index (docs/design/handoff/"LifeOS Journal.dc.html", option 5a) is an ARCHIVE: a
// stats row, a 26-week heatmap, a month picker and an "on this day, one year ago" card. None of
// those can be built against twelve hand-written days, so the year before the hand-written weeks
// is generated — deterministically, from seededUnit and nothing else.
//
// WHY GENERATED AND NOT HAND-WRITTEN: 128 entries at ~320 words is 41,000 words of prose. What
// matters for the screen is that the SHAPE is real — the gaps, the streak, the word-count spread,
// the tag distribution — and a generator gives that without 41,000 words of invented interiority.
// The most recent three weeks stay hand-written above, because those are the days any reviewer
// actually opens.
//
// The rates below are not guessed: they were solved so the dataset lands on the mockup's own
// numbers (128 entries), which is what makes the ported screen legible against the mockup.
const BACKFILL_FROM: ISODate = '2025-09-01'
const BACKFILL_TO: ISODate = '2026-08-31'
const BACKFILL_SPAN = daysBetween(BACKFILL_FROM, BACKFILL_TO)

/** Writing got steadier over the year: ~19% of days at the start, ~45% by the end. */
function wroteOn(date: ISODate): boolean {
  const progress = daysBetween(BACKFILL_FROM, date) / BACKFILL_SPAN
  return seededUnit(`wrote:${date}`) < 0.186 + 0.266 * progress
}

/**
 * The sentence pool the backfill is composed from. Written in the owner's registers — school,
 * the self-paced tracks, the apartment, the video-model rabbit hole, climbing, Berkeley — so a
 * generated day reads like a day rather than like filler.
 */
const BACKFILL_SENTENCES: string[] = [
  "Woke up before the alarm for once and used the extra forty minutes on the problem set instead of my phone, which felt like stealing time from someone.",
  "The lecture hall was too warm and I lost the thread somewhere around the second diagram, so I copied it down and told myself I would come back to it.",
  "Spent the afternoon in Moffitt on the fourth floor where nobody talks, and got more done in ninety minutes than in the whole of yesterday.",
  "Ran the same prompt through two models again to see whether the motion holds when something has to turn around, and it still does not.",
  "Wrote out the argument longhand before typing it, which is slower and somehow the only way I ever find the hole in it.",
  "Section was the good kind of uncomfortable — said a half-formed thing out loud, got pushed on it, and the pushed-on version was better.",
  "Skipped the gym and regretted it by about four in the afternoon, which is exactly the pattern I said I would stop pretending is a coincidence.",
  "Cooked properly instead of eating standing up, and the difference in how the evening went was out of all proportion to the effort.",
  "Two hours at Ironworks. Fell off the same move six times and then did it clean on the seventh without knowing what changed.",
  "The algebra set took longer than it should have because I kept checking the answer before I was actually stuck, which defeats the whole rule.",
  "Read forty pages and understood maybe thirty of them, and I am going to count that as the honest number rather than the flattering one.",
  "Called home and it was fine, and then I sat in the stairwell for ten minutes afterwards not doing anything in particular.",
  "Rewrote the same paragraph four times and the fourth one was shorter than the first, which I think is the direction it is supposed to go.",
  "Got the Python exercises done early and then kept going into the next unit because it was finally moving instead of grinding.",
  "The rain meant the walk back took twice as long and I did not mind, which is not usually how I feel about the hill.",
  "Sat down to write this and realised I could not name a single specific thing from the whole afternoon, which is its own kind of data.",
  "Kept the phone in the other room for the first three hours and the difference was not subtle, so I am writing it down before I talk myself out of it.",
  "A whole day of small administrative things that individually take four minutes and collectively take a day.",
  "Went back through what I wrote in the spring and it is strange how much of it I would have sworn I remembered and did not.",
  "The seminar reading was dense enough that I gave up on the summary and just annotated the three passages that actually mattered.",
  "Coffee with someone from the department who is a year ahead, and most of what she said was about how little anyone has figured out.",
  "Fixed the thing that has been broken on the desktop for three weeks in about eleven minutes once I finally sat down with it.",
  "Long email that could have been three sentences, and I sent the long one anyway because I did not trust the short one.",
  "The library closed before I finished, which turned out to be the only thing that would have made me stop.",
  "Late night, and the good kind — the problem opened up around eleven and I stayed with it until it was properly done.",
  "Nothing much happened and I am writing anyway, because the days I skip are always the ones I later want back.",
  "Groceries, laundry, and a long walk with no destination, which is apparently what a rest day has to look like for it to work.",
  "Watched most of a lecture recording at one and a quarter speed and had to go back twice, so that is the wrong speed for this one.",
  "Made a list of what is actually due in the next two weeks and it was shorter than the version living in my head.",
  "Kept thinking about whether I picked this because I like it or because it sounded like a good answer, and got no further than usual.",
  "Explained the idea to someone who does not do any of this, and the places I stumbled were exactly the places I do not understand it.",
  "Slept badly and it showed in everything, so I moved the hard thing to tomorrow rather than doing a bad version of it today.",
  "The bus was late, the seat was cold, and I read a whole chapter standing up, which never happens when I plan for it.",
  "Two people cancelled and the empty afternoon was the best thing that happened all week.",
  "Went down a rabbit hole on how the training data is filtered and came out three hours later with four saved threads and no conclusions.",
  "Wrote the docstring first and the body second, which is the trick that keeps working every single time I remember to use it.",
  "Ended up in an argument about whether any of this counts as understanding, which is more or less the entire course in one conversation.",
  "The hill was harder than usual and I am choosing to blame the backpack rather than the three weeks of not running.",
  "Finished the week ahead of the plan for the first time since it started, and immediately felt suspicious of it.",
  "Sat with the notebook open for twenty minutes before anything came, and then it came all at once and I nearly missed dinner.",
]

/** Tags that make sense all year, plus course tags once Fall 2026 instruction begins. */
const BACKFILL_TAGS = ['algebra', 'python', 'video-models', 'maya', 'bouldering', 'reading']

function backfillEntry(date: ISODate): { text: string; mood: number | null; energy: number | null; tags: string[] } {
  const pool = BACKFILL_SENTENCES
  // 12–18 sentences, laid out in three paragraphs. A stride co-prime with the pool length walks
  // the whole pool without repeating inside one entry.
  const count = 12 + Math.floor(seededUnit(`len:${date}`) * 7)
  const start = Math.floor(seededUnit(`start:${date}`) * pool.length)
  // The stride varies per day, and every value is co-prime with the pool length. A FIXED stride
  // made consecutive days share long runs of sentences in the same order, which is visible on the
  // index the moment two of them sit next to each other.
  const strides = [7, 11, 13, 17, 19, 23]
  const stride = strides[Math.floor(seededUnit(`stride:${date}`) * strides.length)]!
  const picked: string[] = []
  for (let i = 0; i < count; i++) picked.push(pool[(start + i * stride) % pool.length]!)

  const breakA = Math.floor(count / 3)
  const breakB = Math.floor((count * 2) / 3)
  const text = [
    picked.slice(0, breakA).join(' '),
    picked.slice(breakA, breakB).join(' '),
    picked.slice(breakB).join(' '),
  ]
    .filter(Boolean)
    .join('\n\n')

  // Mood and energy are skippable by design, so roughly one day in six carries neither. A
  // dataset where every day has a number would make the unset state impossible to design against.
  const skip = seededUnit(`skip:${date}`) < 0.17
  const mood = skip ? null : 1 + Math.floor(seededUnit(`mood:${date}`) * 5)
  const energy = skip || seededUnit(`skipE:${date}`) < 0.12
    ? null
    : 1 + Math.floor(seededUnit(`energy:${date}`) * 5)

  const tagPool =
    date >= TERM.instructionStarts ? [...BACKFILL_TAGS, 'cogsci175', 'neuc61'] : BACKFILL_TAGS
  const tagCount = seededUnit(`tagn:${date}`) < 0.34 ? 0 : seededUnit(`tagn:${date}`) < 0.82 ? 1 : 2
  const tags: string[] = []
  for (let i = 0; i < tagCount; i++) {
    const t = tagPool[Math.floor(seededUnit(`tag${i}:${date}`) * tagPool.length)]!
    if (!tags.includes(t)) tags.push(t)
  }

  return { text, mood, energy, tags }
}

// ── Voice memos ─────────────────────────────────────────────────────────────
//
// Two states on today's entry, because the queued one is the state that has to get designed:
// a memo waiting on the desktop worker names the worker, and carries no transcript at all
// rather than an empty one. Recording is the third state and belongs to a live session, so
// nothing is seeded in it — a seeded "recording" memo would be a recording that never ends.

/**
 * A seeded memo, minus the one field the dataset has no business inventing: `error` belongs to a
 * transcription that actually failed, and nothing in a fixture ever failed. `buildVoiceMemos`
 * stamps it null once, so no seed literal can quietly grow a fake reason.
 */
type SeededMemo = Omit<JournalVoiceMemo, 'error'>

const HAND_VOICE_MEMOS: SeededMemo[] = [
  {
    id: 'memo-2026-09-16-0712', date: SEED_TODAY,
    recordedAt: `${SEED_TODAY}T07:12:00-07:00`, durationSec: 64,
    status: 'transcribed', transcriptId: 'transcript-memo-2026-09-16-0712',
    transcriptText:
      '“…before I forget it — the thing Clark and Chalmers are actually claiming is not that the notebook is clever, it is that the coupling is what does the work. Say that in the response instead of summarising the paper again.”',
    paragraphIndex: 0,
  },
  {
    id: 'memo-2026-09-16-0845', date: SEED_TODAY,
    recordedAt: `${SEED_TODAY}T08:45:00-07:00`, durationSec: 52,
    status: 'queued', transcriptId: null, transcriptText: null,
    paragraphIndex: 0,
  },
  {
    // Recorded at 22:31 on the 15th — SEVENTEEN MINUTES after the desktop worker went offline
    // (see SYNC_STATUS below). That is why it is still queued, and the day page says so.
    id: 'memo-2026-09-15-2231', date: '2026-09-15',
    recordedAt: '2026-09-15T22:31:00-07:00', durationSec: 41,
    status: 'queued', transcriptId: null, transcriptText: null,
    paragraphIndex: 1,
  },
  {
    id: 'memo-2026-09-15-1940', date: '2026-09-15',
    recordedAt: '2026-09-15T19:40:00-07:00', durationSec: 82,
    status: 'transcribed', transcriptId: 'transcript-memo-2026-09-15-1940',
    transcriptText:
      '“…walking back from Moffitt and the thing I want to say in section tomorrow is that the coupling matters more than the location. Nobody cares where the memory sits, they care whether you can reach it fast enough to think with it.”',
    paragraphIndex: 0,
  },
  {
    id: 'memo-2026-09-10-2110', date: '2026-09-10',
    recordedAt: '2026-09-10T21:10:00-07:00', durationSec: 138,
    status: 'transcribed', transcriptId: 'transcript-memo-2026-09-10-2110',
    transcriptText:
      '“The exit from the symbol circle has to be perception or action — that is the whole move. Everything else in the lecture was setting that up.”',
    paragraphIndex: 0,
  },
  {
    id: 'memo-2026-09-10-2118', date: '2026-09-10',
    recordedAt: '2026-09-10T21:18:00-07:00', durationSec: 47,
    status: 'transcribed', transcriptId: 'transcript-memo-2026-09-10-2118',
    transcriptText:
      '“Essay question, near enough verbatim: what would have to be true for the systems reply to work, and do you believe it.”',
    paragraphIndex: 0,
  },
  {
    id: 'memo-2026-09-08-1902', date: '2026-09-08',
    recordedAt: '2026-09-08T19:02:00-07:00', durationSec: 95,
    status: 'transcribed', transcriptId: 'transcript-memo-2026-09-08-1902',
    transcriptText:
      '“Representation without a homunculus. The question I could not ask in the hour: who is the interpreter if there is no one inside.”',
    paragraphIndex: 0,
  },
]

/**
 * Memos on the generated backfill days. The "Has voice" filter chip on the index reports a count,
 * so memos have to exist across the whole archive rather than only in the hand-written weeks —
 * otherwise the filter is a chip that empties the screen.
 *
 * All of them are `transcribed`: the desktop worker only went offline on the 15th, and a memo from
 * last November that is still queued would be a queue nobody ever noticed.
 */
const BACKFILL_MEMO_LINES = [
  '“Note to self before this evaporates — the version I said out loud on the walk back was better than anything I have typed.”',
  '“Three things from the section: the example about the map, the objection I could not answer, and the reading he mentioned twice.”',
  '“Recording this instead of writing it because my hands are cold and the idea is small enough to lose.”',
  '“The move on the wall is a hip thing, not an arm thing. Write that down before the next session.”',
  '“Whatever I do tomorrow, the outline goes first. Everything else expands to fill whatever is left.”',
]

function backfillMemos(date: ISODate, paragraphs: number): SeededMemo[] {
  const roll = seededUnit(`memo:${date}`)
  if (roll >= 0.25) return []
  const many = roll < 0.06
  const out: SeededMemo[] = []
  for (let i = 0; i < (many ? 2 : 1); i++) {
    const minute = 12 + Math.floor(seededUnit(`memomin${i}:${date}`) * 46)
    const hour = 18 + i
    const stamp = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
    out.push({
      id: `memo-${date}-${stamp.replace(':', '')}`,
      date,
      recordedAt: `${date}T${stamp}:00-07:00`,
      durationSec: 35 + Math.round(seededUnit(`memodur${i}:${date}`) * 130),
      status: 'transcribed',
      transcriptId: `transcript-memo-${date}-${stamp.replace(':', '')}`,
      transcriptText:
        BACKFILL_MEMO_LINES[Math.floor(seededUnit(`memoline${i}:${date}`) * BACKFILL_MEMO_LINES.length)]!,
      paragraphIndex: Math.min(paragraphs - 1, Math.floor(seededUnit(`memopos${i}:${date}`) * paragraphs)),
    })
  }
  return out
}

function wordCount(text: string): number {
  const t = text.trim()
  return t ? t.split(/\s+/).length : 0
}

/** 'HH:MM' of the seeded clock — the only "now" this dataset has. */
const SEED_CLOCK_HHMM = `${String(Math.floor(SEED_CLOCK_MINUTES / 60)).padStart(2, '0')}:${String(
  SEED_CLOCK_MINUTES % 60,
).padStart(2, '0')}`

const paragraphsOf = (text: string): string[] =>
  text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)

/**
 * Every voice memo in the dataset: the hand-written ones, plus one for roughly a quarter of the
 * generated backfill days. Built before the journal map, because the entries' `voiceMemoCount` is
 * READ OFF THIS LIST rather than stored beside it — one number, one source.
 */
function buildVoiceMemos(): JournalVoiceMemo[] {
  const out: SeededMemo[] = [...HAND_VOICE_MEMOS]
  for (let d = BACKFILL_FROM; d <= BACKFILL_TO; d = addDays(d, 1)) {
    if (!wroteOn(d)) continue
    out.push(...backfillMemos(d, paragraphsOf(backfillEntry(d).text).length))
  }
  return out
    .sort((a, b) => (a.recordedAt < b.recordedAt ? -1 : 1))
    .map((memo) => ({ ...memo, error: null }))
}

const VOICE_MEMOS = buildVoiceMemos()

function buildJournal(): Map<ISODate, JournalEntry> {
  const memosByDate = new Map<ISODate, number>()
  for (const m of VOICE_MEMOS) memosByDate.set(m.date, (memosByDate.get(m.date) ?? 0) + 1)

  const add = (
    date: ISODate,
    text: string,
    mood: number | null,
    energy: number | null,
    tags: string[],
  ) => ({
    date,
    prompt: promptFor(date),
    text,
    wordCount: wordCount(text),
    mood,
    energy,
    tags,
    voiceMemoCount: memosByDate.get(date) ?? 0,
    // Past days were written in the evening; TODAY was written at the seeded clock. An entry
    // whose updatedAt is in the future is a small lie with a visible consequence — the
    // composer's chip would report a save that has not happened yet.
    updatedAt: date === SEED_TODAY ? `${date}T${SEED_CLOCK_HHMM}:00-07:00` : `${date}T21:40:00-07:00`,
  })

  const m = new Map<ISODate, JournalEntry>()
  // The generated year first, so a hand-written day always wins the same date.
  for (let d = BACKFILL_FROM; d <= BACKFILL_TO; d = addDays(d, 1)) {
    if (!wroteOn(d)) continue
    const { text, mood, energy, tags } = backfillEntry(d)
    m.set(d, add(d, text, mood, energy, tags))
  }
  for (const [date, text, mood, energy, tags] of JOURNAL_SEEDS) {
    m.set(date, add(date, text, mood, energy, tags))
  }
  return m
}

// ── Assignments ─────────────────────────────────────────────────────────────
//
// Due timestamps carry an explicit -07:00 (Pacific Daylight Time in September)
// so "11:59pm" means 11:59pm where the owner is. The displayed day is the date
// portion of the string; nothing re-derives it through UTC, which is how a
// deadline silently slides to the next day.

// THE TITLES AND COURSES BELOW ARE THE MOCKUP'S. docs/design/handoff/"LifeOS Home
// Options.dc.html" option 3c is the source of truth for what the Today screen says
// (docs/design/FIDELITY.md → "the demo dataset gets updated to carry the handoff's exact
// strings"). The DATES stay on this dataset's own coherent spine — the mockup is dated
// Monday, August 17, which is before this term's instruction even starts, so adopting its
// calendar would break the weekday, lecture-numbering and streak invariants that demo.test.ts
// asserts. Time-remaining strings are derived from these timestamps, never stored.

/**
 * A seed row: everything the mockups pin, and none of the Canvas detail the peek reads.
 *
 * WHY A SEED TYPE AND NOT EIGHTEEN MORE FIELD LISTS. `lockAt`, `gradingType`, `submissionTypes`
 * and `htmlUrl` are mechanical — they are the same shape for every row of a given kind, and
 * eighteen hand-written copies is eighteen chances for one to drift out of step with the others
 * for no expressive gain. The one field that IS expressive, the description, is written per row
 * in DESCRIPTIONS below and deliberately left absent for several: an assignment that is a title
 * and a date and nothing else is the common case in real Canvas data, and demo mode that never
 * shows it is demo mode that never exercises the peek's honest-empty state.
 */
type AssignmentSeed = Omit<
  Assignment,
  'lockAt' | 'gradingType' | 'submissionTypes' | 'htmlUrl' | 'description'
>

const ASSIGNMENT_SEEDS: AssignmentSeed[] = [
  {
    id: 'a-cogsci175-rr2', courseId: 'course-cogsci175', courseCode: 'COGSCI 175',
    title: 'Reading response 2: The Extended Mind', kind: 'reading_response',
    // The overdue one — and the thing today's journal entry is about. 18:00 yesterday against
    // an 08:41 clock is exactly what renders the mockup's "−14h".
    assignedAt: '2026-09-08T15:00:00-07:00',
    dueAt: '2026-09-15T18:00:00-07:00', status: 'missing', score: null, pointsPossible: 10,
  },
  {
    id: 'a-neu-ps1', courseId: 'course-neuc61', courseCode: 'NEU C61',
    title: 'Problem set 1: Neurons & signaling', kind: 'problem_set',
    assignedAt: '2026-09-09T12:00:00-07:00',
    dueAt: '2026-09-16T23:59:00-07:00', status: 'upcoming', score: null, pointsPossible: 30,
  },
  {
    id: 'a-linguis-outline', courseId: 'course-linguisc142', courseCode: 'LINGUIS C142',
    title: 'Essay outline: Linguistic relativity', kind: 'essay',
    assignedAt: '2026-09-14T15:30:00-07:00',
    dueAt: '2026-09-19T23:59:00-07:00', status: 'upcoming', score: null, pointsPossible: 20,
  },
  {
    id: 'a-cogsci1-rr4', courseId: 'course-cogsci1', courseCode: 'COGSCI 1',
    title: 'Reading response 4', kind: 'reading_response',
    assignedAt: '2026-09-15T10:00:00-07:00',
    dueAt: '2026-09-22T23:59:00-07:00', status: 'upcoming', score: null, pointsPossible: 10,
  },
  {
    id: 'a-linguis-quiz2', courseId: 'course-linguisc142', courseCode: 'LINGUIS C142',
    title: 'Quiz 2 — semantic fields', kind: 'quiz',
    // Canvas returned neither unlock_at nor created_at for this one, so no elapsed bar is
    // drawn. A bar with a guessed start is a lie about how much time is left.
    assignedAt: null,
    dueAt: '2026-09-23T23:59:00-07:00', status: 'upcoming', score: null, pointsPossible: 25,
  },
  {
    id: 'a-cogsci1-ps2', courseId: 'course-cogsci1', courseCode: 'COGSCI 1',
    title: 'Problem set 2', kind: 'problem_set',
    assignedAt: '2026-09-03T10:00:00-07:00',
    dueAt: '2026-09-10T23:59:00-07:00', status: 'graded', score: 18, pointsPossible: 20,
  },
  {
    id: 'a-cogsci175-rr3', courseId: 'course-cogsci175', courseCode: 'COGSCI 175',
    title: 'Reading response 3 — Turing', kind: 'reading_response',
    assignedAt: '2026-09-08T15:00:00-07:00',
    dueAt: '2026-09-15T23:59:00-07:00', status: 'submitted', score: null, pointsPossible: 10,
  },
  {
    id: 'a-cogsci175-essay1', courseId: 'course-cogsci175', courseCode: 'COGSCI 175',
    title: 'Essay 1 — the systems reply', kind: 'essay',
    assignedAt: '2026-09-10T15:00:00-07:00',
    dueAt: '2026-09-24T23:59:00-07:00', status: 'upcoming', score: null, pointsPossible: 100,
  },
  {
    id: 'a-neu-midterm1', courseId: 'course-neuc61', courseCode: 'NEU C61',
    title: 'Midterm 1', kind: 'exam',
    assignedAt: null,
    dueAt: '2026-10-06T09:30:00-07:00', status: 'upcoming', score: null, pointsPossible: 100,
  },

  // ── SCHOOL 6b/6a ──────────────────────────────────────────────────────────
  // COGSCI 175 carries exactly six assignments across four statuses, because the course page's
  // table renders one row per status and a status with no row is a status whose styling never
  // gets checked. The runway (6a) needs more than one course's worth of open deadlines inside
  // its two-week window, so the other three courses get theirs here too.
  {
    id: 'a-cogsci175-rr1', courseId: 'course-cogsci175', courseCode: 'COGSCI 175',
    title: 'Reading response 1: Turing & Searle', kind: 'reading_response',
    assignedAt: '2026-08-27T15:00:00-07:00',
    dueAt: '2026-09-01T23:59:00-07:00', status: 'graded', score: 9.5, pointsPossible: 10,
  },
  {
    id: 'a-cogsci175-quiz2', courseId: 'course-cogsci175', courseCode: 'COGSCI 175',
    title: 'Quiz 2: Embodiment', kind: 'quiz',
    assignedAt: '2026-09-03T15:00:00-07:00',
    dueAt: '2026-09-08T23:59:00-07:00', status: 'submitted', score: null, pointsPossible: 25,
  },
  {
    // 'in class' is DERIVED, not stored: the due time falls inside this course's own Thursday
    // meeting, so the table says "in class" instead of "2:00pm". Oct 8 2026 is a Thursday.
    id: 'a-cogsci175-midterm1', courseId: 'course-cogsci175', courseCode: 'COGSCI 175',
    title: 'Midterm 1 (Ch. 1–4 + Clark & Chalmers)', kind: 'exam',
    assignedAt: null,
    dueAt: '2026-10-08T14:00:00-07:00', status: 'upcoming', score: null, pointsPossible: 100,
  },
  {
    id: 'a-cogsci1-quiz1', courseId: 'course-cogsci1', courseCode: 'COGSCI 1',
    title: 'Quiz 1 · online', kind: 'quiz',
    assignedAt: '2026-09-11T10:00:00-07:00',
    dueAt: '2026-09-18T23:59:00-07:00', status: 'upcoming', score: null, pointsPossible: 20,
  },
  {
    // THE "NO TIME GIVEN" STATE, and the only seed that exists to carry it. Canvas returned a
    // due DAY and no due TIME for this one — which, once it is a timestamp, is local midnight.
    // The Calendar's all-day row says "no time given" for it rather than filling in 11:59 PM,
    // which is docs/design/calendar-brief.md rule 5 and is a state no other seed exercises.
    id: 'a-cogsci1-fieldnotes', courseId: 'course-cogsci1', courseCode: 'COGSCI 1',
    title: 'Field notes 2', kind: 'unclassified',
    assignedAt: '2026-09-10T10:00:00-07:00',
    dueAt: '2026-09-17T00:00:00-07:00', status: 'upcoming', score: null, pointsPossible: 10,
  },
  {
    id: 'a-neu-labquiz2', courseId: 'course-neuc61', courseCode: 'NEU C61',
    title: 'Lab quiz 2 · online', kind: 'quiz',
    assignedAt: '2026-09-14T08:00:00-07:00',
    dueAt: '2026-09-18T20:00:00-07:00', status: 'upcoming', score: null, pointsPossible: 20,
  },
  {
    id: 'a-linguis-essaydraft', courseId: 'course-linguisc142', courseCode: 'LINGUIS C142',
    title: 'Essay draft: Linguistic relativity', kind: 'essay',
    assignedAt: '2026-09-14T15:30:00-07:00',
    dueAt: '2026-09-26T23:59:00-07:00', status: 'upcoming', score: null, pointsPossible: 40,
  },
  {
    id: 'a-neu-ps2', courseId: 'course-neuc61', courseCode: 'NEU C61',
    title: 'Problem set 2: Synapses', kind: 'problem_set',
    assignedAt: '2026-09-14T12:00:00-07:00',
    dueAt: '2026-09-30T23:59:00-07:00', status: 'upcoming', score: null, pointsPossible: 30,
  },
]

/**
 * WHAT THE WORK ASKS FOR, for the rows that have an answer.
 *
 * Written the way a Berkeley instructor writes one — a couple of sentences, a length, a where —
 * because the peek's whole job is to save a trip to bCourses and prose that says nothing would
 * not. TWO OF THEM CARRY URLs ON PURPOSE: the Zoom room and the Google Form are the exact
 * shapes from the owner's 2026-08-29 screenshot, and they are what makes the linkifier visible
 * in demo mode without a Canvas token.
 *
 * The rows NOT listed here have no description, which is not an oversight — see `AssignmentSeed`.
 */
const DESCRIPTIONS: Record<string, string> = {
  'a-cogsci175-rr2':
    'Read Clark & Chalmers, “The Extended Mind” (1998), and respond in 400–600 words. Take a ' +
    'position on the parity principle: is Otto’s notebook part of his mind, or only a tool he ' +
    'uses well? Cite at least one passage.\n\n' +
    'Submit as a PDF. Office hours this week are on Zoom: https://berkeley.zoom.us/j/93412887 — ' +
    'drop in if you want to talk the argument through first.',
  'a-neu-ps1':
    'Problem set 1 covers resting potential, the Nernst equation and saltatory conduction ' +
    '(Chapters 2–3). Show your work; an answer with no derivation earns no credit.\n\n' +
    'Lab section sign-ups for the week close Thursday: ' +
    'https://docs.google.com/forms/d/e/1FAIpQLSdX9k2mQ7vN4pR8sT/viewform?usp=sf_link',
  'a-linguis-outline':
    'One page. State your thesis on linguistic relativity in a single sentence, then list the ' +
    'three pieces of evidence you intend to use and the strongest objection to each. Bring a ' +
    'printed copy to section — we will workshop them in pairs.',
  'a-cogsci175-essay1':
    'Essay 1 (1500–2000 words). Searle’s Chinese Room and the systems reply. Reconstruct the ' +
    'reply in its strongest form, then say whether it survives Searle’s internalization move.\n\n' +
    'Late work loses one third of a letter grade per day. No extensions after the lock date.',
  'a-cogsci175-midterm1':
    'In class, closed book. Chapters 1–4 plus Clark & Chalmers. Bring a pen; blue books are ' +
    'provided. The review sheet is posted under Files.',
  'a-cogsci1-fieldnotes':
    'Field notes 2: twenty minutes of observation in a public space, written up as a page of ' +
    'notes plus a paragraph on what you expected to see and did not.',
}

/**
 * The Canvas detail every demo assignment carries, derived once rather than typed eighteen times.
 *
 * `submissionTypes` follows the kind, because in real Canvas data it does: a quiz is
 * `online_quiz` (which is the ONE thing `assignmentKindOf` infers a kind from, in the Worker),
 * an exam is `on_paper`, and everything else the owner hands in is an upload. `lockAt` exists
 * only for the essay, because a lock date is a thing a professor sets deliberately and most of
 * them do not — and a demo where every row has one would never draw the "no lock date" line.
 */
function withCanvasDetail(seed: AssignmentSeed): Assignment {
  const submissionTypes =
    seed.kind === 'quiz' ? ['online_quiz'] : seed.kind === 'exam' ? ['on_paper'] : ['online_upload']
  return {
    ...seed,
    lockAt: seed.id === 'a-cogsci175-essay1' ? '2026-09-27T23:59:00-07:00' : null,
    // 'points' for almost everything, which is what Berkeley courses actually use; 'pass_fail'
    // for the field notes and NULL for one row, so the peek's "Canvas did not say" line is a
    // state demo mode can be looked at rather than a branch only production ever reaches.
    gradingType:
      seed.id === 'a-cogsci1-fieldnotes'
        ? 'pass_fail'
        : seed.id === 'a-cogsci175-rr3'
          ? null
          : 'points',
    submissionTypes,
    htmlUrl: `https://bcourses.berkeley.edu/courses/${seed.courseId.replace('course-', '')}/assignments/${seed.id}`,
    description: DESCRIPTIONS[seed.id] ?? '',
  }
}

const ASSIGNMENTS: Assignment[] = ASSIGNMENT_SEEDS.map(withCanvasDetail)

/**
 * The calendar day a deadline falls on, read off the string rather than through UTC.
 *
 * Null when Canvas never gave one — every demo assignment has a date, but the type does not,
 * and a filter that compares `undefined <= '2026-09-01'` silently keeps the wrong rows.
 */
export const dueDateOf = (a: Assignment): ISODate | null => a.dueAt?.slice(0, 10) ?? null

// ── School: announcements ───────────────────────────────────────────────────
//
// Read and unread are different rows, not different opacities of the same row, so both are
// seeded. `read` is stored, never inferred from age: an announcement from this morning that has
// been read is read, and one from last week that has not is still bold. Inferring it would make
// the screen quietly forget things the owner never looked at.
//
// The unread counts here are the same counts the four course cards show. demo.test.ts holds them
// to each course's `unreadAnnouncements`, because a badge that disagrees with its own list is the
// exact bug that makes someone stop trusting the badge.

// `let`, not `const`: `markAnnouncementsRead` replaces rows the way `createTask` replaces tasks,
// so expanding an announcement in demo mode clears its dot exactly as it does against the Worker.
// eslint-disable-next-line prefer-const
let ANNOUNCEMENTS: Announcement[] = [
  {
    id: 'ann-175-midterm-scope', courseId: 'course-cogsci175', courseCode: 'COGSCI 175',
    author: 'Prof. M. Rhodes',
    title: 'Midterm 1 scope posted (Ch. 1–4 + Clark & Chalmers)',
    body: 'No section material on the midterm. Bring a pencil, not a laptop.',
    postedAt: `${SEED_TODAY}T06:41:00-07:00`, read: false,
  },
  {
    id: 'ann-neu-ps1-extended', courseId: 'course-neuc61', courseCode: 'NEU C61',
    author: 'GSI Ramirez',
    title: 'Problem set 1 deadline extended 24 hours',
    // THE OWNER'S OWN SCREENSHOT, 2026-08-29: a GSI's announcement carrying a Zoom room and a
    // Google Forms URL, both of which rendered as dead text until the feed learned to linkify.
    // The shapes are exactly the two that were broken — one with a query string, one without —
    // so demo mode exercises the fix with no Canvas token in sight.
    body:
      'New due date Wed 11:59pm. Runway updated.\n\n' +
      'Extra office hours tonight on Zoom: https://berkeley.zoom.us/j/93412887?pwd=RmJq2 — and ' +
      'if you want a regrade, fill this in first: ' +
      'https://docs.google.com/forms/d/e/1FAIpQLSdX9k2mQ7vN4pR8sT/viewform.',
    postedAt: `${SEED_TODAY}T02:41:00-07:00`, read: false,
  },
  {
    id: 'ann-cogsci1-reader', courseId: 'course-cogsci1', courseCode: 'COGSCI 1',
    author: 'Prof. J. Whitney',
    title: 'Reader available at Copy Central',
    body: "Pick up the course reader before Friday's lecture.",
    postedAt: '2026-09-15T16:20:00-07:00', read: false,
  },
  {
    id: 'ann-175-essay-prompt', courseId: 'course-cogsci175', courseCode: 'COGSCI 175',
    author: 'Prof. M. Rhodes',
    title: 'Essay 1 prompt posted',
    body: 'One question, 1,500 words. Say what would have to be true for the systems reply to work — not what the Chinese Room is.',
    postedAt: '2026-09-15T11:05:00-07:00', read: false,
  },
  {
    id: 'ann-linguis-room', courseId: 'course-linguisc142', courseCode: 'LINGUIS C142',
    author: 'Prof. S. Renner',
    title: 'Room change for Wednesday only → Wheeler 108',
    body: 'Barrows 118 is being used for a placement exam. One week only.',
    // Read, and newer than one of the unread ones — which is the point. The list is ordered by
    // when things were posted, not by whether they have been dealt with, so a read row lands in
    // the middle of the feed exactly as it does in life.
    postedAt: '2026-09-15T13:05:00-07:00', read: true,
  },
  {
    id: 'ann-neu-section-move', courseId: 'course-neuc61', courseCode: 'NEU C61',
    author: 'GSI Ramirez',
    title: 'Discussion section moves to Dwinelle 235',
    body: 'From this week onward. Same time, different room.',
    postedAt: '2026-09-14T17:40:00-07:00', read: true,
  },
  {
    id: 'ann-cogsci1-ps2-solutions', courseId: 'course-cogsci1', courseCode: 'COGSCI 1',
    author: 'Prof. J. Whitney',
    title: 'Problem set 2 solutions posted',
    body: 'On the Files tab. Compare before you look at your score, not after.',
    postedAt: '2026-09-11T18:00:00-07:00', read: true,
  },
  {
    id: 'ann-175-recording-policy', courseId: 'course-cogsci175', courseCode: 'COGSCI 175',
    author: 'Prof. M. Rhodes',
    title: 'Recording lectures is fine by me',
    body: "Ask once, keep it to yourself, don't post it anywhere. That is the whole policy.",
    postedAt: '2026-09-09T13:15:00-07:00', read: true,
  },
]

/**
 * A course card's unread badge, COUNTED from the announcement rows rather than read off the seed.
 *
 * The seeded `unreadAnnouncements` values are the same numbers this produces at rest — school
 * .test.ts holds every badge to its own list — but they are a snapshot, and once
 * `markAnnouncementsRead` can clear a dot, a snapshot is a badge that disagrees with the list
 * under it. The Worker counts the rows for the same reason (routes/school.ts → `unreadByCourse`).
 */
function withUnreadCount<T extends { id: string; unreadAnnouncements: number }>(course: T): T {
  return {
    ...course,
    unreadAnnouncements: ANNOUNCEMENTS.filter((a) => a.courseId === course.id && !a.read).length,
  }
}

// ── School: the syllabus, after a model read the PDF ────────────────────────
//
// Every number in this object came out of a document, not out of Canvas, which is why the whole
// panel wears one provenance chip instead of pretending to be synced data. Only COGSCI 175 has
// been through the extractor; `getSyllabus` returns null for the other three, and the panel says
// so in words rather than rendering an empty grading breakdown that reads as "no requirements".

const SYLLABUS_EXTRACTS: SyllabusExtract[] = [
  {
    courseId: 'course-cogsci175',
    sourceFile: 'cogsci175-fall2026-syllabus.pdf',
    provenance: {
      model: 'claude-haiku-4.5',
      sourceRef: 'cogsci175-fall2026-syllabus.pdf',
      generatedAt: '2026-08-26T09:12:00-07:00',
    },
    gradingBreakdown: [
      { label: 'Participation', weightPct: 15 },
      { label: 'Reading responses', weightPct: 20 },
      { label: 'Essay', weightPct: 15 },
      { label: 'Midterm 1', weightPct: 25 },
      { label: 'Final exam', weightPct: 25 },
    ],
    exams: [
      // The same two exams Canvas carries as assignments. They are repeated here because the
      // syllabus is where the owner reads them as a shape of the semester, not as a deadline.
      { label: 'Midterm 1', date: '2026-10-08', detail: 'in class' },
      { label: 'Final exam', date: '2026-12-15', detail: '8:00am' },
    ],
    weeklyTopics: [
      { week: 1, title: 'Foundations: Turing, Searle' },
      { week: 2, title: 'Symbol systems & computation' },
      { week: 3, title: 'The Chinese Room & symbol grounding' },
      { week: 4, title: 'The extended mind' },
      { week: 5, title: 'Embodied cognition' },
      { week: 6, title: 'Perception & prediction' },
    ],
  },
]

// ── School: the files bCourses holds under a course ─────────────────────────
//
// THE FOUR STATES OF THE SYLLABUS CARD, one per course, because a demo dataset where every course
// works is a dataset that never shows the owner what a broken one looks like:
//
//   COGSCI 1       a syllabus PDF that WAS read — the case the whole lane exists for. Its lecture
//                  slide decks sit beside it as `not_attempted`, which is what the catalog looks
//                  like in real life: one file that matters and fifteen that do not.
//   NEU C61        a syllabus PDF that could NOT be read. Its instructor scanned the handout, so
//                  there is no text layer, and the card says "found, could not be read" rather
//                  than going quiet.
//   LINGUIS C142   no files at all. The card falls back to the Canvas-typed syllabus flow.
//   COGSCI 175     no files either — it is the course whose extract a model already made, and it
//                  is here to prove the extract panel still wins when both exist.
//
// NOTHING HERE IS AI. `text` is a transcription of a PDF's text layer; there is no provenance
// chip on this card's file half, and there should never be one.

const COURSE_FILES: CourseFile[] = [
  {
    id: 'file-cogsci1-syllabus',
    externalId: '90210001',
    courseId: 'course-cogsci1',
    courseCode: 'COGSCI 1',
    filename: 'CogSci1_F26_Syllabus.pdf',
    displayName: 'CogSci1_F26_Syllabus.pdf',
    contentType: 'application/pdf',
    sizeBytes: 214_388,
    folderPath: 'course files/Course Information',
    url: 'https://bcourses.berkeley.edu/files/90210001/download?verifier=demo',
    updatedAt: '2026-08-21T16:04:00-07:00',
    extraction: 'extracted',
    extractionError: null,
    extractedAt: '2026-08-26T06:12:00-07:00',
    pages: 4,
    characters: 612,
  },
  {
    id: 'file-cogsci1-lecture02',
    externalId: '90210002',
    courseId: 'course-cogsci1',
    courseCode: 'COGSCI 1',
    filename: 'Lecture02_Perception.pdf',
    displayName: 'Lecture 02 — Perception.pdf',
    contentType: 'application/pdf',
    sizeBytes: 3_902_114,
    folderPath: 'course files/Week 2',
    url: 'https://bcourses.berkeley.edu/files/90210002/download?verifier=demo',
    updatedAt: '2026-09-02T08:30:00-07:00',
    // Nothing tried to read it, and nothing should: a slide deck is not a syllabus, and reading
    // every file a course posts is a download budget rather than a feature.
    extraction: 'not_attempted',
    extractionError: null,
    extractedAt: null,
    pages: null,
    characters: null,
  },
  {
    id: 'file-cogsci1-reader',
    externalId: '90210003',
    courseId: 'course-cogsci1',
    courseCode: 'COGSCI 1',
    filename: 'CourseReader_Fall2026.pdf',
    displayName: 'Course Reader (Fall 2026).pdf',
    contentType: 'application/pdf',
    sizeBytes: 41_205_772,
    folderPath: 'course files/Readings',
    url: 'https://bcourses.berkeley.edu/files/90210003/download?verifier=demo',
    updatedAt: '2026-08-20T11:00:00-07:00',
    extraction: 'not_attempted',
    extractionError: null,
    extractedAt: null,
    pages: null,
    characters: null,
  },
  {
    id: 'file-neuc61-syllabus',
    externalId: '90210101',
    courseId: 'course-neuc61',
    courseCode: 'NEU C61',
    filename: 'NEUC61-syllabus-scan.pdf',
    displayName: 'NEU C61 Syllabus (scanned).pdf',
    contentType: 'application/pdf',
    sizeBytes: 1_884_002,
    folderPath: 'course files',
    url: 'https://bcourses.berkeley.edu/files/90210101/download?verifier=demo',
    updatedAt: '2026-08-19T09:45:00-07:00',
    extraction: 'failed',
    extractionError:
      'The PDF opened but carries no text layer — it is almost certainly a scan or an image, ' +
      'and nothing in it can be read without OCR.',
    extractedAt: '2026-08-26T06:12:00-07:00',
    pages: null,
    characters: null,
  },
]

/** The text of the one demo file that has any. Short, and shaped like a real syllabus's opening. */
const COURSE_FILE_TEXT: Record<string, string> = {
  'file-cogsci1-syllabus': [
    'COGNITIVE SCIENCE 1 — INTRODUCTION TO COGNITIVE SCIENCE',
    'Fall 2026 · Dwinelle 155 · MWF 10:00–11:00',
    'Instructor: Prof. J. Whitney · Office hours: Tuesdays 2–4, Berkeley Way West 1102',
    '',
    'COURSE DESCRIPTION',
    'An introduction to the study of mind across psychology, linguistics, philosophy, computer',
    'science and neuroscience. We ask what a mind is made of, what it does, and how five fields',
    'that disagree about all three manage to study the same object.',
    '',
    'GRADING',
    'Reading responses 20% · Two midterms 40% · Final exam 30% · Section participation 10%',
    '',
    'EXAMS',
    'Midterm 1: Friday, October 2, in class.',
    'Midterm 2: Friday, November 6, in class.',
    'Final exam: per the registrar’s schedule.',
    '',
    'POLICIES',
    'Late reading responses are not accepted; the two lowest are dropped instead.',
  ].join('\n'),
}

// ── School: the lecture study set ───────────────────────────────────────────
//
// One lecture is worked all the way through, because study mode is only a real screen if there is
// something real to study. All of it hangs off the Sep 10 transcript and all of it carries that
// transcript as its `sourceRef` — the flashcards go further and keep the millisecond they were
// drawn from, so a card can cite itself down to '12:31' instead of waving at the lecture.
//
// NOTHING HERE IS RENDERABLE WITHOUT PROVENANCE. That is enforced at the render path by
// hasProvenance() (packages/shared/src/school.ts), not by everyone remembering.

const STUDY_LECTURE_ID = 'lecture-course-cogsci175-2026-09-10'
const STUDY_TRANSCRIPT_REF = 'transcript-lecture-course-cogsci175-2026-09-10'
const STUDY_PROVENANCE = {
  model: 'claude-haiku-4.5',
  sourceRef: STUDY_TRANSCRIPT_REF,
  generatedAt: '2026-09-10T19:26:00-07:00',
}

const LECTURE_SUMMARIES: LectureSummary[] = [
  {
    lectureId: STUDY_LECTURE_ID,
    // Terse on purpose. A summary that runs as long as the transcript is not a summary, and the
    // handoff's own bullets are one sentence each.
    bullets: [
      'Correct symbol manipulation is not understanding — the man in the room applies every rule and understands nothing.',
      'The systems reply moves understanding to the whole room; Searle puts the room inside one head.',
      'Symbol grounding, flagged for the midterm: symbols mean only what an outside interpreter takes them to mean.',
    ],
    keyConcepts: ['Chinese Room', 'systems reply', 'symbol grounding', 'Harnad'],
    provenance: STUDY_PROVENANCE,
  },
]

/** [id, prompt, answer, sourceMs, dueForReview] */
const FLASHCARD_SEEDS: Array<[string, string, string, number | null, boolean]> = [
  ['fc-1', 'What is Searle actually arguing against in the Chinese Room?',
    'That running the right program is sufficient for understanding. Speed and correctness are conceded; understanding is the thing denied.', 724_000, false],
  ['fc-2', 'State the systems reply in one sentence.',
    'The man does not understand Chinese, but the man plus the rulebook plus the paper — the whole system — does.', 736_400, false],
  ['fc-3', 'How does Searle answer the systems reply?',
    'By having the man memorise the rulebook, so the entire system is inside one head, and reporting that he still understands nothing.', 736_400, true],
  ['fc-4', 'Why does the student’s objection matter?',
    'Because if the rules are memorised there is no external difference to observe — so the disagreement cannot be settled by behaviour.', 751_900, false],
  ['fc-5', 'Name the fork in the road the lecture draws.',
    'Either understanding is fixed by behaviour, in which case the Turing test settles it, or it is something over and above behaviour, and we owe an account of what.', 768_200, true],
  ['fc-6', 'What is the symbol grounding problem?',
    "Harnad's worry that the symbols in a system are meaningful only because someone outside interprets them.", 784_600, true],
  ['fc-7', 'What is the Chinese-dictionary image for?',
    'Learning Chinese from a Chinese-only dictionary: every definition sends you to more symbols, with no exit from the symbol circle.', 801_300, false],
  ['fc-8', 'What would count as an exit from the symbol circle?',
    'Tying some symbols to something that is not a symbol — perception, action, a body.', 801_300, false],
  ['fc-9', 'Why does embodied cognition appear next in the course?',
    'It is offered as an answer to grounding, not as a separate topic bolted on at the end.', 818_700, true],
  ['fc-10', 'What does the essay ask for?',
    'Not a summary of the Chinese Room: what would have to be true for the systems reply to work, and whether you believe it.', 833_100, false],
  ['fc-11', 'Whose paper is set for Thursday?',
    'Clark and Chalmers.', 833_100, false],
  // Two cards drawn from the summary rather than from one moment in the recording. They carry no
  // timestamp, and none is guessed for them — the card simply cites the lecture.
  ['fc-12', 'In one line: why is correct symbol manipulation not enough?',
    'Because the symbols mean nothing to the system doing the manipulating; their meaning is supplied from outside.', null, false],
]

const FLASHCARDS: Flashcard[] = FLASHCARD_SEEDS.map(
  ([id, prompt, answer, sourceMs, dueForReview]) => ({
    id: `${STUDY_LECTURE_ID}-${id}`,
    lectureId: STUDY_LECTURE_ID,
    prompt,
    answer,
    sourceMs,
    dueForReview,
    provenance: STUDY_PROVENANCE,
  }),
)

/** [id, question, answer, answered] */
const PRACTICE_SEEDS: Array<[string, string, string, boolean]> = [
  ['pq-1', 'Why does memorising the rulebook matter?',
    'It removes what the systems reply pointed at: with the room inside one head, no wider system is left to credit.', true],
  ['pq-2', 'What does the Turing test settle, on the behaviourist horn of the fork?',
    'Everything. If understanding just is being indistinguishable in behaviour, then passing the test is understanding, and there is nothing further to ask.', true],
  ['pq-3', 'Give the symbol grounding problem in your own words.',
    'The symbols a system manipulates have no meaning of their own; they mean what an interpreter outside the system takes them to mean, so nothing inside is about anything.', true],
  ['pq-4', 'Why is a Chinese-only dictionary the right image for the problem?',
    'Because every lookup returns more symbols. Without something non-symbolic to land on, the search never terminates in meaning.', false],
  ['pq-5', 'What kind of thing could ground a symbol?',
    'Something that is not itself a symbol: a perceptual state, an action, a body in contact with the world.', false],
  ['pq-6', 'How is embodied cognition a response to Harnad rather than a change of subject?',
    'It proposes the grounding: sensorimotor engagement is what ties some representations to the world, which is exactly the debt the symbol circle leaves unpaid.', false],
  ['pq-7', 'State the strongest version of the objection to Searle you can.',
    'That intuitions about what "he" understands are not evidence about what the system understands, and Searle has offered an intuition where an argument is needed.', false],
  ['pq-8', 'What is the essay actually asking you to do?',
    'To specify the conditions under which the systems reply would succeed, and then to take a position on whether those conditions hold.', false],
]

const PRACTICE_QUESTIONS: PracticeQuestion[] = PRACTICE_SEEDS.map(
  ([id, question, answer, answered]) => ({
    id: `${STUDY_LECTURE_ID}-${id}`,
    lectureId: STUDY_LECTURE_ID,
    question,
    answer,
    answered,
    provenance: STUDY_PROVENANCE,
  }),
)

// ── Tasks ───────────────────────────────────────────────────────────────────
//
// THE TASKS SCREEN'S DATASET — docs/design/handoff/"LifeOS Tasks.dc.html", option 8a.
//
// The mockup is dated Thu Aug 20 and this dataset's clock is Wed Sep 16, so every date below is
// the mockup's OFFSET rather than its literal: a row the mockup shows three days late is three
// days late here too. That is what "coherent with the seeded clock" has to mean — copying
// "Thu Aug 20" across would put the whole board in the past and every derived pill would lie.
//
// The mockup's own numbers, all of which the screen derives rather than states:
//   2 overdue (oldest 6 days) · 5 due today · 7 dated later, 4 of them inside seven days
//   12 undated (Someday), 4 of those unfiled (Inbox) · 11 completions in the last seven days
// The header's "9 open" is the 5 + 4 the board is actually showing; overdue is counted beside
// it, never inside it. See components/tasks/derive.ts for why.
//
// NO WAITLIST POSITION ANYWHERE. The Canvas API cannot supply one, so LifeOS never shows one
// (README §3, "Absent by design"). 'Update the waitlist number from CalCentral' is the owner's
// own errand — going and looking somewhere else — and not a number this app claims to hold.
//
// ESTIMATES ARE SPARSE ON PURPOSE, and they are applied BELOW the literals rather than typed
// into each of them (`TASK_ESTIMATES`). Six of the forty-two tasks carry one, because that is
// what a real board looks like — an estimate is something the owner bothered to type, not a
// field every row has. It matters here more than it looks: the Calendar suggests a block only
// for a task that carries an estimate (docs/design/calendar-brief.md rule 2), so a dataset
// where every task had one would quietly hide the rule it exists to demonstrate.
//
// Anything absent from this record has `estimateMin: null`, and null means NO ESTIMATE — not
// zero, and not "short".
const TASK_ESTIMATES: Record<string, number> = {
  'task-reading-response': 90, // the overdue one — an hour and a half of writing
  'task-waitlist': 15,
  'task-reader': 30,
  'task-advisor': 20,
  'task-insurance': 45,
  'task-recycling': 10,
}

const TASK_SEEDS: Task[] = ([
  // ── Overdue · open and already past its date ──────────────────────────────
  {
    id: 'task-reading-response', title: 'Reading response 2 — submit on Canvas',
    done: false, due: '2026-09-13', list: 'School admin', priority: 'high',
    rrule: null, reminderAt: '2026-09-13T09:00:00-07:00',
    createdAt: '2026-09-07T17:30:00-07:00', completedAt: null,
  },
  {
    // The oldest thing on the board, and the screen says so out loud rather than letting six
    // days quietly become "a while ago".
    id: 'task-waitlist', title: 'Waitlist follow-up — email the COGSCI 180 GSI',
    done: false, due: '2026-09-10', list: 'School admin', priority: 'high',
    rrule: null, reminderAt: null,
    createdAt: '2026-08-28T10:02:00-07:00', completedAt: null,
  },

  // ── Today ─────────────────────────────────────────────────────────────────
  {
    id: 'task-reader', title: 'Buy the COGSCI 1 reader at Copy Central',
    done: false, due: SEED_TODAY, list: 'School admin', priority: 'medium',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-03T18:20:00-07:00', completedAt: null,
  },
  {
    // The one row whose pill carries a clock time: the reminder is for today, so 'today · 18:00'
    // says something. A reminder five days out has a time too, and printing it would read like
    // an appointment when all the app knows is the day.
    id: 'task-maya', title: 'Text Maya about the sublet',
    done: false, due: SEED_TODAY, list: 'Apartment', priority: 'high',
    rrule: null, reminderAt: `${SEED_TODAY}T18:00:00-07:00`,
    createdAt: '2026-09-14T21:05:00-07:00', completedAt: null,
  },
  {
    // Recurring, and its previous instance is in the done log two days back — which is why a
    // repeated chore must not read as a duplicate.
    id: 'task-clipper', title: 'Refill Clipper card',
    done: false, due: SEED_TODAY, list: 'Errands', priority: 'low',
    rrule: 'FREQ=MONTHLY', reminderAt: null,
    createdAt: '2026-09-14T09:51:00-07:00', completedAt: null,
  },
  {
    id: 'task-prescription', title: 'Pick up prescription at Walgreens',
    done: false, due: SEED_TODAY, list: 'Errands', priority: 'low',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-12T11:15:00-07:00', completedAt: null,
  },
  {
    id: 'task-recycling', title: 'Take out recycling',
    done: false, due: SEED_TODAY, list: 'Apartment', priority: 'none',
    rrule: 'FREQ=WEEKLY', reminderAt: null,
    createdAt: '2026-09-09T07:30:00-07:00', completedAt: null,
  },

  // ── Upcoming · inside the next seven days (the board's own window) ─────────
  {
    id: 'task-advisor', title: 'Email advisor about the spring petition',
    done: false, due: '2026-09-17', list: 'School admin', priority: 'medium',
    rrule: null, reminderAt: '2026-09-17T09:00:00-07:00',
    createdAt: '2026-09-11T16:05:00-07:00', completedAt: null,
  },
  {
    id: 'task-hdmi', title: 'Return the borrowed HDMI cable to Ravi',
    done: false, due: '2026-09-18', list: 'Errands', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-13T20:40:00-07:00', completedAt: null,
  },
  {
    id: 'task-insurance', title: 'Renters insurance — compare two quotes',
    done: false, due: '2026-09-21', list: 'Apartment', priority: 'medium',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-06T15:00:00-07:00', completedAt: null,
  },
  {
    // The mockup says "Pay the September rent" from an August frame. From a September frame the
    // same task is October's — the offset is the thing being copied, not the month name.
    id: 'task-rent', title: 'Pay the October rent',
    done: false, due: '2026-09-22', list: 'Apartment', priority: 'high',
    rrule: 'FREQ=MONTHLY', reminderAt: '2026-09-22T09:00:00-07:00',
    createdAt: '2026-09-01T09:00:00-07:00', completedAt: null,
  },

  // ── Upcoming · dated beyond the board's window ────────────────────────────
  // These exist so the Upcoming LENS count (everything still ahead) and the Upcoming CARD (the
  // next seven days) are different numbers, which is what makes both of them worth printing.
  {
    id: 'task-section-swap', title: 'Email the NEU C61 GSI about swapping discussion sections',
    done: false, due: '2026-09-25', list: 'School admin', priority: 'medium',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-11T13:40:00-07:00', completedAt: null,
  },
  {
    id: 'task-flu-shot', title: 'Book the flu shot at the Tang Center',
    done: false, due: '2026-09-28', list: 'Errands', priority: 'low',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-15T10:20:00-07:00', completedAt: null,
  },
  {
    id: 'task-bike-lights', title: 'Replace the bike lights before the clocks change',
    done: false, due: '2026-10-02', list: 'Errands', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-08T19:15:00-07:00', completedAt: null,
  },

  // ── Someday · no date at all, and none is invented for any of them ────────
  // The four filed under 'Inbox' are the UNFILED ones — captured with no list. The Inbox is the
  // absence of a list, not a list, which is why a row in it carries no list chip.
  {
    id: 'task-grounding-paper', title: 'Read the Harnad symbol grounding paper',
    done: false, due: null, list: 'Inbox', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: `${SEED_TODAY}T08:12:00-07:00`, completedAt: null,
  },
  {
    id: 'task-inbox-predictive', title: 'That podcast episode on predictive coding',
    done: false, due: null, list: 'Inbox', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-15T22:31:00-07:00', completedAt: null,
  },
  {
    id: 'task-inbox-desk', title: 'Look into a standing desk for the apartment',
    done: false, due: null, list: 'Inbox', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-14T13:02:00-07:00', completedAt: null,
  },
  {
    id: 'task-inbox-office-hours', title: 'Ask about GSI office hours for LINGUIS C142',
    done: false, due: null, list: 'Inbox', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-11T09:44:00-07:00', completedAt: null,
  },
  {
    id: 'task-portra', title: 'Develop the Portra roll',
    done: false, due: null, list: 'Errands', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-08-30T16:10:00-07:00', completedAt: null,
  },
  {
    id: 'task-shelf', title: 'Put up the shelf above the desk',
    done: false, due: null, list: 'Apartment', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-02T18:25:00-07:00', completedAt: null,
  },
  {
    id: 'task-thermostat', title: 'Figure out the thermostat schedule',
    done: false, due: null, list: 'Apartment', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-04T20:10:00-07:00', completedAt: null,
  },
  {
    id: 'task-knives', title: 'Get the kitchen knives sharpened',
    done: false, due: null, list: 'Apartment', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-08-27T12:40:00-07:00', completedAt: null,
  },
  {
    id: 'task-transcript', title: 'Request the transcript for the transfer file',
    done: false, due: null, list: 'School admin', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-09T11:05:00-07:00', completedAt: null,
  },
  {
    id: 'task-club', title: 'Look up the cognitive science club meetings',
    done: false, due: null, list: 'School admin', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-05T17:50:00-07:00', completedAt: null,
  },
  {
    id: 'task-passport', title: 'Renew the passport photo',
    done: false, due: null, list: 'Errands', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-08-25T14:00:00-07:00', completedAt: null,
  },
  {
    id: 'task-tires', title: 'Take the bike in for new tires',
    done: false, due: null, list: 'Errands', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-07T15:35:00-07:00', completedAt: null,
  },

  // ── The done log · eleven completions in the last seven days ──────────────
  // Everything here carries a `due` EXCEPT the library book, on purpose: `getReminders` treats a
  // dated task with no time on it as the deadline card's business, not an ambient reminder, so
  // only the undated one comes back through to the Today screen — where it renders struck
  // through with its "done Sat" stamp.
  {
    id: 'task-done-pset', title: 'Print the NEU C61 problem set',
    done: true, due: SEED_TODAY, list: 'School admin', priority: 'medium',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-14T18:00:00-07:00', completedAt: `${SEED_TODAY}T09:15:00-07:00`,
  },
  {
    id: 'task-done-filter', title: 'Refill water filter',
    done: true, due: SEED_TODAY, list: 'Apartment', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-13T08:00:00-07:00', completedAt: `${SEED_TODAY}T08:05:00-07:00`,
  },
  {
    id: 'task-done-quiz', title: 'Quiz 1 study block',
    done: true, due: '2026-09-15', list: 'School admin', priority: 'high',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-12T09:00:00-07:00', completedAt: '2026-09-15T20:10:00-07:00',
  },
  {
    id: 'task-done-venmo', title: 'Venmo Ravi for groceries',
    done: true, due: '2026-09-15', list: 'Apartment', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-14T19:20:00-07:00', completedAt: '2026-09-15T18:40:00-07:00',
  },
  {
    id: 'task-syllabus', title: 'Print the LINGUIS C142 syllabus',
    done: true, due: '2026-09-15', list: 'School admin', priority: 'low',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-13T12:00:00-07:00', completedAt: '2026-09-15T09:30:00-07:00',
  },
  {
    id: 'task-done-waitlist-number', title: 'Update the waitlist number from CalCentral',
    done: true, due: '2026-09-14', list: 'School admin', priority: 'medium',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-10T10:00:00-07:00', completedAt: '2026-09-14T16:20:00-07:00',
  },
  {
    id: 'task-done-laundry', title: 'Laundry',
    done: true, due: '2026-09-14', list: 'Apartment', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-13T10:00:00-07:00', completedAt: '2026-09-14T11:00:00-07:00',
  },
  {
    // Last month's instance of the Clipper card. Its recurrence stamp is what tells the two
    // apart in the log.
    id: 'task-done-clipper', title: 'Refill Clipper card',
    done: true, due: '2026-09-14', list: 'Errands', priority: 'low',
    rrule: 'FREQ=MONTHLY', reminderAt: null,
    createdAt: '2026-08-14T09:00:00-07:00', completedAt: '2026-09-14T09:50:00-07:00',
  },
  {
    id: 'task-done-canvas-sync', title: 'Set up the Canvas sync',
    done: true, due: '2026-09-13', list: 'School admin', priority: 'medium',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-08T20:00:00-07:00', completedAt: '2026-09-13T19:45:00-07:00',
  },
  {
    // Finished on a Saturday, undated, and therefore still ambient — so the Today screen's
    // Reminders card carries it with a "done Sat" stamp. Completion is not an erasure: a
    // reminder you dealt with is information for a few days.
    id: 'task-library', title: 'Return library book',
    done: true, due: null, list: 'Errands', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-07T11:00:00-07:00', completedAt: '2026-09-12T14:20:00-07:00',
  },
  {
    id: 'task-done-gym', title: 'Cancel the old gym membership',
    done: true, due: '2026-09-11', list: 'Errands', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-09-02T13:00:00-07:00', completedAt: '2026-09-11T10:15:00-07:00',
  },

  // ── Older completions, still inside the 90-day keep window ────────────────
  // Their whole job is to make "11 this week" a different number from the length of the log, so
  // the Done log's `All` reveals something instead of promising nothing.
  {
    id: 'task-done-topper', title: 'Return the mattress topper',
    done: true, due: '2026-09-05', list: 'Apartment', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-08-31T16:00:00-07:00', completedAt: '2026-09-05T13:00:00-07:00',
  },
  {
    id: 'task-done-notebooks', title: 'Buy notebooks',
    done: true, due: '2026-08-30', list: 'Errands', priority: 'none',
    rrule: null, reminderAt: null,
    createdAt: '2026-08-26T11:30:00-07:00', completedAt: '2026-08-30T15:20:00-07:00',
  },
] as Omit<Task, 'estimateMin' | 'proposalId'>[]).map((t) => ({
  ...t,
  estimateMin: TASK_ESTIMATES[t.id] ?? null,
  // Not one of these came from the chat. The cast above is why this is spelled out: it
  // widens the seed literals, so a field left off them would arrive as `undefined` and
  // `undefined !== null` would make every seeded task read as "from chat".
  proposalId: null,
}))

// ── Calendar (docs/design/calendar-brief.md; mockups 11a week, 11b day) ─────
//
// A COHERENT WEEK, ASSEMBLED THE WAY THE REAL ONE IS. There are three layers here and they have
// three different owners, which is the whole point of the screen:
//
//   1. CLASSES are derived from each course's own `meetings` — the same derivation the Today
//      screen's schedule uses (`classOccurrences`). They come back `source: 'canvas'` and
//      `editable: false`: there is no seed row to move, so "Canvas is read-only" is a fact
//      about where the data comes from rather than a rule the UI has to remember.
//      NOTE FOR REMOTE MODE: Canvas carries NO meeting pattern at all (verified against the
//      live API — packages/shared/src/school.ts says so in the `meetings` field's own
//      description), so the real Worker returns no timed Canvas blocks. The demo dataset has
//      placeholder meeting times and is honest about them; the grid simply has fewer blue
//      blocks on real data, and nothing invents any.
//   2. DEADLINES are derived from `ASSIGNMENTS` and live in the all-day row. Never on the grid:
//      a deadline is an instant, and giving it a height would mean inventing a duration.
//   3. BLOCKS are LifeOS's own — the only layer with any affordance on it.
//
// The seeds below sit in the seeded week (Mon 2026-09-14 → Sun 2026-09-20) and are placed
// around the real class times rather than through them. Navigate to another week and the
// classes and deadlines are still there, because those derive from the term; the personal
// blocks are not, because nobody put any there.

/** The Monday of the seeded week — every block seed is an offset from this. */
const CAL_WEEK_START: ISODate = startOfWeek(SEED_TODAY)

/**
 * The demo's fixed UTC offset. September in Berkeley is PDT, and the whole dataset is anchored
 * to one week in September, so one literal is honest here. Everything the screen reads —
 * `date`, `startMinutes`, `endMinutes` — is sliced off the string rather than parsed through a
 * Date, so no render ever depends on the host machine's zone.
 */
const CAL_OFFSET = '-07:00'

const calTimestamp = (date: ISODate, minutes: number): ISOTimestamp =>
  `${date}T${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}:00${CAL_OFFSET}`

interface BlockSeed {
  id: string
  /**
   * Minutes before the start to be told about it, Google-Calendar style. Present on exactly one
   * seed: the picker has to have something to open ON, and a week where every block had one
   * would make "no reminder" look like the unusual case when it is the normal one.
   */
  reminders?: number[]
  /** Days after CAL_WEEK_START. 0 = Monday. */
  day: number
  start: number
  end: number
  title: string
  source: CalendarWritableSource
  location?: string
  taskId?: string
}

const CALENDAR_SEEDS: BlockSeed[] = [
  // Monday — the shift, after the 15:30 LINGUIS lecture ends.
  { id: 'cal-shift-mon', day: 0, start: 17 * 60 + 30, end: 21 * 60, title: 'Shift — library desk', source: 'work', location: 'Doe Library' },
  // Tuesday — a learning block before the day opens, and the gym after COGSCI 175.
  { id: 'cal-algebra-tue', day: 1, start: 9 * 60, end: 10 * 60, title: 'Algebra — factoring practice', source: 'learning' },
  { id: 'cal-gym-tue', day: 1, start: 17 * 60, end: 18 * 60, title: 'Gym', source: 'personal', location: 'RSF' },
  // Wednesday — today. A short block (under 40 min, so the grid drops its time line), the one
  // task-linked block on the board, and dinner.
  { id: 'cal-advisor-wed', day: 2, start: 11 * 60 + 15, end: 11 * 60 + 45, title: 'Advisor check-in', source: 'personal', location: 'Zoom' },
  { id: 'cal-rr2-wed', day: 2, start: 13 * 60 + 15, end: 14 * 60 + 45, title: 'Reading response 2 — write-up', source: 'task', taskId: 'task-reading-response' },
  { id: 'cal-dinner-wed', day: 2, start: 19 * 60, end: 20 * 60 + 30, title: 'Dinner — Maya', source: 'personal', location: 'Sun Singer', reminders: [30] },
  // Thursday — the long shift.
  { id: 'cal-shift-thu', day: 3, start: 16 * 60, end: 20 * 60, title: 'Shift — library desk', source: 'work', location: 'Doe Library' },
  // Friday — Python before the 09:00 discussion.
  { id: 'cal-python-fri', day: 4, start: 7 * 60 + 30, end: 8 * 60 + 30, title: 'Python — dictionaries', source: 'learning' },
  // Weekend.
  { id: 'cal-run-sat', day: 5, start: 8 * 60, end: 9 * 60 + 30, title: 'Long run', source: 'personal' },
  { id: 'cal-quiz-review-sun', day: 6, start: 13 * 60, end: 16 * 60, title: 'Quiz 2 review', source: 'learning' },
]

function seedToBlock(seed: BlockSeed): CalendarBlock {
  const date = addDays(CAL_WEEK_START, seed.day)
  return {
    id: seed.id,
    title: seed.title,
    source: seed.source,
    state: 'accepted',
    date,
    startsAt: calTimestamp(date, seed.start),
    endsAt: calTimestamp(date, seed.end),
    startMinutes: seed.start,
    endMinutes: seed.end,
    location: seed.location ?? null,
    // Only a Google event has a status word. Every one of these is LifeOS's own.
    status: null,
    taskId: seed.taskId ?? null,
    // LifeOS owns every one of these, so every one of them can be moved.
    editable: true,
    courseCode: null,
    courseSlug: null,
    // Nothing in the seeded week was proposed by the chat. Accepting a proposal is what sets
    // this, and the grid reads it to draw FROM CHAT.
    proposalId: null,
    // ALWAYS PRESENT, like the wire: the API states `reminders` on every block, and a fixture
    // that left it undefined would let a `?? []` rot unnoticed on the one path that matters.
    reminders: seed.reminders ?? [],
  }
}

/** Mutable, like `tasks`: creating, moving and deleting are real against the fixture. */
let calendarBlocks: CalendarBlock[] = CALENDAR_SEEDS.map(seedToBlock)

/** How many blocks this session has created. Monotonic, for the same reason `createdTasks` is. */
let createdBlocks = 0

/**
 * THE ONE PLACE A BLOCK IS ADDED in demo mode — `createCalendarBlock` and `acceptChatProposal`
 * both come through here, which is the fixture's version of the Worker routing both through
 * `createCalendarBlockRow`. Two writers would agree today and disagree the first time either
 * one gained a field.
 */
function addCalendarBlock(input: CalendarBlockInput, proposalId: string | null): CalendarBlock {
  const date = input.startsAt.slice(0, 10) as ISODate
  const minutesOf = (ts: string) => Number(ts.slice(11, 13)) * 60 + Number(ts.slice(14, 16))
  const block: CalendarBlock = {
    // MONOTONIC, NOT A COUNT OF THE ARRAY — `createdTasks` explains why at length.
    id: `cal-demo-new-${++createdBlocks}`,
    title: input.title,
    source: input.source,
    state: input.state ?? 'accepted',
    date,
    startsAt: input.startsAt,
    endsAt: input.endsAt,
    startMinutes: minutesOf(input.startsAt),
    endMinutes: minutesOf(input.endsAt),
    location: input.location ?? null,
    // A block created through this API is always LifeOS's own.
    status: null,
    taskId: input.taskId ?? null,
    editable: true,
    courseCode: null,
    courseSlug: null,
    proposalId,
    // A block gets exactly the reminders it was created with, and a create that named none has
    // none. No default offset is invented here or anywhere else in this feature.
    reminders: input.reminders ?? [],
  }
  calendarBlocks = [...calendarBlocks, block]
  return block
}

// ── The Google Calendar mirror, in demo ─────────────────────────────────────
//
// FIVE EVENTS, and every one of them is a thing the connector really produces, so the demo
// exercises the shape rather than decorating it: a timed one-off, a timed WEEKLY series expanded
// into two visible occurrences (which is what `expandRule` does at sync time — nothing in the
// browser owns an RRULE engine), a multi-day all-day event, and a single all-day one that is
// TENTATIVE, because "Google said maybe" is a state the screen has to be able to draw.
//
// They are NOT `calendarBlocks`. A Google event is a mirror of a row that lives in Google, so it
// is `editable: false` and there is no demo write that can touch one — the same asymmetry the
// real table has (supabase/migrations/20260826150000_gcal_events.sql).

interface GoogleSeed {
  id: string
  /** Days after CAL_WEEK_START. 0 = Monday. */
  day: number
  start: number
  end: number
  title: string
  location?: string
  /** Google's own status word. Defaults to `confirmed` — most events are. */
  status?: 'confirmed' | 'tentative' | 'cancelled'
}

const GOOGLE_TIMED_SEEDS: GoogleSeed[] = [
  // Monday and Thursday — one WEEKLY;BYDAY=MO,TH series, as the two occurrences the sync writes.
  { id: 'gcal-standup-mon', day: 0, start: 9 * 60 + 30, end: 10 * 60, title: 'Team sync', location: 'Meet' },
  { id: 'gcal-standup-thu', day: 3, start: 9 * 60 + 30, end: 10 * 60, title: 'Team sync', location: 'Meet' },
  // Wednesday — today. A one-off somebody else put on the calendar, which is the whole point of
  // mirroring: it is on the day and the owner did not schedule it here. TENTATIVE on purpose —
  // the detail peek has to be able to draw "Google said maybe" on a TIMED block, not only on the
  // all-day events below, and this is the one seed that exercises it.
  {
    id: 'gcal-dentist-wed',
    day: 2,
    start: 15 * 60 + 30,
    end: 16 * 60 + 15,
    title: 'Dentist',
    location: 'Shattuck Ave',
    status: 'tentative',
  },
]

function googleSeedToBlock(seed: GoogleSeed): CalendarBlock {
  const date = addDays(CAL_WEEK_START, seed.day)
  return {
    id: seed.id,
    title: seed.title,
    source: 'google',
    state: 'accepted',
    date,
    startsAt: calTimestamp(date, seed.start),
    endsAt: calTimestamp(date, seed.end),
    startMinutes: seed.start,
    endMinutes: seed.end,
    location: seed.location ?? null,
    status: seed.status ?? 'confirmed',
    taskId: null,
    // THE READ-ONLY FLAG. There is no row behind this block for anything to update — it lives in
    // Google, and LifeOS never writes back.
    editable: false,
    courseCode: null,
    courseSlug: null,
    // A Google event was never proposed by anything. It mirrors somebody else's calendar.
    proposalId: null,
  }
}

const GOOGLE_BLOCKS: CalendarBlock[] = GOOGLE_TIMED_SEEDS.map(googleSeedToBlock)

const GOOGLE_ALL_DAY: CalendarAllDayEvent[] = [
  {
    id: 'gcal-visit',
    externalId: 'demo-visit@google.com',
    source: 'google',
    title: 'Sam visiting',
    // Friday through Sunday. `endDate` is INCLUSIVE; `endsAt` is the exclusive instant ICS uses,
    // i.e. the start of the Monday after — the one off-by-one this shape exists to settle.
    date: addDays(CAL_WEEK_START, 4),
    endDate: addDays(CAL_WEEK_START, 6),
    startsAt: calTimestamp(addDays(CAL_WEEK_START, 4), 0),
    endsAt: calTimestamp(addDays(CAL_WEEK_START, 7), 0),
    location: null,
    status: 'confirmed',
    editable: false,
  },
  {
    id: 'gcal-holiday',
    externalId: 'demo-holiday@google.com',
    source: 'google',
    title: 'Lab closed',
    date: addDays(CAL_WEEK_START, 1),
    endDate: addDays(CAL_WEEK_START, 1),
    startsAt: calTimestamp(addDays(CAL_WEEK_START, 1), 0),
    endsAt: calTimestamp(addDays(CAL_WEEK_START, 2), 0),
    location: null,
    // GOOGLE SAID MAYBE, so the screen says maybe. Never quietly promoted to confirmed.
    status: 'tentative',
    editable: false,
  },
]


const CLASS_KIND_LABEL: Record<ClassOccurrence['kind'], string> = {
  lecture: 'Lecture',
  discussion: 'Discussion',
  lab: 'Lab',
}

/** One day's classes, as read-only Canvas blocks. Derived from the courses, never seeded. */
function canvasBlocksOn(date: ISODate): CalendarBlock[] {
  return classOccurrences(date).map((o) => ({
    id: `class-${date}-${o.courseId}-${o.kind}-${o.startMinutes}`,
    title: `${o.courseCode} ${CLASS_KIND_LABEL[o.kind]}`,
    source: 'canvas' as const,
    state: 'accepted' as const,
    // Derived from the timetable Canvas synced. Nothing proposed it.
    proposalId: null,
    date,
    startsAt: calTimestamp(date, o.startMinutes),
    endsAt: calTimestamp(date, o.endMinutes),
    startMinutes: o.startMinutes,
    endMinutes: o.endMinutes,
    location: o.location,
    // Canvas has no concept of tentative or cancelled. Only Google states one.
    status: null,
    taskId: null,
    // THE READ-ONLY FLAG. There is no row behind this block for anything to update.
    editable: false,
    courseCode: o.courseCode,
    courseSlug: courseSlug(o.courseCode),
  }))
}

/** The all-day row for a range: Canvas's own deadlines, with Canvas's own copy. */
function dueItemsBetween(from: ISODate, to: ISODate): CalendarDueItem[] {
  return ASSIGNMENTS.flatMap((a) => {
    const date = dueDateOf(a)
    if (date === null || date < from || date > to) return []
    // Local midnight means Canvas gave a day and no time — the same derivation the Worker makes
    // (apps/worker/src/lib/calendar.ts), stated once in each implementation of the contract.
    const hhmm = a.dueAt!.slice(11, 16)
    const minutes = Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5))
    const hasTime = minutes !== 0
    return [
      {
        id: `due-${a.id}`,
        assignmentId: a.id,
        date,
        title: a.title,
        dueAt: a.dueAt,
        hasTime,
        dueMinutes: hasTime ? minutes : null,
        label: dueLabel(a.title, hasTime, hasTime ? minutes : null),
        courseCode: a.courseCode,
        courseSlug: courseSlug(a.courseCode),
        status: a.status,
      },
    ]
  }).sort((x, y) => (x.date === y.date ? (x.title < y.title ? -1 : 1) : x.date < y.date ? -1 : 1))
}

/** Thrown when something tries to change a block LifeOS does not own. */
export class ReadOnlyBlockError extends Error {
  constructor(id: string) {
    super(
      `${id} is a Canvas event. It is derived from your synced courses and there is no row ` +
        'behind it to change — schedule your own work around it instead.',
    )
    this.name = 'ReadOnlyBlockError'
  }
}

// ── Learning ────────────────────────────────────────────────────────────────
//
// TWO IMPORTED PLANS, IN FULL. docs/design/handoff/"LifeOS Learning.dc.html" (9a/9b) needs a whole
// curriculum behind it — eight units and 182 daily items for algebra, six and 118 for Python —
// because the track page draws the outline, the pace chart and the session log off the same plan
// and any one of them inventing its own numbers would show up as three answers to one question.
//
// THE MODEL, one sentence: a plan is an ORDERED LIST OF ITEMS, one per calendar day from its start
// date. Everything else on both screens is derived from that list plus the days work was actually
// logged on — progress, the pace phrase, the current unit, today's item, the week strip, the
// heatmap. Nothing below stores a percentage, a streak or a "days ahead" number.
//
// WHY BEING AHEAD IS NOT THE SAME AS BEING ON TODAY'S ITEM. Algebra is two days ahead, so the item
// the plan dates today is already finished and "today's item" is the next unfinished one further
// down the list. That distinction is the reason `nextItem` exists and the reason the outline's
// `today` chip sits on 3.4 rather than on whatever the calendar says.
//
// WHERE THE NUMBERS CAME FROM. The mockup's own figures are the target and they are reproduced by
// construction, not by hardcoding: 46 of 182 = 25%, unit 3 current in week 7, +2 days for algebra;
// 21 of 118 = 18%, unit 2 current, −4 days for Python. Two of the mockup's unit counts had to move
// (unit 1 to 21 items, unit 8 to 17) because the eight units as drawn sum to 186 while the header
// says 182 — the totals are made to agree here rather than left to disagree on screen.

/**
 * Algebra's plan: 182 items — one a day, which is a 26-week arc — imported the day before it
 * starts. The length is not written down anywhere; it is the number of items in ALGEBRA_UNITS.
 */
const PLAN_START: ISODate = '2026-08-04'

/** Python's plan is shorter and started later — which is most of why it is behind. */
const PY_PLAN_START: ISODate = '2026-08-23'

/**
 * Days with logged learning activity. The gaps are the point: Aug 29–30 (a weekend
 * that got away) and Sep 4. Everything after Sep 4 is unbroken, which is what makes
 * the current streak exactly 12 days ending today — computed below, never hardcoded.
 */
const LEARNING_GAPS: ISODate[] = ['2026-08-29', '2026-08-30', '2026-09-04']

function activityDays(from: ISODate, to: ISODate): ISODate[] {
  const out: ISODate[] = []
  for (let d = from; d <= to; d = addDays(d, 1)) {
    if (!LEARNING_GAPS.includes(d)) out.push(d)
  }
  return out
}

/** Every day anything was worked on. Algebra runs the whole window, so this is the union. */
const ACTIVITY_DAYS = activityDays(PLAN_START, SEED_TODAY)

/** Python stopped three days ago — which is what the overview card's "last: 3 days ago" reads. */
const PY_LAST_ACTIVE: ISODate = addDays(SEED_TODAY, -3)
const PY_ACTIVITY_DAYS = activityDays(PY_PLAN_START, PY_LAST_ACTIVE)

/**
 * Days two items were finished instead of one. This is the ONLY mechanism by which a track gets
 * ahead of its plan, and the counts are exact rather than approximate: algebra has 41 activity
 * days and 46 finished items, so five of them are doubles; Python has 19 and 21, so two are.
 * A dataset that did not add up would be caught by `completionDates` below, loudly.
 */
const DOUBLE_DAYS: Record<string, ISODate[]> = {
  'track-algebra': ['2026-08-09', '2026-08-16', '2026-08-23', '2026-09-06', '2026-09-13'],
  'track-python': ['2026-08-27', '2026-09-08'],
}

interface UnitSeed {
  name: string
  items: string[]
}

/**
 * ALGEBRA — 21 + 22 + 24 + 20 + 24 + 28 + 26 + 17 = 182 items across eight units. Unit names are
 * the mockup's own, verbatim; 3.1–3.6 are its own item titles, verbatim, because they are the rows
 * the screenshot pair is judged on.
 */
const ALGEBRA_UNITS: UnitSeed[] = [
  {
    name: 'Unit 1 · Foundations and order of operations',
    items: [
      '1.1 Number sense', '1.2 Place value and rounding', '1.3 Order of operations',
      '1.4 Factors and multiples', '1.5 Prime factorisation',
      '1.6 Greatest common factor and least common multiple', '1.7 Fraction arithmetic',
      '1.8 Fractions and decimals', '1.9 Ratios and rates', '1.10 Proportions',
      '1.11 Negative numbers', '1.12 Negative numbers practice', '1.13 The number line',
      '1.14 Absolute value', '1.15 Percent and percent change', '1.16 Percent word problems',
      '1.17 Unit conversions', '1.18 Exponent notation', '1.19 Square roots',
      '1.20 Unit 1 review', '1.21 Unit 1 checkpoint',
    ],
  },
  {
    name: 'Unit 2 · Expressions and properties',
    items: [
      '2.1 Terms and coefficients', '2.2 Simplifying expressions',
      '2.3 Simplifying expressions practice', '2.4 Order of operations review',
      '2.5 The distributive property', '2.6 Exponent rules', '2.7 Exponent rules practice set',
      '2.8 Combining like terms', '2.9 Evaluating expressions',
      '2.10 Translating words into expressions', '2.11 Factoring out a common term',
      '2.12 Factoring practice', '2.13 Rational expressions',
      '2.14 Simplifying rational expressions', '2.15 Adding rational expressions',
      '2.16 Multiplying binomials', '2.17 Special products', '2.18 Factoring trinomials',
      '2.19 Factoring trinomials practice', '2.20 Difference of squares', '2.21 Unit 2 review',
      '2.22 Unit 2 checkpoint',
    ],
  },
  {
    name: 'Unit 3 · Linear equations',
    items: [
      '3.1 One-step equations', '3.2 Variables on both sides',
      '3.3 Distributive property in equations', '3.4 Solving two-step equations · 12 problems',
      '3.5 Equations with fractions', '3.6 Word problems: rate and distance',
      '3.7 Literal equations', '3.8 Slope from two points', '3.9 Slope-intercept form',
      '3.10 Point-slope form', '3.11 Standard form',
      '3.12 Graphing lines from slope-intercept', '3.13 Graphing lines from intercepts',
      '3.14 Parallel and perpendicular lines', '3.15 Writing the equation of a line',
      '3.16 Linear models from a table', '3.17 Linear models from a word problem',
      '3.18 Interpreting slope in context', '3.19 Direct variation',
      '3.20 Piecewise-defined lines', '3.21 Mixed practice: solving',
      '3.22 Mixed practice: graphing', '3.23 Unit 3 review', '3.24 Unit 3 checkpoint',
    ],
  },
  {
    name: 'Unit 4 · Inequalities',
    items: [
      '4.1 Graphing inequalities on a number line', '4.2 One-step inequalities',
      '4.3 Multiplying by a negative — flipping the sign', '4.4 Two-step inequalities',
      '4.5 Inequalities with variables on both sides', '4.6 Compound inequalities: and',
      '4.7 Compound inequalities: or', '4.8 Absolute-value equations',
      '4.9 Absolute-value inequalities', '4.10 Word problems with inequalities',
      '4.11 Interval notation', '4.12 Graphing linear inequalities in two variables',
      '4.13 Systems of linear inequalities', '4.14 Feasible regions',
      '4.15 Mixed practice: solving inequalities', '4.16 Mixed practice: graphing inequalities',
      '4.17 Inequality word problems II', '4.18 Error hunt: common inequality mistakes',
      '4.19 Unit 4 review', '4.20 Unit 4 checkpoint',
    ],
  },
  {
    name: 'Unit 5 · Systems of equations',
    items: [
      '5.1 Solving by graphing', '5.2 Substitution method', '5.3 Elimination method',
      '5.4 Word problems with two unknowns', '5.5 Choosing a method',
      '5.6 Systems with no solution', '5.7 Systems with infinitely many solutions',
      '5.8 Mixture problems', '5.9 Rate problems with two unknowns', '5.10 Break-even problems',
      '5.11 Three-variable systems: setup', '5.12 Three-variable systems: elimination',
      '5.13 Matrices as a shorthand', '5.14 Row operations', '5.15 Solving with matrices',
      '5.16 Determinants of 2×2 matrices', '5.17 Cramer’s rule',
      '5.18 Systems in context: chemistry', '5.19 Systems in context: budgets',
      '5.20 Mixed practice: substitution', '5.21 Mixed practice: elimination',
      '5.22 Error hunt: systems', '5.23 Unit 5 review', '5.24 Unit 5 checkpoint',
    ],
  },
  {
    name: 'Unit 6 · Quadratics and factoring',
    items: [
      '6.1 Quadratic expressions', '6.2 Factoring x² + bx + c', '6.3 Factoring ax² + bx + c',
      '6.4 Difference of two squares', '6.5 Perfect-square trinomials',
      '6.6 Factoring by grouping', '6.7 Solving by factoring', '6.8 The zero-product property',
      '6.9 Completing the square', '6.10 The quadratic formula', '6.11 The discriminant',
      '6.12 Graphing parabolas from standard form', '6.13 Vertex form',
      '6.14 Converting to vertex form', '6.15 Axis of symmetry and vertex',
      '6.16 Intercepts of a parabola', '6.17 Maximum and minimum problems',
      '6.18 Projectile motion problems', '6.19 Area problems', '6.20 Quadratic inequalities',
      '6.21 Systems with a quadratic', '6.22 Complex numbers: introduction',
      '6.23 Arithmetic with complex numbers', '6.24 Complex roots',
      '6.25 Mixed practice: factoring', '6.26 Mixed practice: solving', '6.27 Unit 6 review',
      '6.28 Unit 6 checkpoint',
    ],
  },
  {
    name: 'Unit 7 · Functions and graphing',
    items: [
      '7.1 What a function is', '7.2 Function notation', '7.3 Domain and range',
      '7.4 Evaluating functions', '7.5 Piecewise functions', '7.6 Graphing piecewise functions',
      '7.7 Transformations: shifts', '7.8 Transformations: stretches',
      '7.9 Transformations: reflections', '7.10 Combining transformations',
      '7.11 Inverse functions', '7.12 Verifying an inverse', '7.13 Composition of functions',
      '7.14 Even and odd functions', '7.15 Absolute-value functions', '7.16 Radical functions',
      '7.17 Rational functions', '7.18 Asymptotes', '7.19 Exponential functions',
      '7.20 Exponential growth and decay', '7.21 Logarithms', '7.22 Logarithm rules',
      '7.23 Solving exponential equations', '7.24 Modelling with functions', '7.25 Unit 7 review',
      '7.26 Unit 7 checkpoint',
    ],
  },
  {
    name: 'Unit 8 · Pre-calculus bridge',
    items: [
      '8.1 Sequences and series', '8.2 Arithmetic sequences', '8.3 Geometric sequences',
      '8.4 Summation notation', '8.5 The unit circle', '8.6 Radian measure',
      '8.7 Sine, cosine and tangent', '8.8 Graphing trigonometric functions',
      '8.9 Trigonometric identities', '8.10 Solving trigonometric equations',
      '8.11 Polar coordinates', '8.12 Vectors in the plane', '8.13 Limits, informally',
      '8.14 One-sided limits', '8.15 Continuity', '8.16 The idea of a derivative',
      '8.17 Bridge checkpoint',
    ],
  },
]

/** PYTHON — 19 + 18 + 20 + 20 + 20 + 21 = 118 items across six units. */
const PYTHON_UNITS: UnitSeed[] = [
  {
    name: 'Unit 1 · Syntax and control flow',
    items: [
      '1.1 Running your first script', '1.2 Values and types', '1.3 Names and assignment',
      '1.4 Strings and formatting', '1.5 String methods', '1.6 Numbers and arithmetic',
      '1.7 Booleans and comparisons', '1.8 if / elif / else', '1.9 Nested conditions',
      '1.10 Truthiness', '1.11 Input and output', '1.12 Comments and docstrings',
      '1.13 Reading a traceback', '1.14 The REPL as a notebook', '1.15 Style: naming things',
      '1.16 Style: line length and imports', '1.17 Small exercises: conditionals',
      '1.18 Unit 1 review', '1.19 Unit 1 checkpoint',
    ],
  },
  {
    name: 'Unit 2 · Loops and lists',
    items: [
      '2.1 Lists and indexing', '2.2 Slicing', '2.3 While loops · read + 6 exercises',
      '2.4 For loops', '2.5 range()', '2.6 break and continue', '2.7 Nested loops',
      '2.8 List methods', '2.9 Sorting lists', '2.10 List comprehensions', '2.11 Tuples',
      '2.12 Unpacking', '2.13 enumerate() and zip()', '2.14 Two-dimensional lists',
      '2.15 Loop patterns: accumulate', '2.16 Loop patterns: search', '2.17 Unit 2 review',
      '2.18 Unit 2 checkpoint',
    ],
  },
  {
    name: 'Unit 3 · Functions and modules',
    items: [
      '3.1 Defining a function', '3.2 Parameters and arguments', '3.3 Return values',
      '3.4 Default arguments', '3.5 Keyword arguments', '3.6 *args and **kwargs', '3.7 Scope',
      '3.8 Docstrings that earn their place', '3.9 Pure functions', '3.10 Recursion, gently',
      '3.11 Lambdas', '3.12 map, filter, and when not to',
      '3.13 Importing from the standard library', '3.14 Writing your own module',
      '3.15 __main__ and scripts', '3.16 Packages and folders', '3.17 Virtual environments',
      '3.18 pip and requirements', '3.19 Unit 3 review', '3.20 Unit 3 checkpoint',
    ],
  },
  {
    name: 'Unit 4 · Dictionaries, sets and text',
    items: [
      '4.1 Dictionaries', '4.2 Dictionary methods', '4.3 Iterating a dictionary',
      '4.4 Nested dictionaries', '4.5 Dictionary comprehensions', '4.6 Sets',
      '4.7 Set operations', '4.8 Counting with a dictionary', '4.9 collections.Counter',
      '4.10 defaultdict', '4.11 Word-frequency exercise', '4.12 Text cleaning',
      '4.13 Regular expressions: basics', '4.14 Regular expressions: groups',
      '4.15 Splitting and joining', '4.16 Unicode and encodings', '4.17 CSV as text',
      '4.18 JSON as text', '4.19 Unit 4 review', '4.20 Unit 4 checkpoint',
    ],
  },
  {
    name: 'Unit 5 · Files, errors and testing',
    items: [
      '5.1 Opening a file', '5.2 Reading line by line', '5.3 Writing files',
      '5.4 Context managers', '5.5 Paths with pathlib', '5.6 Working with directories',
      '5.7 Reading CSV with the csv module', '5.8 Reading and writing JSON', '5.9 Exceptions',
      '5.10 try / except / finally', '5.11 Raising your own exceptions',
      '5.12 Custom exception classes', '5.13 Logging instead of printing',
      '5.14 assert and invariants', '5.15 Your first test with pytest', '5.16 Fixtures',
      '5.17 Parametrised tests', '5.18 Debugging with breakpoint()', '5.19 Unit 5 review',
      '5.20 Unit 5 checkpoint',
    ],
  },
  {
    name: 'Unit 6 · Small programs end to end',
    items: [
      '6.1 Choosing a project', '6.2 Sketching the data first', '6.3 argparse basics',
      '6.4 A command-line tool: skeleton', '6.5 A command-line tool: reading input',
      '6.6 A command-line tool: output', '6.7 Handling bad input',
      '6.8 Reading an API with requests', '6.9 Rate limits and retries',
      '6.10 Caching a response', '6.11 Scheduling a script',
      '6.12 Reading environment variables', '6.13 Secrets, and where not to put them',
      '6.14 Packaging the tool', '6.15 Writing the README', '6.16 Refactoring pass one',
      '6.17 Refactoring pass two', '6.18 Tests for the tool', '6.19 Shipping it',
      '6.20 Unit 6 review', '6.21 Final checkpoint',
    ],
  },
]

/**
 * The owner's own words about a session, keyed by the item he wrote them against. Notes belong to
 * ITEMS rather than to dates: the date a thing was finished moves whenever the schedule does, and a
 * note that drifted onto a different item would be the app putting words in his mouth.
 */
const SESSION_NOTES: Record<string, string> = {
  'track-algebra:0': 'first session — plan imported yesterday',
  'track-algebra:7': 'slow but fine',
  'track-algebra:20': 'passed on the second attempt',
  'track-algebra:22': 'kept mixing up sign flips',
  'track-algebra:28': 'did this on the bus, half-distracted',
  'track-algebra:43': 'easy — could have done two items',
  'track-algebra:45': 'struggled with factoring the negatives out front',
  'track-python:0': 'set up the environment first, which took the whole hour',
  'track-python:19': 'lists finally clicked',
}

// ── The v2 plan fields (docs/LEARNING-PLAN-FORMAT.md) ───────────────────────
//
// RESOURCES, ESTIMATES AND INSTRUCTIONS, seeded the way a real imported plan carries them: the
// track's standing library on the track, and the thing-to-open on the ITEM, inherited from the
// unit the item belongs to because that is how a plan actually reads ("PCC ch. 4" is a chapter,
// and a chapter is a unit's worth of items).
//
// THE TAIL UNITS HAVE NONE ON PURPOSE. A plan authored in one sitting names resources for the
// part its author could see, and the far end of a 26-week arc is genuinely unlinked. The screens
// have to draw that state — an item with no resource shows NO button, not a dead one — and a
// dataset where every row is complete would never prove it.

/** Each track's standing library: the things opened every session. */
const PLAN_RESOURCES: Record<string, LearningResource[]> = {
  'track-algebra': [
    { label: 'OpenStax — Elementary Algebra 2e', url: 'https://openstax.org/details/books/elementary-algebra-2e', kind: 'book' },
    { label: 'Khan Academy — Algebra 1', url: 'https://www.khanacademy.org/math/algebra', kind: 'course' },
    { label: 'Desmos graphing calculator', url: 'https://www.desmos.com/calculator', kind: 'doc' },
  ],
  'track-python': [
    { label: 'Python Crash Course, 3rd ed.', url: 'https://ehmatthes.github.io/pcc_3e/', kind: 'book' },
    { label: 'Exercism — Python track', url: 'https://exercism.org/tracks/python', kind: 'practice' },
  ],
}

/** unit → the thing its items open, keyed `trackId:unitNumber`. Missing means the plan named none. */
const UNIT_RESOURCES: Record<string, LearningResource> = {
  'track-algebra:1': { label: 'OpenStax ch. 1 — Foundations', url: 'https://openstax.org/books/elementary-algebra-2e/pages/1-introduction', kind: 'book' },
  'track-algebra:2': { label: 'OpenStax ch. 6 — Polynomials', url: 'https://openstax.org/books/elementary-algebra-2e/pages/6-introduction', kind: 'book' },
  'track-algebra:3': { label: 'Khan — Linear equations unit', url: 'https://www.khanacademy.org/math/algebra/x2f8bb11595b61c86:linear-equations-graphs', kind: 'course' },
  'track-algebra:4': { label: 'Khan — Inequalities unit', url: 'https://www.khanacademy.org/math/algebra/x2f8bb11595b61c86:solve-equations-inequalities', kind: 'course' },
  'track-algebra:5': { label: 'OpenStax ch. 4 — Systems', url: 'https://openstax.org/books/elementary-algebra-2e/pages/4-introduction', kind: 'book' },
  'track-algebra:6': { label: 'Desmos — quadratics activity', url: 'https://www.desmos.com/calculator', kind: 'practice' },
  'track-python:1': { label: 'PCC ch. 1-2 — Getting started', url: 'https://ehmatthes.github.io/pcc_3e/', kind: 'book' },
  'track-python:2': { label: 'PCC ch. 3-4 — Lists and loops', url: 'https://ehmatthes.github.io/pcc_3e/', kind: 'book' },
  'track-python:3': { label: 'Exercism — Python exercises', url: 'https://exercism.org/tracks/python/exercises', kind: 'practice' },
  'track-python:4': { label: 'Python docs — the standard library', url: 'https://docs.python.org/3/library/', kind: 'doc' },
}

/** The plan's own estimate for a sitting in this unit, keyed the same way. Missing = no guess. */
const UNIT_MINUTES: Record<string, number> = {
  'track-algebra:1': 30,
  'track-algebra:2': 40,
  'track-algebra:3': 45,
  'track-algebra:4': 45,
  'track-python:1': 40,
  'track-python:2': 50,
}

/**
 * How to do a particular item well, in the plan's words. SPARSE on purpose: a plan writes these
 * where they matter, and an app that showed a generic sentence on every row would be authoring
 * advice nobody gave.
 */
const ITEM_INSTRUCTIONS: Record<string, string> = {
  // Keyed by the item's index in the flattened plan, so the sentence lands on the item it is
  // about: 43 is '3.1 One-step equations', 46 is '3.4 Solving two-step equations · 12 problems'
  // (the one the overview offers today), 47 is '3.5 Equations with fractions'.
  'track-algebra:43': 'Work these on paper before checking. The point is the sign discipline, not the answer.',
  'track-algebra:46': 'Twelve problems, one sitting. Write the check step for each — substitute your answer back in.',
  'track-algebra:47': 'Clear the fractions first by multiplying through by the common denominator, then solve as usual.',
  'track-python:20': 'Type every example by hand — no copy-paste. Note anything that surprised you in the session note.',
  'track-python:21': 'Do the first four exercises without looking at the hints. Use the hints on the fifth if you need them.',
}

interface PlanSeed {
  id: string
  name: string
  subtitle: string
  start: ISODate
  units: UnitSeed[]
  importedAt: string
  /** How many items are finished. A PREFIX of the plan — you do not skip ahead in a curriculum. */
  itemsDone: number
  activityDays: ISODate[]
}

const PLANS: PlanSeed[] = [
  {
    id: 'track-algebra',
    name: 'Algebra',
    subtitle: 'Alg 1 → Calculus',
    start: PLAN_START,
    units: ALGEBRA_UNITS,
    importedAt: '2026-08-03T16:30:00-07:00',
    // 44 items were planned by today and 46 are finished, which is the +2 the card states.
    itemsDone: 46,
    activityDays: ACTIVITY_DAYS,
  },
  {
    id: 'track-python',
    name: 'Python fundamentals',
    subtitle: 'Syntax → small projects',
    start: PY_PLAN_START,
    units: PYTHON_UNITS,
    importedAt: '2026-08-22T21:10:00-07:00',
    // 25 planned by today against 21 finished — the −4 the card states.
    itemsDone: 21,
    activityDays: PY_ACTIVITY_DAYS,
  },
]

const planOf = (trackId: string) => PLANS.find((p) => p.id === trackId)

/** Every item of a plan, flattened into the order the plan runs them. */
interface PlanItem {
  id: string
  index: number
  title: string
  unitNumber: number
  unitName: string
  plannedDate: ISODate
  /** The one thing to open. Null in the tail units, where the plan named none. */
  resource: LearningResource | null
  minutesEstimate: number | null
  instructions: string
}

function flattenPlan(plan: PlanSeed): PlanItem[] {
  const out: PlanItem[] = []
  plan.units.forEach((unit, u) => {
    for (const title of unit.items) {
      const index = out.length
      out.push({
        id: `item-${plan.id}-${String(index).padStart(3, '0')}`,
        index,
        title,
        unitNumber: u + 1,
        unitName: unit.name,
        plannedDate: addDays(plan.start, index),
        // `?? null` and not `?? somethingPlausible`: a unit the plan gave no resource for yields
        // an item with none, which is what the row is required to draw honestly.
        resource: UNIT_RESOURCES[`${plan.id}:${u + 1}`] ?? null,
        minutesEstimate: UNIT_MINUTES[`${plan.id}:${u + 1}`] ?? null,
        instructions: ITEM_INSTRUCTIONS[`${plan.id}:${index}`] ?? '',
      })
    }
  })
  return out
}

const PLAN_ITEMS: Record<string, PlanItem[]> = Object.fromEntries(
  PLANS.map((p) => [p.id, flattenPlan(p)]),
)

/**
 * Which day each finished item was finished on: one item per activity day, two on a double day,
 * in plan order. It THROWS rather than truncating when the days and the items do not add up — a
 * dataset that quietly loses a session is a dataset whose streak and pace disagree, and finding
 * that by eye on a screenshot is exactly the failure this whole port is guarding against.
 */
function completionDates(plan: PlanSeed): ISODate[] {
  const doubles = DOUBLE_DAYS[plan.id] ?? []
  const out: ISODate[] = []
  for (const day of plan.activityDays) {
    const take = doubles.includes(day) ? 2 : 1
    for (let i = 0; i < take && out.length < plan.itemsDone; i++) out.push(day)
  }
  if (out.length !== plan.itemsDone) {
    throw new Error(
      `${plan.id}: ${plan.activityDays.length} activity days with ${doubles.length} doubles ` +
        `carry ${out.length} items, but the plan says ${plan.itemsDone} are finished.`,
    )
  }
  return out
}

/** itemId → { date it was finished, minutes logged }. The one mutable fact in this domain. */
interface Completion {
  date: ISODate
  minutes: number
  /**
   * What he wrote when he logged it. Null for the seeded completions — their notes live in
   * SESSION_NOTES, keyed by item, because a seeded dataset's prose is authored beside the plan
   * rather than typed. A note written THROUGH the workspace lands here and wins.
   */
  note?: string | null
}

function seedDoneItems(): Map<string, Completion> {
  const done = new Map<string, Completion>()
  for (const plan of PLANS) {
    const dates = completionDates(plan)
    const items = PLAN_ITEMS[plan.id]!
    dates.forEach((date, i) => {
      const item = items[i]!
      done.set(item.id, {
        date,
        minutes: 18 + Math.round(seededUnit(`min:${plan.id}:${item.index}`) * 42),
      })
    })
  }
  return done
}

function computeStreak(done: Map<string, Completion>): LearningStreak {
  const byDate = new Map<ISODate, { minutes: number; items: number }>()
  for (const { date, minutes } of done.values()) {
    const prev = byDate.get(date) ?? { minutes: 0, items: 0 }
    byDate.set(date, { minutes: prev.minutes + minutes, items: prev.items + 1 })
  }
  const days = [...byDate.entries()]
    .map(([date, v]) => ({ date, ...v }))
    .sort((a, b) => (a.date < b.date ? -1 : 1))

  let longest = 0
  let run = 0
  let current = 0
  for (let i = 0; i < days.length; i++) {
    const prev = days[i - 1]
    run = prev && daysBetween(prev.date, days[i]!.date) === 1 ? run + 1 : 1
    longest = Math.max(longest, run)
    current = run // the last run computed is the one ending at the newest day
  }
  const lastDate = days.length ? days[days.length - 1]!.date : null
  // A streak only counts as "current" if it reaches today.
  if (!lastDate || daysBetween(lastDate, SEED_TODAY) > 1) current = 0
  return { current, longest, lastDate, days }
}

// ── Interests ───────────────────────────────────────────────────────────────
//
// THE SAVES CORPUS, and the nightly clusters over it — docs/design/handoff/"LifeOS
// Interests.dc.html", option 7a.
//
// THE AI-VIDEO CLUSTER IS THE HERO, and it is not decoration: it is the owner's actual live
// use case, and it is the one cluster a reviewer opens. Its fourteen saves, their exact
// titles, their sources and their ages are transcribed from the mockup, so the ported screen
// reads back against the .dc.html line for line.
//
// WHY 247 AND NOT FIFTY. The band counts `247 items · 12 in inbox`, and those are the numbers
// the screen is judged against. A hard-coded 247 over a fifty-row array would be the app
// claiming to know a size it did not measure, so the archive is GENERATED — the same trick the
// journal backfill uses above, and for the same reason: what matters is that the SHAPE is real.
// Every count on the screen is derived from this array and cannot drift away from it.
//
// THREE POPULATIONS, and the difference between them is what makes the inbox meaningful:
//   · 38 CLUSTERED saves — hand-written, in the five clusters the mockup names.
//   · 12 INBOX saves — unfiled AND unread. Arrived, not dealt with. That is what an inbox is,
//     and it is why a merely-unread save inside a cluster does not count toward it.
//   · 197 ARCHIVE saves — read, and never grouped by the nightly job. `topic: null` means "not
//     placed", not "miscellaneous", and the screen never claims the clusters cover everything.
//
// Their dates stay clear of 2026-09-15, which is the journal day page's screenshot: a save
// landing there would silently add a row to another screen's proof shot.

const SAVE_SUMMARY_MODEL = 'claude-haiku-4.5'

/** A two-liner, with the provenance that lets it render at all. */
function aiSummary(text: string, sourceRef: string, generatedAt: ISOTimestamp): SaveSummary {
  return { text, provenance: { model: SAVE_SUMMARY_MODEL, sourceRef, generatedAt } }
}

/** The hand-written corpus. Tuple form so fifty saves stay readable as a table. */
type SaveSeed = [
  id: string,
  title: string,
  source: SaveSource,
  sourceDetail: string,
  url: string,
  topic: string | null,
  savedAt: ISOTimestamp,
  unread: boolean,
  durationSec: number | null,
]

// AI video models — 14. Newest first; the mockup's ten, then the four behind "4 more in AI
// video models", the oldest of which is what makes the footer read "Mar 2026".
const AI_VIDEO_SEEDS: SaveSeed[] = [
  ['save-prompt-adherence', 'Benchmarking prompt-adherence across open video models', 'reddit', 'r/LocalLLaMA', 'https://www.reddit.com/r/LocalLLaMA/comments/1n4k2p/prompt_adherence_video', 'AI video models', '2026-09-14T11:12:00-07:00', true, null],
  ['save-wan-motion', 'Wan 2.2 motion quality comparison — 40 test prompts, settings included', 'reddit', 'r/StableDiffusion', 'https://www.reddit.com/r/StableDiffusion/comments/1n2f8x/wan22_motion', 'AI video models', '2026-09-13T14:22:00-07:00', true, null],
  ['save-img2vid-workflow', 'My img2vid workflow for character consistency (JSON in comments)', 'reddit', 'r/comfyui', 'https://www.reddit.com/r/comfyui/comments/1n0b4d/img2vid_consistency', 'AI video models', '2026-09-11T20:45:00-07:00', true, null],
  ['save-camera-control', 'Camera-control LoRAs: what actually works for dolly shots', 'reddit', 'r/aivideo', 'https://www.reddit.com/r/aivideo/comments/1mxr7q/camera_control_loras', 'AI video models', '2026-09-09T09:10:00-07:00', true, null],
  ['save-temporal-coherence', 'Evaluating temporal coherence in video diffusion', 'article', 'huggingface.co', 'https://huggingface.co/blog/temporal-coherence-eval', 'AI video models', '2026-09-08T16:30:00-07:00', false, null],
  ['save-hands', 'Why video models fail at hands — and the fix', 'youtube', 'YouTube', 'https://www.youtube.com/watch?v=v1d3o-hands', 'AI video models', '2026-09-02T21:05:00-07:00', false, 1080],
  ['save-negative-prompts', 'Negative prompts that actually reduce warping (tested on 12 seeds)', 'reddit', 'r/StableDiffusion', 'https://www.reddit.com/r/StableDiffusion/comments/1mk9v2/negative_warping', 'AI video models', '2026-08-26T13:40:00-07:00', false, null],
  ['save-dit-long-horizon', 'Diffusion transformers for long-horizon video generation', 'article', 'arxiv.org', 'https://arxiv.org/abs/2608.04471', 'AI video models', '2026-08-25T10:02:00-07:00', false, null],
  ['save-two-pass-faces', 'Anyone else getting better faces from two-pass upscaling?', 'reddit', 'r/aivideo', 'https://www.reddit.com/r/aivideo/comments/1m9d3s/two_pass_faces', 'AI video models', '2026-08-17T22:18:00-07:00', true, null],
  ['save-cost-per-second', 'Cost per second across hosted video models — August numbers', 'article', 'replicate.com', 'https://replicate.com/blog/video-cost-per-second-august', 'AI video models', '2026-08-12T08:55:00-07:00', false, null],
  ['save-batch-overnight', 'Node graph for batch-testing 40 prompts overnight', 'reddit', 'r/comfyui', 'https://www.reddit.com/r/comfyui/comments/1m1c8k/batch_overnight', 'AI video models', '2026-08-05T23:41:00-07:00', false, null],
  ['save-wan-vs-hunyuan', 'Wan 2.1 vs Hunyuan: the same 12 shots, no cherry-picking', 'youtube', 'YouTube', 'https://www.youtube.com/watch?v=w4n-vs-huny', 'AI video models', '2026-07-12T19:26:00-07:00', false, 1560],
  ['save-interpolation', 'Thread: why frame interpolation makes motion look worse, not better', 'x', 'x.com', 'https://x.com/demo/status/1826004417', 'AI video models', '2026-05-20T12:03:00-07:00', false, null],
  ['save-first-img2vid', 'First img2vid run that did not melt the face — settings inside', 'reddit', 'r/StableDiffusion', 'https://www.reddit.com/r/StableDiffusion/comments/1jd0p7/first_img2vid', 'AI video models', '2026-03-09T17:14:00-08:00', false, null],
]

const OTHER_CLUSTER_SEEDS: SaveSeed[] = [
  // Cognitive science — 9.
  ['save-extended-mind', 'Best intro reading on the extended mind?', 'reddit', 'r/cogsci', 'https://www.reddit.com/r/cogsci/comments/1n2a1r/extended_mind', 'Cognitive science', '2026-09-13T22:03:00-07:00', false, null],
  ['save-predictive-processing', 'Predictive processing, explained without the free-energy detour', 'youtube', 'YouTube', 'https://www.youtube.com/watch?v=pp-no-fep', 'Cognitive science', '2026-09-07T19:00:00-07:00', false, 1560],
  ['save-mind-not-computer', 'The mind is not a computer, and the metaphor is doing work', 'article', 'aeon.co', 'https://aeon.co/essays/the-mind-is-not-a-computer', 'Cognitive science', '2026-09-01T11:20:00-07:00', false, null],
  ['save-sleep-memory', 'What actually happens to memory during sleep consolidation', 'reddit', 'r/neuro', 'https://www.reddit.com/r/neuro/comments/1mi7f2/sleep_consolidation', 'Cognitive science', '2026-08-24T08:36:00-07:00', false, null],
  ['save-embodied-sep', 'Embodied cognition — the entry that is worth the hour', 'article', 'plato.stanford.edu', 'https://plato.stanford.edu/entries/embodied-cognition/', 'Cognitive science', '2026-08-15T15:48:00-07:00', false, null],
  ['save-replication', 'Papers where the replication failed and the textbook did not notice', 'reddit', 'r/cogsci', 'https://www.reddit.com/r/cogsci/comments/1lp2v9/replication_textbooks', 'Cognitive science', '2026-07-30T20:11:00-07:00', false, null],
  ['save-attention-lecture', 'A ninety-minute lecture on attention that is better than my textbook', 'youtube', 'YouTube', 'https://www.youtube.com/watch?v=attention-90', 'Cognitive science', '2026-07-02T18:05:00-07:00', false, 5400],
  ['save-grounding-survey', 'Grounding language in perception: a survey worth skimming', 'article', 'arxiv.org', 'https://arxiv.org/abs/2606.01188', 'Cognitive science', '2026-06-11T13:29:00-07:00', false, null],
  ['save-chinese-room', 'Chinese room objections that are actually new', 'reddit', 'r/philosophy', 'https://www.reddit.com/r/philosophy/comments/1kb6h4/chinese_room_new', 'Cognitive science', '2026-04-18T16:52:00-07:00', false, null],

  // Study methods — 5.
  ['save-interleaving', 'Interleaving beat blocking for me and I have the grades to prove it', 'reddit', 'r/GetStudying', 'https://www.reddit.com/r/GetStudying/comments/1n1d6m/interleaving', 'Study methods', '2026-09-12T09:33:00-07:00', false, null],
  ['save-spaced-rep', 'Spaced repetition, explained by playing with it', 'article', 'ncase.me', 'https://ncase.me/remember/', 'Study methods', '2026-09-04T14:07:00-07:00', false, null],
  ['save-read-a-paper', 'How to read a paper in twenty minutes without lying to yourself', 'youtube', 'YouTube', 'https://www.youtube.com/watch?v=read-a-paper', 'Study methods', '2026-08-21T10:44:00-07:00', false, 1200],
  ['save-anki-settings', 'The deck settings I stopped fiddling with', 'reddit', 'r/Anki', 'https://www.reddit.com/r/Anki/comments/1lg8j1/deck_settings', 'Study methods', '2026-07-19T21:58:00-07:00', false, null],
  ['save-deep-work', 'Deep work for people with four courses and a job', 'article', 'calnewport.com', 'https://calnewport.com/deep-work-four-courses/', 'Study methods', '2026-05-03T07:26:00-07:00', false, null],

  // Fitness — 6.
  ['save-split', 'A 3-day split that survives a 13-unit semester', 'article', 'stronglifts.com', 'https://stronglifts.com/3-day-split-semester/', 'Fitness', '2026-09-10T07:40:00-07:00', false, null],
  ['save-finger-strength', 'Finger strength for people who only climb twice a week', 'reddit', 'r/climbing', 'https://www.reddit.com/r/climbing/comments/1mv4c8/finger_strength', 'Fitness', '2026-09-03T18:22:00-07:00', false, null],
  ['save-warmups', 'Warm-ups that actually change what the first hour feels like', 'youtube', 'YouTube', 'https://www.youtube.com/watch?v=warmups-first-hour', 'Fitness', '2026-08-19T06:51:00-07:00', false, 720],
  ['save-berkeley-hills', 'Berkeley hills without wrecking your knees', 'reddit', 'r/running', 'https://www.reddit.com/r/running/comments/1mc9k3/berkeley_hills', 'Fitness', '2026-08-01T06:18:00-07:00', false, null],
  ['save-protein-timing', 'Protein timing matters less than you were told', 'article', 'examine.com', 'https://examine.com/guides/protein-timing/', 'Fitness', '2026-06-27T12:40:00-07:00', false, null],
  ['save-progression', 'The progression I wish I had started with', 'reddit', 'r/bodyweightfitness', 'https://www.reddit.com/r/bodyweightfitness/comments/1kt1n6/progression', 'Fitness', '2026-05-15T17:09:00-07:00', false, null],

  // Berkeley — 4.
  ['save-moffitt-quiet', 'Where the fourth floor of Moffitt is actually quiet', 'reddit', 'r/berkeley', 'https://www.reddit.com/r/berkeley/comments/1mwq2d/moffitt_fourth', 'Berkeley', '2026-09-08T13:15:00-07:00', false, null],
  ['save-enrollment', 'Fall 2026 enrollment thread — waitlist reality check', 'reddit', 'r/berkeley', 'https://www.reddit.com/r/berkeley/comments/1mm5t7/fall_2026_enrollment', 'Berkeley', '2026-08-28T09:02:00-07:00', false, null],
  ['save-transfer-guide', 'The transfer student guide nobody sends you', 'article', 'berkeley.edu', 'https://transfers.berkeley.edu/getting-started/guide', 'Berkeley', '2026-08-10T11:37:00-07:00', false, null],
  ['save-southside-food', 'Cheapest way to eat well on Southside', 'reddit', 'r/berkeley', 'https://www.reddit.com/r/berkeley/comments/1l2h9k/southside_food', 'Berkeley', '2026-06-05T19:44:00-07:00', false, null],
]

// The inbox — 12. Unfiled AND unread: the nightly job has not placed them and the owner has
// not opened them. This is the one population whose size the band states out loud.
const INBOX_SEEDS: SaveSeed[] = [
  ['save-reader', 'Where do people actually buy the COGSCI 1 reader?', 'reddit', 'r/berkeley', 'https://www.reddit.com/r/berkeley/comments/1n5x0f/cogsci1_reader', null, `${SEED_TODAY}T07:55:00-07:00`, true, null],
  ['save-citation-graph', 'Thread: the citation graph of a paper nobody cites', 'x', 'x.com', 'https://x.com/demo/status/1907442210', null, '2026-09-14T20:31:00-07:00', true, null],
  ['save-ports', 'The laptop that finally has enough ports', 'article', 'theverge.com', 'https://www.theverge.com/reviews/laptop-ports-2026', null, '2026-09-13T08:47:00-07:00', true, null],
  ['save-argparse', 'argparse in ten minutes', 'reddit', 'r/learnpython', 'https://www.reddit.com/r/learnpython/comments/1n1b3z/argparse_ten_minutes', null, '2026-09-12T18:30:00-07:00', true, null],
  ['save-ai-phrase', 'A forty-minute history of the phrase “artificial intelligence”', 'youtube', 'YouTube', 'https://www.youtube.com/watch?v=ai-phrase-history', null, '2026-09-11T22:12:00-07:00', true, 2400],
  ['save-one-pan', 'One pan, four dinners, no leftovers you will avoid', 'reddit', 'r/cooking', 'https://www.reddit.com/r/cooking/comments/1mzh5r/one_pan_four', null, '2026-09-10T17:06:00-07:00', true, null],
  ['save-sleep-study', 'The sleep study everyone misread', 'article', 'nytimes.com', 'https://www.nytimes.com/2026/09/09/health/sleep-study-misread.html', null, '2026-09-09T21:50:00-07:00', true, null],
  ['save-renters-rights', 'The renters’ rights thing I did not know until too late', 'reddit', 'r/apartmentliving', 'https://www.reddit.com/r/apartmentliving/comments/1mwf1t/renters_rights', null, '2026-09-08T19:24:00-07:00', true, null],
  ['save-transformer-paper', 'Thread: how the transformer paper almost did not get published', 'x', 'x.com', 'https://x.com/demo/status/1901338702', null, '2026-09-06T12:58:00-07:00', true, null],
  ['save-index-card', 'Twelve thousand words on the invention of the index card', 'article', 'longreads.com', 'https://longreads.com/2026/09/04/the-index-card/', null, '2026-09-04T23:19:00-07:00', true, null],
  ['save-midterm-systems', 'Nobody’s system survives midterms and that is fine', 'reddit', 'r/productivity', 'https://www.reddit.com/r/productivity/comments/1mtj4c/systems_midterms', null, '2026-09-02T15:41:00-07:00', true, null],
  ['save-fourier', 'The best explanation of Fourier transforms I have found', 'youtube', 'YouTube', 'https://www.youtube.com/watch?v=fourier-best', null, '2026-08-31T20:08:00-07:00', true, 1140],
]

// ── The archive backfill ────────────────────────────────────────────────────
//
// 197 saves the owner read and the nightly job never grouped. They never reach the library
// grid — the grid shows one cluster at a time, and these are in none — but they are what makes
// `247 items` a measured number instead of a decorated one. Composed rather than hand-written,
// deterministically, from two pools: the same reasoning as the journal backfill.

const ARCHIVE_FRAMES: string[] = [
  '$ — what actually worked',
  'Anyone else run into $?',
  '$, explained properly for once',
  'The $ thread I keep coming back to',
  'Notes on $ after three months',
  '$: the version nobody links',
  'Why $ is harder than it looks',
  'A short guide to $',
  '$ — before and after',
  'Everything I got wrong about $',
  '$ without the hype',
  'Two weeks of $, honestly',
]

const ARCHIVE_SUBJECTS: string[] = [
  'batch rendering overnight',
  'seed locking',
  'upscaler stacks',
  'note-taking in Obsidian',
  'reading dense papers',
  'cheap GPU rentals',
  'sleep debt',
  'meal prep on Sundays',
  'the Berkeley bus system',
  'Python type hints',
  'writing shorter emails',
  'flashcard decks that stick',
  'climbing plateaus',
  'standing desks',
  'colour management',
  'audio cleanup',
  'shell aliases',
  'backing up a desktop',
  'reading on a phone',
  'the transfer credit process',
]

const ARCHIVE_SOURCES: Array<[SaveSource, string, string]> = [
  ['reddit', 'r/StableDiffusion', 'https://www.reddit.com/r/StableDiffusion/comments/'],
  ['reddit', 'r/comfyui', 'https://www.reddit.com/r/comfyui/comments/'],
  ['reddit', 'r/berkeley', 'https://www.reddit.com/r/berkeley/comments/'],
  ['reddit', 'r/GetStudying', 'https://www.reddit.com/r/GetStudying/comments/'],
  ['reddit', 'r/learnpython', 'https://www.reddit.com/r/learnpython/comments/'],
  ['article', 'example.org', 'https://example.org/posts/'],
  ['youtube', 'YouTube', 'https://www.youtube.com/watch?v='],
  ['x', 'x.com', 'https://x.com/demo/status/'],
]

const ARCHIVE_COUNT = 197
const ARCHIVE_FROM: ISODate = '2026-03-02'
const ARCHIVE_TO: ISODate = '2026-08-20'

function buildArchive(): InterestSave[] {
  const span = daysBetween(ARCHIVE_FROM, ARCHIVE_TO)
  const rows: InterestSave[] = []
  for (let i = 0; i < ARCHIVE_COUNT; i++) {
    const frame = ARCHIVE_FRAMES[i % ARCHIVE_FRAMES.length]!
    const subject = ARCHIVE_SUBJECTS[Math.floor(i / ARCHIVE_FRAMES.length) % ARCHIVE_SUBJECTS.length]!
    const [source, sourceDetail, urlBase] = ARCHIVE_SOURCES[i % ARCHIVE_SOURCES.length]!
    const date = addDays(ARCHIVE_FROM, Math.floor((i * span) / ARCHIVE_COUNT))
    const hour = 8 + Math.floor(seededUnit(`save-hour:${i}`) * 14)
    const minute = Math.floor(seededUnit(`save-min:${i}`) * 60)
    rows.push({
      id: `save-archive-${i}`,
      title: frame.replace('$', subject),
      url: `${urlBase}archive-${i}`,
      source,
      sourceDetail,
      topic: null,
      savedAt: `${date}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00-07:00`,
      unread: false,
      note: null,
      noteEditedAt: null,
      excerpt: null,
      summary: null,
      durationSec: source === 'youtube' ? 300 + Math.floor(seededUnit(`save-len:${i}`) * 2400) : null,
    })
  }
  return rows
}

/** Notes, excerpts and summaries only exist where they exist. Absent is the common case. */
const SAVE_NOTES: Record<string, [note: string, editedAt: ISOTimestamp]> = {
  'save-wan-motion': ['try these settings on the aquarium prompt', '2026-09-13T18:40:00-07:00'],
  'save-extended-mind': ['For the 175 essay.', '2026-09-13T22:20:00-07:00'],
  'save-interleaving': ['worth trying on the algebra track', '2026-09-12T10:02:00-07:00'],
}

const SAVE_EXCERPTS: Record<string, string> = {
  // THESE TWO EXCERPTS CARRY THE HANDOFF'S OWN WORDS. Option 10b of
  // docs/design/handoff/"LifeOS Search.dc.html" quotes both of these saves back as search
  // snippets — "ran the same 40 prompts through both video models at 24fps, identical seeds" and
  // "human raters score motion quality more consistently than any automatic metric currently used
  // to rank a video model" — and docs/design/FIDELITY.md is explicit that the demo dataset gets
  // updated to carry the handoff's exact strings.
  //
  // WHY IT MATTERS HERE AND NOT ONLY ON THE INTERESTS PANE: a literal search cannot find words
  // the corpus does not contain. Both phrases were paraphrased away ("Wan 2.1 and 2.2" for "both
  // video models"), and the query 10b is designed around — "video model motion quality" — missed
  // the two saves the whole option is built on. The handoff's phrasing is ADDED to each excerpt
  // rather than swapped in for it, so the words the Interests detail pane already renders are
  // untouched and no other screen's proof shot moves.
  'save-wan-motion':
    'Ran the same 40 prompts through Wan 2.1 and 2.2 — both video models — at 24fps, 81 frames, identical seeds. 2.2 holds limb coherence noticeably better past the two-second mark; the gain drops off if CFG goes above 6. Full settings table and the seed list are in the post…',
  'save-prompt-adherence':
    'Scored 300 generations from six open models against the prompt they were given, blind, two raters. The ranking is not the one the demo reels imply, and the gap narrows a lot once you allow a second pass…',
  'save-camera-control':
    'Tried every camera-control LoRA I could find on the same dolly-in shot. Two of them do something real; the rest are conditioning tricks that fall apart the moment the subject moves…',
  'save-temporal-coherence':
    'We propose a metric that separates per-frame fidelity from cross-frame identity drift, and show that current benchmarks conflate the two. We argue that human raters score motion quality more consistently than any automatic metric currently used to rank a video model…',
  'save-hands':
    'The failure is temporal, not spatial — every individual frame has a plausible hand in it, and no two consecutive frames agree about which hand it is.',
}

const SAVE_SUMMARIES: Record<string, SaveSummary> = {
  'save-wan-motion': aiSummary(
    'Wan 2.2 improves motion coherence on long shots, but only under CFG 6 — above that the older model matches it. The author publishes every setting, so the comparison is reproducible.',
    'https://www.reddit.com/r/StableDiffusion/comments/1n2f8x/wan22_motion',
    '2026-09-13T14:24:00-07:00',
  ),
  'save-prompt-adherence': aiSummary(
    'Blind-rated 300 generations across six open models and found the published rankings do not survive a second rater. The margins are smaller than any demo reel suggests.',
    'https://www.reddit.com/r/LocalLLaMA/comments/1n4k2p/prompt_adherence_video',
    '2026-09-14T11:14:00-07:00',
  ),
  'save-hands': aiSummary(
    'Argues the hand failure is temporal rather than spatial: per-frame quality is fine, and it is cross-frame identity that drifts. The proposed fix is a consistency loss, not a bigger model.',
    'https://www.youtube.com/watch?v=v1d3o-hands',
    '2026-09-02T21:09:00-07:00',
  ),
}

/**
 * NEAREST NEIGHBOURS — "by meaning, not tags". Authored for the hero cluster, because the
 * mockup names exactly which three sit under the Wan 2.2 save and in which order.
 *
 * Everywhere else the fallback is the save's own cluster, newest first. That is not a
 * substitute for meaning; it IS meaning, because a cluster is what the nightly job grouped by
 * meaning in the first place. A save in no cluster gets an empty list and the pane says so,
 * rather than reaching for whatever happened to be saved nearby in time.
 */
const RELATED: Record<string, string[]> = {
  'save-wan-motion': ['save-prompt-adherence', 'save-temporal-coherence', 'save-camera-control'],
}

function buildSave(seed: SaveSeed): InterestSave {
  const [id, title, source, sourceDetail, url, topic, savedAt, unread, durationSec] = seed
  const note = SAVE_NOTES[id]
  return {
    id,
    title,
    url,
    source,
    sourceDetail,
    topic,
    savedAt,
    unread,
    note: note ? note[0] : null,
    noteEditedAt: note ? note[1] : null,
    excerpt: SAVE_EXCERPTS[id] ?? null,
    summary: SAVE_SUMMARIES[id] ?? null,
    durationSec,
  }
}

const SAVES: InterestSave[] = [
  ...AI_VIDEO_SEEDS.map(buildSave),
  ...OTHER_CLUSTER_SEEDS.map(buildSave),
  ...INBOX_SEEDS.map(buildSave),
  ...buildArchive(),
]

const SAVES_BY_ID = new Map(SAVES.map((s) => [s.id, s]))

/**
 * The nightly clusters, derived from the corpus and ordered MOST RECENTLY ADDED TO FIRST.
 *
 * Not by count: the mockup's row runs 14 · 9 · 5 · 6 · 4, which no count ordering produces, and
 * a grouping the machine redoes every night is naturally ordered by what it last touched. The
 * dates below are the only thing that decides that order, so the demo dataset — not a hand-kept
 * array order — is what puts the AI-video cluster first.
 */
function buildTopics(saves: InterestSave[]): InterestTopic[] {
  const byLabel = new Map<string, InterestSave[]>()
  for (const s of saves) {
    if (!s.topic) continue
    const list = byLabel.get(s.topic)
    if (list) list.push(s)
    else byLabel.set(s.topic, [s])
  }
  return [...byLabel.entries()]
    .map(([label, rows]) => {
      const dates = rows.map((r) => r.savedAt).sort()
      return {
        id: `topic-${label.toLowerCase().replace(/\s+/g, '-')}`,
        label,
        count: rows.length,
        oldestSavedAt: dates[0]!,
        newestSavedAt: dates[dates.length - 1]!,
      }
    })
    .sort((a, b) => (a.newestSavedAt < b.newestSavedAt ? 1 : -1))
}

/** Nearest neighbours: the authored list where there is one, the cluster otherwise. */
function relatedSaves(save: InterestSave): InterestSave[] {
  const authored = RELATED[save.id]
  if (authored) return authored.map((id) => SAVES_BY_ID.get(id)).filter((s): s is InterestSave => Boolean(s))
  if (!save.topic) return []
  return SAVES.filter((s) => s.topic === save.topic && s.id !== save.id)
    .sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1))
    .slice(0, 3)
}

/** The band's counts. Every one measured, none of them typed in. */
const REDDIT_BACKLOG_ITEMS = 312

// ── Sync status ─────────────────────────────────────────────────────────────
//
// One source is deliberately broken. It is the only way the error state ever
// gets designed (blueprint/templates/data-contract.ts).

const SYNC_STATUS: SyncStatusRow[] = [
  {
    source: 'canvas', lastSyncAt: `${SEED_TODAY}T08:29:00-07:00`, status: 'ok',
    itemsPulled: 47, lastError: null,
  },
  {
    // The Google Calendar mirror. Its presence here — and only its presence — is what turns the
    // Calendar sources list's Google row from the brief's honest `off` statement into a real
    // switch, in demo exactly as in remote.
    source: 'gcal', lastSyncAt: `${SEED_TODAY}T08:31:00-07:00`, status: 'ok',
    itemsPulled: 5, lastError: null,
  },
  {
    source: 'pc_worker', lastSyncAt: '2026-09-15T22:14:00-07:00', status: 'error',
    // The count is the number of lectures the pipeline has not finished — one queued (Sep 15)
    // and one abandoned mid-job (Sep 3). demo.test.ts derives it from LECTURE_SEEDS, so this
    // sentence cannot drift away from the data it describes.
    itemsPulled: 3, lastError: 'Desktop worker offline since 22:14 — 2 lectures waiting to transcribe.',
  },
  {
    source: 'reddit', lastSyncAt: null, status: 'never', itemsPulled: 0, lastError: null,
  },
]

// ── Connections · the trust surface (docs/design/handoff, options 12a/12b) ──
//
// TWO MODELLED STATES, not one. The Connections screen's whole subject is
// degradation, and its two designed states are whole-screen states: a broken
// connector moves the banner, the nav dot, the section order and the header
// counts together. So the dataset carries both and `?state=` picks one — that
// is what makes 12a reviewable at all, since a demo build cannot actually break
// a connector on request.
//
// EVERY TIMESTAMP IS ANCHORED TO SEED_TODAY (2026-09-16 08:41), not to the
// mockup's own Aug 24. The offsets are the mockup's exactly — "3 days ago",
// "41 min ago", "unused for 83 days" — so every RELATIVE string renders
// verbatim; the absolute dates differ from the mockup's because this repo has
// one demo clock and it is not this screen's to move. Recorded in the port's
// deviation list.

const ts = (date: ISODate, hhmm: string) => `${date}T${hhmm}:00-07:00`
const daysAgo = (n: number) => addDays(SEED_TODAY, -n)

/** 12a: the token expired three days ago and School has been frozen since. */
const CANVAS_BROKEN: Connector = {
  id: 'canvas',
  name: 'Canvas · bCourses',
  shortName: 'Canvas',
  status: 'error',
  scope: 'bcourses.berkeley.edu · 4 courses · assignments, grades, announcements, files',
  note: {
    tone: 'error',
    text: 'Canvas token expired — reconnect. Berkeley tokens last 90 days.',
  },
  outage: { stale: 'assignment due dates and grades on the School screen' },
  lastSyncAt: ts(daysAgo(3), '18:12'),
  newItemsLastSync: null,
  itemsPulledAllTime: 412,
  retryAt: null,
  plannedFor: null,
}

/** 12b: the same connector working — and still naming what it will never know. */
const CANVAS_OK: Connector = {
  ...CANVAS_BROKEN,
  status: 'ok',
  note: {
    tone: 'neutral',
    // THE ABSENT-BY-DESIGN CAVEAT. The Canvas API has no waitlist position, so
    // LifeOS has no field for one and says so here rather than rendering an
    // empty slot the owner would keep checking (README §Overview).
    text: 'Waitlist position is not in the Canvas API, so LifeOS never shows one. Check CalCentral for that.',
  },
  outage: null,
  lastSyncAt: ts(SEED_TODAY, '08:35'),
  newItemsLastSync: 2,
  itemsPulledAllTime: 431,
}

const REDDIT_OK: Connector = {
  id: 'reddit',
  name: 'Reddit',
  shortName: 'Reddit',
  status: 'ok',
  scope: 'u/kaz_ · saved posts and comments · 6 subreddits followed',
  note: {
    tone: 'neutral',
    text: 'Pulls new saves every 30 minutes. Deleted posts keep their text — the link goes dead, the note doesn’t.',
  },
  outage: null,
  lastSyncAt: ts(SEED_TODAY, '08:23'),
  newItemsLastSync: 4,
  itemsPulledAllTime: 247,
  retryAt: null,
  plannedFor: null,
}

/**
 * RATE LIMITED IS NOT AN ERROR. The retry is already scheduled, so the card
 * offers no button and the header count does not call it "needs attention"
 * (README §Sync: "a rate-limit shows the next retry time and offers no button,
 * because there is nothing for the user to do"). The sentence carries the time
 * the connector itself scheduled; `retryAt` is the same fact, machine-readable.
 */
const REDDIT_RATE_LIMITED: Connector = {
  ...REDDIT_OK,
  status: 'rate_limited',
  note: { tone: 'warning', text: 'Reddit asked us to slow down — retrying at 3:40pm. Nothing to do.' },
  lastSyncAt: ts(SEED_TODAY, '06:41'),
  newItemsLastSync: null,
  retryAt: ts(SEED_TODAY, '15:40'),
}

/**
 * The Google Calendar connector, working — and still naming what it will never do.
 *
 * ITS CAVEAT IS THE READ-ONLY DIRECTION, not a missing field. Canvas's caveat is about a fact
 * the API does not carry (waitlist position); this one is about what LifeOS deliberately never
 * does: it mirrors the secret iCal feed one way and writes nothing back to Google, ever. A card
 * that said nothing would let the owner assume moving a block here moved it there.
 */
const GCAL_OK: Connector = {
  id: 'gcal',
  name: 'Google Calendar',
  shortName: 'Google',
  scope: 'Secret iCal link · 5 events mirrored · read-only, 180 days ahead',
  status: 'ok',
  note: {
    tone: 'neutral',
    text:
      'This is a read-only mirror: changes you make in Google appear here at the next sync, and ' +
      'nothing LifeOS does here is ever written back to Google.',
  },
  outage: null,
  lastSyncAt: ts(SEED_TODAY, '08:31'),
  newItemsLastSync: 5,
  itemsPulledAllTime: 5,
  retryAt: null,
  plannedFor: null,
}

const PLANNED: Connector[] = [
  {
    id: 'oura',
    name: 'Oura',
    shortName: 'Oura',
    status: 'planned',
    scope: 'Sleep and readiness beside journal entries · never connected',
    note: null,
    outage: null,
    lastSyncAt: null,
    newItemsLastSync: null,
    itemsPulledAllTime: null,
    retryAt: null,
    plannedFor: 'V2',
  },
]

const WORKER_STORAGE: StorageNote = {
  machine: 'kaz-desktop',
  archivedBytes: 18_400_000_000,
  syncedBytes: 240_000_000,
}

const WORKER_ONLINE: DesktopWorkerStatus = {
  state: 'online',
  machine: 'kaz-desktop',
  model: 'Whisper large-v3',
  activity: 'idle',
  lastSeenAt: ts(SEED_TODAY, '08:40'),
  lastJob: {
    title: 'NEU C61 lecture',
    audioSec: 41 * 60,
    transcribedInSec: 3 * 60 + 12,
    finishedAt: ts(SEED_TODAY, '08:29'),
  },
  queue: [],
  jobsThisWeek: 9,
  audioThisWeekSec: 4 * 3600 + 22 * 60,
  storage: WORKER_STORAGE,
}

/**
 * OFFLINE EXPANDS INTO A NAMED QUEUE. "3 jobs waiting" with no names is
 * indistinguishable from "something is broken"; three titled, dated rows in the
 * order the PC will run them is a wait with an end to it.
 *
 * `activity` is null, not 'idle': the app cannot see what a sleeping machine is
 * doing, and a field it cannot fill does not get filled (README §Overview).
 */
const WORKER_OFFLINE: DesktopWorkerStatus = {
  state: 'offline',
  machine: 'kaz-desktop',
  model: 'Whisper large-v3',
  activity: null,
  lastSeenAt: ts(daysAgo(1), '23:48'),
  lastJob: null,
  queue: [
    {
      id: 'job-1',
      title: 'NEU C61 · Lecture 7',
      kind: 'lecture',
      recordedAt: ts(SEED_TODAY, '07:02'),
      durationSec: 48 * 60,
      work: 'transcribe',
    },
    {
      id: 'job-2',
      title: 'COGSCI 131 · Lecture 6',
      kind: 'lecture',
      recordedAt: ts(daysAgo(1), '14:10'),
      durationSec: 51 * 60,
      work: 'transcribe',
    },
    {
      id: 'job-3',
      title: 'Voice memo · walking home',
      kind: 'voice_memo',
      recordedAt: ts(daysAgo(1), '21:37'),
      durationSec: 7 * 60,
      work: 'transcribe',
    },
  ],
  jobsThisWeek: 9,
  audioThisWeekSec: 4 * 3600 + 22 * 60,
  storage: WORKER_STORAGE,
}

const TOKEN_IPHONE: CaptureToken = {
  id: 'token-iphone',
  label: 'iPhone · Shortcut',
  device: 'phone',
  lastUsedAt: ts(SEED_TODAY, '08:00'),
  capturesThisMonth: 88,
  revokedAt: null,
  createdAt: ts(daysAgo(120), '12:00'),
}

const TOKEN_CHROME: CaptureToken = {
  id: 'token-chrome',
  label: 'Chrome extension · MacBook Air',
  device: 'extension',
  lastUsedAt: ts(SEED_TODAY, '06:41'),
  capturesThisMonth: 134,
  revokedAt: null,
  createdAt: ts(daysAgo(95), '12:00'),
}

/**
 * THE STALE TOKEN, in its two moments: unused for 83 days in 12a — where its
 * Revoke turns dark, because the destructive action gets MORE prominent for a
 * token nobody is using — and revoked in 12b, where it stays in the model so
 * the panel can report it afterwards instead of quietly forgetting it.
 */
const TOKEN_IPAD_STALE: CaptureToken = {
  id: 'token-ipad',
  label: 'iPad · Safari',
  device: 'tablet',
  lastUsedAt: ts(daysAgo(83), '10:14'),
  capturesThisMonth: 0,
  revokedAt: null,
  createdAt: ts(daysAgo(140), '12:00'),
}

const TOKEN_IPAD_REVOKED: CaptureToken = {
  ...TOKEN_IPAD_STALE,
  revokedAt: ts(daysAgo(2), '16:05'),
}

const CONNECTIONS: Record<
  ConnectionsScenario,
  {
    connectors: Connector[]
    worker: DesktopWorkerStatus
    tokens: CaptureToken[]
    data: DataStatus
  }
> = {
  connector_broken: {
    connectors: [CANVAS_BROKEN, GCAL_OK, REDDIT_OK, ...PLANNED],
    worker: WORKER_ONLINE,
    tokens: [TOKEN_IPHONE, TOKEN_CHROME, TOKEN_IPAD_STALE],
    data: {
      mode: 'real',
      realJournalEntries: 128,
      index: {
        itemsIndexed: 2340,
        pending: 63,
        blockedBy: 'Canvas',
        currentAsOf: ts(SEED_TODAY, '08:32'),
      },
    },
  },
  worker_offline: {
    connectors: [CANVAS_OK, GCAL_OK, REDDIT_RATE_LIMITED, ...PLANNED],
    worker: WORKER_OFFLINE,
    tokens: [TOKEN_IPHONE, TOKEN_CHROME, TOKEN_IPAD_REVOKED],
    data: {
      mode: 'demo',
      realJournalEntries: 128,
      index: {
        itemsIndexed: 2340,
        pending: 284,
        blockedBy: 'the 3 queued transcripts',
        currentAsOf: null,
      },
    },
  },
}

/**
 * 12b IS THE DEFAULT, deliberately. Its `data.mode` is 'demo', which is the
 * truth about any build running on this file — so the screen a demo-mode user
 * lands on announces demo mode in the banner and the nav footnote, exactly as
 * it should. 12a is reachable only by asking for it (`?state=12a`), and every
 * number on it is sample data like everything else here.
 */
const DEFAULT_SCENARIO: ConnectionsScenario = 'worker_offline'

// ── Mutable state (writes work in demo mode too) ────────────────────────────

const journal = buildJournal()
let tasks: Task[] = TASK_SEEDS.map((t) => ({ ...t }))
/**
 * THE ONE PLACE A TASK IS ADDED in demo mode — `createTask` and `acceptChatProposal` both come
 * through here, the fixture's version of the Worker routing both through `createTaskRow`.
 *
 * `due` (a day) and `reminderAt` (an instant) are independent and both pass through untouched:
 * they are what the Reminders card filters on, and a create path that dropped one would make a
 * reminder that exists on the board and never appears on Today.
 */
function addTask(input: TaskInput, proposalId: string | null): Task {
  const t: Task = {
    // MONOTONIC, NOT A COUNT OF THE ARRAY. `tasks.length + 1` reissues an id the moment anything
    // has been deleted — delete one row, add one row, and React renders two children with the
    // same key and starts omitting one of them. A counter that only ever goes up cannot do that,
    // and the `new` segment keeps it clear of the seeded `task-demo-N` ids.
    id: `task-demo-new-${++createdTasks}`,
    title: input.title,
    done: false,
    due: input.due ?? null,
    list: input.list ?? 'Inbox',
    priority: 'none',
    rrule: input.rrule ?? null,
    reminderAt: input.reminderAt ?? null,
    createdAt: `${SEED_TODAY}T${SEED_CLOCK_HHMM}:00-07:00`,
    completedAt: null,
    // Nothing infers a length from a typed-in title. No estimate means no estimate.
    estimateMin: input.estimateMin ?? null,
    proposalId,
  }
  tasks = [t, ...tasks]
  return t
}

/** How many tasks this session has created. Only ever goes up — see `addTask`. */
let createdTasks = 0
/** itemId → when it was finished and how long it took. Checking a box on `/learning` mutates it. */
const doneItems = seedDoneItems()

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T

// ── Learning derivations (both Learning screens, and the Today card) ────────
//
// Progress, pace, the current unit, today's item, the week strip and the session log all come out
// of ONE source: the flattened plan plus `doneItems`. README §State Management puts pace wording
// under "Derived, never stored", and two screens computing the same number two ways is how a
// dataset starts contradicting itself in public.

/** How many items the plan expected to be finished by `date`, counting its first day as one. */
function plannedBy(plan: PlanSeed, date: ISODate): number {
  const elapsed = daysBetween(plan.start, date) + 1
  return Math.max(0, Math.min(PLAN_ITEMS[plan.id]!.length, elapsed))
}

function itemsDoneOn(trackId: string): number {
  const items = PLAN_ITEMS[trackId]!
  return items.filter((i) => doneItems.has(i.id)).length
}

function trackOf(plan: PlanSeed): LearningTrack {
  const items = PLAN_ITEMS[plan.id]!
  const next = items.find((i) => !doneItems.has(i.id))
  return {
    id: plan.id,
    // A demo plan was never imported, so it has no slug of its own. Its id is the ref every read
    // in this adapter answers to, which is the honest answer to "what would I send back".
    slug: plan.id,
    name: plan.name,
    subtitle: plan.subtitle,
    // The unit the next unfinished item lives in. Once the plan is finished there is no current
    // unit, and the last one is named rather than an empty string pretending to be one.
    currentUnit: next?.unitName ?? items[items.length - 1]!.unitName,
    itemsTotal: items.length,
    itemsDone: itemsDoneOn(plan.id),
    planStartDate: plan.start,
    planEndDate: addDays(plan.start, items.length - 1),
    // The AUTHOR's revision label, not the format version: this is the first revision of the plan,
    // written against v2 of the import format. Two different numbers with two different meanings.
    planVersion: 'v1',
    importedAt: plan.importedAt,
    planSource: 'planning agent',
    resources: PLAN_RESOURCES[plan.id] ?? [],
  }
}

function toLearningItem(plan: PlanSeed, item: PlanItem): LearningItem {
  const completion = doneItems.get(item.id)
  return {
    id: item.id,
    trackId: plan.id,
    trackName: plan.name,
    unit: item.unitName,
    title: item.title,
    date: item.plannedDate,
    done: completion !== undefined,
    minutes: completion?.minutes ?? null,
    resource: item.resource,
    minutesEstimate: item.minutesEstimate,
    instructions: item.instructions,
  }
}

/** The item a plan dates to `date`, or null when that day falls outside the plan entirely. */
function itemOnDate(plan: PlanSeed, date: ISODate): LearningItem | null {
  const index = daysBetween(plan.start, date)
  const item = PLAN_ITEMS[plan.id]![index]
  return item ? toLearningItem(plan, item) : null
}

function paceOf(plan: PlanSeed, date: ISODate): LearningPace {
  const days = itemsDoneOn(plan.id) - plannedBy(plan, date)
  const direction: LearningPace['direction'] = days > 0 ? 'ahead' : days < 0 ? 'behind' : 'on_plan'
  const n = Math.abs(days)
  const phrase =
    direction === 'on_plan' ? 'on the plan line' : `${n} ${n === 1 ? 'day' : 'days'} ${direction}`
  return { days, direction, phrase }
}

function detailItem(item: PlanItem): LearningUnitItem {
  const completion = doneItems.get(item.id)
  return {
    id: item.id,
    title: item.title,
    index: item.index,
    plannedDate: item.plannedDate,
    done: completion !== undefined,
    completedDate: completion?.date ?? null,
    minutes: completion?.minutes ?? null,
    resource: item.resource,
    minutesEstimate: item.minutesEstimate,
    instructions: item.instructions,
  }
}

function unitsOf(plan: PlanSeed): LearningUnit[] {
  const items = PLAN_ITEMS[plan.id]!
  const nextIndex = items.find((i) => !doneItems.has(i.id))?.index ?? items.length
  return plan.units.map((unit, u) => {
    const own = items.filter((i) => i.unitNumber === u + 1).map((i) => detailItem(i))
    const done = own.filter((i) => i.done).length
    const isCurrent = own.some((i) => i.index === nextIndex)
    const state: LearningUnitState = isCurrent ? 'current' : done === own.length ? 'done' : 'future'
    // "done Aug 24" is the day the LAST item of the unit was actually finished, which is not the
    // day the plan had it. A unit still open has no completion date at all.
    const completedDate =
      done === own.length
        ? own.reduce<ISODate | null>((latest, i) => {
            if (!i.completedDate) return latest
            return latest === null || i.completedDate > latest ? i.completedDate : latest
          }, null)
        : null
    return {
      id: `${plan.id}-unit-${u + 1}`,
      name: unit.name,
      number: u + 1,
      state,
      itemsTotal: own.length,
      itemsDone: done,
      completedDate,
      startsOn: own[0]!.plannedDate,
      week: isCurrent ? Math.floor(daysBetween(plan.start, SEED_TODAY) / 7) + 1 : null,
      items: own,
    }
  })
}

function sessionsOf(plan: PlanSeed): LearningSession[] {
  const items = PLAN_ITEMS[plan.id]!
  const byDate = new Map<ISODate, { items: string[]; minutes: number; note: string | null }>()
  for (const item of items) {
    const completion = doneItems.get(item.id)
    if (!completion) continue
    const row = byDate.get(completion.date) ?? { items: [], minutes: 0, note: null }
    row.items.push(item.title)
    row.minutes += completion.minutes
    // The first note written that day wins; a day with none keeps null rather than ''. A note
    // typed into the workspace is on the completion itself and outranks the seeded one.
    row.note = row.note ?? completion.note ?? SESSION_NOTES[`${plan.id}:${item.index}`] ?? null
    byDate.set(completion.date, row)
  }
  return [...byDate.entries()]
    .map(([date, v]) => ({ date, ...v }))
    .sort((a, b) => (a.date < b.date ? 1 : -1))
}

/** Cumulative done against cumulative planned, one point per day from the plan's start to today. */
function paceSeriesOf(plan: PlanSeed): LearningPacePoint[] {
  const doneByDate = new Map<ISODate, number>()
  for (const item of PLAN_ITEMS[plan.id]!) {
    const completion = doneItems.get(item.id)
    if (!completion) continue
    doneByDate.set(completion.date, (doneByDate.get(completion.date) ?? 0) + 1)
  }
  const out: LearningPacePoint[] = []
  let running = 0
  for (let d = plan.start; d <= SEED_TODAY; d = addDays(d, 1)) {
    running += doneByDate.get(d) ?? 0
    out.push({ date: d, done: running, planned: plannedBy(plan, d) })
  }
  return out
}

function summaryOf(plan: PlanSeed): LearningTrackSummary {
  const items = PLAN_ITEMS[plan.id]!
  const track = trackOf(plan)
  const nextPlanItem = items.find((i) => !doneItems.has(i.id))
  const nextItem = nextPlanItem ? detailItem(nextPlanItem) : null
  const following = nextPlanItem ? items[nextPlanItem.index + 1] : undefined

  const completions = items.map((i) => doneItems.get(i.id)).filter((c) => c !== undefined)
  const loggedMinutes = completions.reduce((n, c) => n + c.minutes, 0)
  // The estimate is HIS average on THIS track, rounded to five minutes — a personal baseline, not
  // a population norm, and absent entirely until there is something to average.
  const estimateMinutes = completions.length
    ? Math.max(5, Math.round(loggedMinutes / completions.length / 5) * 5)
    : null

  const weekStart = startOfWeek(SEED_TODAY)
  const week = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i)
    return { date, items: completions.filter((c) => c.date === date).length }
  })
  const weekPlanned = items.filter(
    (i) => i.plannedDate >= weekStart && i.plannedDate <= addDays(weekStart, 6),
  ).length

  const dates = completions.map((c) => c.date).sort()
  const worked = new Set(dates)
  let streakDays = 0
  while (worked.has(addDays(SEED_TODAY, -streakDays))) streakDays += 1

  return {
    track,
    percent: Math.round((track.itemsDone / track.itemsTotal) * 100),
    pace: paceOf(plan, SEED_TODAY),
    currentUnit: track.currentUnit,
    nextItem,
    followingItem: following ? detailItem(following) : null,
    estimateMinutes,
    week,
    weekDone: week.reduce((n, d) => n + d.items, 0),
    weekPlanned,
    lastActive: dates.length ? dates[dates.length - 1]! : null,
    streakDays,
  }
}

/**
 * THE RESOURCES SECTION for one track — the standing library plus every link its items name,
 * deduplicated by url. Deliberately the same derivation the Worker runs
 * (apps/worker/src/lib/learning.ts → `libraryOf`): demo mode and remote mode disagreeing about
 * what is on the shelf would make the screen unreviewable.
 */
function libraryOf(plan: PlanSeed): LearningTrackLibrary {
  const byUrl = new Map<string, LearningLibraryEntry>()
  for (const resource of PLAN_RESOURCES[plan.id] ?? []) {
    if (byUrl.has(resource.url)) continue
    byUrl.set(resource.url, { ...resource, origin: 'track', itemCount: 0, itemsDone: 0, nextItemTitle: null })
  }
  for (const item of PLAN_ITEMS[plan.id]!) {
    if (!item.resource) continue
    const entry =
      byUrl.get(item.resource.url) ??
      ({ ...item.resource, origin: 'item', itemCount: 0, itemsDone: 0, nextItemTitle: null } as LearningLibraryEntry)
    entry.itemCount += 1
    if (doneItems.has(item.id)) entry.itemsDone += 1
    else entry.nextItemTitle = entry.nextItemTitle ?? item.title
    byUrl.set(item.resource.url, entry)
  }
  return { trackId: plan.id, trackName: plan.name, entries: [...byUrl.values()] }
}

function detailOf(plan: PlanSeed): LearningTrackDetail {
  const items = PLAN_ITEMS[plan.id]!
  const track = trackOf(plan)
  const nextPlanItem = items.find((i) => !doneItems.has(i.id))
  const sessions = sessionsOf(plan)
  return {
    track,
    percent: Math.round((track.itemsDone / track.itemsTotal) * 100),
    pace: paceOf(plan, SEED_TODAY),
    units: unitsOf(plan),
    paceSeries: paceSeriesOf(plan),
    sessions,
    totalMinutes: sessions.reduce((n, s) => n + s.minutes, 0),
    nextItem: nextPlanItem ? detailItem(nextPlanItem) : null,
  }
}

// ── Journal derivations (the Journal index and the "that day" strip) ────────
//
// EVERY NUMBER ON THE JOURNAL INDEX IS COMPUTED HERE, not stored. README §State Management puts
// counts, streaks and pace wording under "Derived, never stored", and a stored streak is a number
// that goes wrong silently the first time an entry is written for a past day.

/** 26 columns of seven days, ending on the seeded today. The first cell is a Monday. */
const HEATMAP_WEEKS = 26
const HEATMAP_FROM: ISODate = startOfWeek(addDays(SEED_TODAY, -(HEATMAP_WEEKS - 1) * 7))

/** How many months the "Jump to a month" picker offers. Six, as the mockup draws it. */
const MONTH_PICKER_COUNT = 6

function streaks(dates: ISODate[]): { current: number; longest: number } {
  const sorted = [...dates].sort()
  let longest = 0
  let run = 0
  let endsToday = 0
  for (let i = 0; i < sorted.length; i++) {
    const prev = sorted[i - 1]
    run = prev && daysBetween(prev, sorted[i]!) === 1 ? run + 1 : 1
    longest = Math.max(longest, run)
    // A streak is only "current" if it reaches today.
    endsToday = sorted[i] === SEED_TODAY ? run : endsToday
  }
  return { current: endsToday, longest }
}

function journalStats(): JournalStats {
  const entries = [...journal.values()]
  const words = entries.reduce((n, e) => n + e.wordCount, 0)
  const byDate = new Map(entries.map((e) => [e.date, e]))
  const { current, longest } = streaks(entries.map((e) => e.date))

  const heatmap: JournalHeatDay[] = []
  for (let d = HEATMAP_FROM; d <= SEED_TODAY; d = addDays(d, 1)) {
    heatmap.push({ date: d, words: byDate.get(d)?.wordCount ?? 0 })
  }

  const tagCounts = new Map<string, number>()
  for (const e of entries) for (const t of e.tags) tagCounts.set(t, (tagCounts.get(t) ?? 0) + 1)

  // Months, newest first, starting from the one the seeded today falls in.
  const months: JournalMonth[] = []
  for (let i = 0; i < MONTH_PICKER_COUNT; i++) {
    const anchorDate = toDate(SEED_TODAY)
    anchorDate.setUTCMonth(anchorDate.getUTCMonth() - i, 1)
    const first = toISODate(anchorDate) as ISODate
    const month = first.slice(0, 7)
    // Days of the month that have actually happened; the current month stops at today.
    const lastOfMonth = new Date(Date.UTC(anchorDate.getUTCFullYear(), anchorDate.getUTCMonth() + 1, 0, 12))
    const last = (toISODate(lastOfMonth) < SEED_TODAY ? toISODate(lastOfMonth) : SEED_TODAY) as ISODate
    let written = 0
    let days = 0
    let gap = 0
    let longestGap = 0
    for (let d = first; d <= last; d = addDays(d, 1)) {
      days += 1
      if (byDate.has(d)) {
        written += 1
        gap = 0
      } else {
        gap += 1
        longestGap = Math.max(longestGap, gap)
      }
    }
    months.push({ month, label: monthName(first, true), written, days, longestGap })
  }

  return {
    entryCount: entries.length,
    wordCount: words,
    averageWords: entries.length ? Math.round(words / entries.length) : 0,
    currentStreak: current,
    longestStreak: longest,
    entriesThisWeek: entries.filter((e) => daysBetween(e.date, SEED_TODAY) < 7 && e.date <= SEED_TODAY)
      .length,
    withVoice: entries.filter((e) => e.voiceMemoCount > 0).length,
    heatmap,
    tags: [...tagCounts.entries()]
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count || (a.tag < b.tag ? -1 : 1)),
    months,
    quietPattern: quietPattern(entries),
  }
}

/**
 * The one derived OBSERVATION on the screen: which weekday carries the longest entries and which
 * the shortest. It is an average of what is there, and nothing more — no causal story about
 * discussion sections, no threshold, no advice (CLAUDE.md → "Mirror, not adviser").
 *
 * Null unless every weekday has at least three entries behind it and the two ends actually
 * differ. A pattern computed from one Tuesday is not a pattern.
 */
function quietPattern(entries: JournalEntry[]): JournalStats['quietPattern'] {
  const totals = Array.from({ length: 7 }, () => ({ words: 0, n: 0 }))
  for (const e of entries) {
    const slot = totals[weekdayOf(e.date)]!
    slot.words += e.wordCount
    slot.n += 1
  }
  if (totals.some((t) => t.n < 3)) return null
  const averages = totals.map((t, i) => ({ weekday: i, avg: t.words / t.n }))
  const sorted = [...averages].sort((a, b) => b.avg - a.avg)
  const top = sorted[0]!
  const bottom = sorted[sorted.length - 1]!
  if (top.weekday === bottom.weekday) return null
  const name = (wd: number) => weekdayName(addDays(SEED_TODAY, wd - weekdayOf(SEED_TODAY)))
  return { longestWeekday: name(top.weekday), shortestWeekday: name(bottom.weekday) }
}

/** Which day of an unbroken run of learning activity a date is, or null if it is not in one. */
function learningStreakDay(date: ISODate): number | null {
  if (!ACTIVITY_DAYS.includes(date)) return null
  let day = 1
  while (ACTIVITY_DAYS.includes(addDays(date, -day))) day += 1
  return day
}

function dayContext(date: ISODate): JournalDayContext {
  const attended = classOccurrences(date).map((c) => ({
    title: `${c.courseCode} ${c.kind}`,
    detail: c.location,
  }))

  const completedTasks = tasks
    .filter((t) => t.completedAt !== null && t.completedAt.slice(0, 10) === date)
    .map((t) => t.title)

  const learning = PLANS.map((p) => itemOnDate(p, date))
    .filter((i) => i !== null)
    .filter((i) => i.done)
    .map((i) => ({ title: `${i.trackName}: ${i.title}`, minutes: i.minutes }))

  const saves = SAVES.filter((s) => s.savedAt.slice(0, 10) === date).map((s) => ({
    title: s.title,
    detail: s.sourceDetail,
  }))

  return { date, attended, completedTasks, learning, streakDay: learningStreakDay(date), saves }
}

// ── CHAT (11a/11b) and the ⌘K CAPTURE PALETTE (11c) ─────────────────────────
//
// (docs/design/handoff/"LifeOS Chat.dc.html".) APPENDED as its own block at the end of the
// dataset, never interleaved: screen ports run in parallel and a shared block edited by two lanes
// merges badly. Nothing above this line was touched.
//
// ITS OWN IMPORT LINE, for the same reason — a one-identifier edit inside the shared import block
// at the top of the file is a merge conflict for no benefit. ES modules allow it; the declaration
// is hoisted exactly as if it sat with the others.

import { formatLearningExport, formatShortDate, minutesToClock } from '@app/shared'
import type {
  CaptureInput,
  CaptureResult,
  ChatMessage,
  ChatScope,
  ChatScopeId,
  ChatScopeSizePart,
  ChatSource,
  ChatStarter,
  ChatAttachment,
  ChatAttachmentMime,
  ChatProposal,
  ChatProposalDecision,
  ChatProposalConflict,
  ChatProposalEvidence,
  ChatProposalPayload,
  ChatProposalResult,
  ChatThread,
  ChatThreadDetail,
  ClipPreview,
} from './types'

// ── Scope sizes — DERIVED, never stored ─────────────────────────────────────
//
// README §State Management puts "scope-size lines" under *Derived, never stored*, and CLAUDE.md's
// machine-readability rule says an LLM asking the Worker "how much can you see" must get the same
// numbers the screen shows. So they are computed here, on the server side of the contract, and the
// view only pluralises them.
//
// WHAT COUNTS AS A LECTURE IN SCOPE: one LifeOS actually holds a recording for. The dataset has 30
// lecture rows and 6 recordings; counting all 30 would promise reach the app does not have, since
// a lecture that was never recorded holds nothing to read. A citation can only point into
// something that exists.

const isRecorded = (l: Lecture) => l.status !== 'not_recorded'

/** 'COGSCI 175' → 'cogsci175', which is the journal tag the owner actually types. */
const courseTag = (code: string) => code.toLowerCase().replace(/\s+/g, '')

/**
 * The journal entries a COURSE scope admits: the ones that name the course, by tag or in the
 * writing. Not the whole journal — a course scope that claimed all 128 entries would be claiming
 * to have read 128 entries about four different courses to answer a question about one.
 */
function courseJournalEntries(course: Course): JournalEntry[] {
  const tag = courseTag(course.code)
  return [...journal.values()].filter((e) => e.tags.includes(tag) || e.text.includes(course.code))
}

const part = (count: number, noun: string): ChatScopeSizePart => ({ count, noun })
/** Zero is dropped, not printed: "0 lectures in scope" sounds broken where empty sounds empty. */
const sized = (...parts: ChatScopeSizePart[]) => parts.filter((p) => p.count > 0)

function scopeFor(id: ChatScopeId): ChatScope | null {
  const transcribedMemos = VOICE_MEMOS.filter((m) => m.status === 'transcribed')
  const recorded = LECTURES.filter(isRecorded)

  if (id === 'everything') {
    return {
      id,
      label: 'Everything',
      domain: 'everything',
      size: sized(
        part(journal.size, 'entry'),
        part(recorded.length, 'lecture'),
        part(SAVES.length, 'save'),
        part(PLANS.length, 'track'),
      ),
    }
  }
  if (id === 'journal') {
    return {
      id,
      label: 'Journal',
      domain: 'journal',
      size: sized(part(journal.size, 'entry'), part(transcribedMemos.length, 'memo')),
    }
  }
  if (id === 'school') {
    return {
      id,
      label: 'School',
      domain: 'school',
      size: sized(
        part(recorded.length, 'lecture'),
        part(SYLLABUS_EXTRACTS.length, 'syllabus'),
        part(ANNOUNCEMENTS.length, 'announcement'),
      ),
    }
  }
  if (id === 'interests') {
    return {
      id,
      label: 'Interests',
      domain: 'interests',
      size: sized(part(SAVES.length, 'save'), part(buildTopics(SAVES).length, 'cluster')),
    }
  }
  if (id === 'learning') {
    const items = PLANS.reduce((n, p) => n + PLAN_ITEMS[p.id]!.length, 0)
    return {
      id,
      label: 'Learning',
      domain: 'learning',
      size: sized(part(PLANS.length, 'track'), part(items, 'item')),
    }
  }

  const course = COURSES.find((c) => c.id === id.slice('course:'.length))
  if (!course) return null
  return {
    id,
    label: course.code,
    domain: 'school',
    size: sized(
      part(LECTURES.filter((l) => l.courseId === course.id && isRecorded(l)).length, 'lecture'),
      part(SYLLABUS_EXTRACTS.filter((s) => s.courseId === course.id).length, 'syllabus'),
      part(ANNOUNCEMENTS.filter((a) => a.courseId === course.id).length, 'announcement'),
      part(courseJournalEntries(course).length, 'journal entry'),
    ),
  }
}

/** Every pill the SCOPE row can offer: the five fixed scopes, then one per course. */
function allScopes(): ChatScope[] {
  const ids: ChatScopeId[] = [
    'everything',
    'journal',
    'school',
    'interests',
    'learning',
    ...COURSES.map((c) => `course:${c.id}` as ChatScopeId),
  ]
  return ids.map(scopeFor).filter((s): s is ChatScope => s !== null)
}

// ── Source refs → source cards ──────────────────────────────────────────────
//
// A fixture cites an ENTITY, never a string. The locator, the date line, the timestamp and the
// href are all resolved off the real row at read time, so a card that says
// "COGSCI 175 · Lecture 5 · 13:04" says it because a lecture with index 5 and a segment starting
// at 784,600ms both exist. If either stopped existing this THROWS rather than rendering a
// plausible dead card — a citation nobody can follow is worse than no citation, because it looks
// like one.

type ChatSourceRef =
  | { kind: 'transcript'; lectureId: string; segmentIndex: number; q: string }
  | { kind: 'announcement'; announcementId: string }
  | { kind: 'syllabus'; courseId: string; examLabel: string }
  | { kind: 'journal'; date: ISODate; term: string | null }
  | { kind: 'voice_memo'; memoId: string }

const bad = (what: string): never => {
  throw new Error(`demo chat fixture cites something that does not exist: ${what}`)
}

/** 'Morning memo' / 'Afternoon memo' / 'Evening memo' — read off when it was recorded. */
function memoLabel(recordedAt: ISOTimestamp): string {
  const hour = Number(recordedAt.slice(11, 13))
  if (hour < 12) return 'Morning memo'
  if (hour < 17) return 'Afternoon memo'
  return 'Evening memo'
}

function resolveSource(ref: ChatSourceRef): ChatSource {
  if (ref.kind === 'transcript') {
    const lecture = LECTURES.find((l) => l.id === ref.lectureId) ?? bad(ref.lectureId)
    const t = TRANSCRIPTS.get(ref.lectureId) ?? bad(`transcript for ${ref.lectureId}`)
    const seg =
      t.segments.find((s) => s.index === ref.segmentIndex) ??
      bad(`${ref.lectureId} segment ${ref.segmentIndex}`)
    // The deep link is only honest if the phrase is actually IN the segment: the lecture page
    // filters its transcript by `?q=`, so a phrase that matches nothing lands the reader at the
    // top of the recording while the card promised a moment.
    if (!seg.text.toLowerCase().includes(ref.q.toLowerCase())) {
      bad(`"${ref.q}" is not in ${ref.lectureId} segment ${ref.segmentIndex}`)
    }
    return {
      kind: 'transcript',
      pill: 'TRANSCRIPT',
      locator: `${lecture.courseCode} · ${lectureCitation(lecture)} · ${msToTimestamp(seg.startMs)}`,
      detail: null,
      href: `/school/${courseSlug(lecture.courseCode)}/lecture/${lectureRef(lecture)}?q=${encodeURIComponent(ref.q)}`,
      timestampMs: seg.startMs,
    }
  }

  if (ref.kind === 'announcement') {
    const a = ANNOUNCEMENTS.find((x) => x.id === ref.announcementId) ?? bad(ref.announcementId)
    return {
      kind: 'announcement',
      pill: 'ANNOUNCEMENT',
      locator: a.title,
      detail: `${a.author} · ${formatShortDate(a.postedAt.slice(0, 10) as ISODate)}`,
      href: `/school/${courseSlug(a.courseCode)}`,
      timestampMs: null,
    }
  }

  if (ref.kind === 'syllabus') {
    const s = SYLLABUS_EXTRACTS.find((x) => x.courseId === ref.courseId) ?? bad(ref.courseId)
    const course = COURSES.find((c) => c.id === ref.courseId) ?? bad(ref.courseId)
    const exam = s.exams.find((e) => e.label === ref.examLabel) ?? bad(ref.examLabel)
    const weight = s.gradingBreakdown.find((g) => g.label === ref.examLabel)
    // Everything in the locator came out of the PDF. The percentage is appended only when the
    // breakdown actually carries one — a weight invented for a nicer-looking line is the exact
    // fabrication this screen exists to not do.
    const bits = [exam.label, formatShortDate(exam.date), exam.detail]
      .filter((b): b is string => Boolean(b))
      .concat(weight ? [`${weight.weightPct}%`] : [])
    return {
      kind: 'syllabus',
      pill: 'SYLLABUS · PDF',
      locator: bits.join(' · '),
      detail: `extracted ${formatShortDate(s.provenance.generatedAt.slice(0, 10) as ISODate)}`,
      href: `/school/${courseSlug(course.code)}`,
      timestampMs: null,
    }
  }

  if (ref.kind === 'journal') {
    const entry = journal.get(ref.date) ?? bad(`journal entry ${ref.date}`)
    // The match count is COUNTED, not stored. The mockup's "· 2 matches" is a fact about the
    // entry's text and the term retrieval hit on, and it moves the day either one changes.
    const matches = ref.term
      ? (entry.text.match(new RegExp(ref.term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')) ?? [])
          .length
      : 0
    return {
      kind: 'journal',
      pill: 'JOURNAL',
      locator: `${weekdayName(ref.date)} entry`,
      detail:
        matches > 1
          ? `${formatShortDate(ref.date)} · ${matches} matches`
          : formatShortDate(ref.date),
      href: `/journal/${ref.date}`,
      timestampMs: null,
    }
  }

  const memo = VOICE_MEMOS.find((m) => m.id === ref.memoId) ?? bad(ref.memoId)
  return {
    kind: 'voice_memo',
    pill: 'VOICE MEMO',
    locator: `${memoLabel(memo.recordedAt)} · ${msToTimestamp(memo.durationSec * 1000)}`,
    detail: `${formatShortDate(memo.date)} · ${memo.status}`,
    href: `/journal/${memo.date}`,
    timestampMs: null,
  }
}

// ── The threads ─────────────────────────────────────────────────────────────
//
// Seven threads, matching the mockup's list. ONE of them carries a written conversation, and that
// is deliberate: the other six would need forty-two invented answers, each of which would have to
// carry invented citations to render at all. Inventing cited answers about the owner's own life is
// precisely what this screen exists to prevent, so those threads carry their length as a fact and
// the screen says in words that their messages are not in the demo dataset.
//
// TWO DEVIATIONS FROM THE MOCKUP'S 11a, both forced by the same rule and both reported:
//   · The thread is scoped to COGSCI 175, not NEU C61. NEU C61 has one recorded lecture, no
//     syllabus and no midterm announcement in this dataset; COGSCI 175 has four recordings, the
//     extracted syllabus (Midterm 1 · Oct 8 · in class · 25%) and this morning's midterm-scope
//     announcement — so every one of the mockup's three source-card TYPES resolves to a real row.
//   · The transcript card cites Lecture 5 at 13:04 rather than "Lecture 4 · 12:31". 12:31 does
//     exist in the corpus (segment 43 of that same lecture) but it is a student's question;
//     segment 45 is where the instructor actually says "the one I want on the midterm".

interface ChatThreadSeed {
  id: string
  title: string
  scopeId: ChatScopeId
  startedAt: ISOTimestamp
  updatedAt: ISOTimestamp
  /** The conversation's length. Equals `messages.length` whenever the messages are written out. */
  messageCount: number
  messages: Array<{
    role: 'user' | 'assistant'
    text: string
    sources?: ChatSourceRef[]
    scopeLimit?: ChatMessage['scopeLimit']
    /**
     * What this answer OFFERED to add. `evidenceCites` are 1-based indexes into this message's
     * own `sources` — the same numbers the answer's cards carry — so a proposal in the demo
     * cites something that really resolves, exactly as one from the Worker does.
     */
    proposals?: {
      id: string
      payload: ChatProposalPayload
      evidenceCites: number[]
      /**
       * HIS OWN WORDS, when he asked for this outright rather than the answer finding it. The
       * Worker verifies this really is a piece of the message he sent before it stores such a
       * proposal, so a fixture that put words here he never typed would be modelling something
       * the real path refuses — keep it a literal slice of the `user` message above.
       */
      evidenceRequest?: string
    }[]
  }>
}

const CHAT_THREAD_SEEDS: ChatThreadSeed[] = [
  {
    id: 'thread-midterm-prep',
    title: 'Midterm prep — COGSCI 175',
    scopeId: 'course:course-cogsci175',
    // Both before the seeded clock (08:41). A thread that started in the future is the kind of
    // incoherence that makes a whole dataset stop being trusted.
    startedAt: `${SEED_TODAY}T08:04:00-07:00`,
    updatedAt: `${SEED_TODAY}T08:26:00-07:00`,
    messageCount: 6,
    messages: [
      {
        role: 'user',
        text: 'What did the professor actually say would be on the midterm?',
      },
      {
        role: 'assistant',
        text:
          'The syllabus puts Midterm 1 on Oct 8, in class, at 25% of the grade. This morning’s ' +
          'announcement scopes it to chapters 1–4 plus Clark & Chalmers and says no section ' +
          'material is on it. In Lecture 5 he was more specific than either — Harnad’s symbol ' +
          'grounding version of the Chinese Room is, in his words, the one he wants on the ' +
          'midterm. Nothing in your COGSCI 175 material says whether it is closed-book.',
        sources: [
          {
            kind: 'transcript',
            lectureId: 'lecture-course-cogsci175-2026-09-10',
            segmentIndex: 45,
            q: 'the one I want on the midterm',
          },
          { kind: 'announcement', announcementId: 'ann-175-midterm-scope' },
          { kind: 'syllabus', courseId: 'course-cogsci175', examLabel: 'Midterm 1' },
        ],
        // ── CHAT v3: what this answer OFFERED to add ─────────────────────────────
        //
        // NOTHING HAS BEEN CREATED BY THESE. They are two cards under the answer, and pressing
        // Accept in demo mode really does add a block to the Calendar and a reminder to the
        // Reminders board — which is the point: the destructive-ish half of a feature that ships
        // untested because it needed a login is how a button ends up not working.
        //
        // Both quote a source this same message cites. The midterm's date and weight come off
        // the extracted syllabus (source 3); the reading scope off this morning's announcement
        // (source 2). Neither is a sentence written to look convincing.
        proposals: [
          {
            id: 'proposal-demo-midterm-block',
            evidenceCites: [3],
            payload: {
              kind: 'calendar_block',
              title: 'Midterm 1 — COGSCI 175',
              startsAt: '2026-10-08T14:00:00-07:00',
              endsAt: '2026-10-08T16:00:00-07:00',
              timezone: 'America/Los_Angeles',
              location: 'In class',
              lane: 'personal',
              notes: null,
              quote: 'Midterm 1 · Oct 8 · in class · 25% of the grade',
            },
          },
          {
            id: 'proposal-demo-midterm-reminder',
            evidenceCites: [2],
            payload: {
              kind: 'task',
              title: 'Re-read Clark & Chalmers before Midterm 1',
              due: '2026-10-07',
              reminderAt: '2026-10-07T19:00:00-07:00',
              list: 'School admin',
              notes: 'The day before the sitting on the syllabus.',
              quote: 'chapters 1–4 plus Clark & Chalmers; no section material is on it',
            },
          },
        ],
      },
      {
        role: 'user',
        text: 'Have I written anything about being behind in this class?',
      },
      {
        role: 'assistant',
        text:
          'Once, and about the reading response rather than the material: today you wrote that ' +
          'you are fourteen hours late on it and keep re-reading Clark & Chalmers instead of ' +
          'writing. On Sep 3 the only complaint was the wrong edition of the reader. Your Sep 10 ' +
          'voice memo caught the essay question near enough verbatim the same night, so what is ' +
          'missing is the writing, not the notes.',
        sources: [
          { kind: 'journal', date: SEED_TODAY, term: 'response' },
          { kind: 'journal', date: '2026-09-03', term: null },
          { kind: 'voice_memo', memoId: 'memo-2026-09-10-2118' },
        ],
        scopeLimit: {
          text:
            'Scope was COGSCI 175, so this answer did not read your other three courses. ' +
            'Widen to School?',
          widenTo: 'school',
          widenLabel: 'Widen',
        },
      },
      // ── CHAT v3: WHEN HE JUST ASKS FOR IT ────────────────────────────────────
      //
      // The first two proposals in this thread were FOUND — read out of a syllabus and an
      // announcement. This pair is the other half, and it is the half the feature shipped
      // without: he asks for a reminder outright, and the evidence for it is that he asked.
      // Nothing in the corpus says he wants to email anybody, and it does not need to.
      //
      // The answer still cites what it CLAIMS. "Nothing says whether it is closed-book" is a
      // statement about his material and carries the announcement it read; the reminder carries
      // his own sentence. Two different kinds of honesty on one message, which is the point.
      {
        role: 'user',
        text:
          'Remind me to email her about whether the midterm is closed-book tomorrow at 9am.',
      },
      {
        role: 'assistant',
        text:
          'Still nothing on that: this morning’s announcement scopes the reading and the ' +
          'grading, and stops short of saying whether it is closed-book. Here is the reminder ' +
          'you asked for — tomorrow is Sep 17.',
        sources: [{ kind: 'announcement', announcementId: 'ann-175-midterm-scope' }],
        proposals: [
          {
            id: 'proposal-demo-closed-book-ask',
            evidenceCites: [],
            evidenceRequest: 'email her about whether the midterm is closed-book tomorrow at 9am',
            payload: {
              kind: 'task',
              title: 'Email her about whether the midterm is closed-book',
              due: '2026-09-17',
              reminderAt: '2026-09-17T09:00:00-07:00',
              list: 'School admin',
              // WHICH DAY "TOMORROW" WAS COUNTED FROM, in words. A relative date that does not
              // say what it resolved against is a date nobody can check.
              notes: 'Tomorrow, counted from Sep 16.',
              quote: 'email her about whether the midterm is closed-book tomorrow at 9am',
            },
          },
        ],
      },
    ],
  },
  {
    id: 'thread-video-saves',
    title: 'Video model saves',
    scopeId: 'interests',
    startedAt: `${SEED_TODAY}T07:12:00-07:00`,
    updatedAt: `${SEED_TODAY}T07:30:00-07:00`,
    messageCount: 11,
    messages: [],
  },
  {
    id: 'thread-last-week',
    title: 'What was on my mind last week?',
    scopeId: 'journal',
    startedAt: '2026-09-14T20:02:00-07:00',
    updatedAt: '2026-09-14T20:18:00-07:00',
    messageCount: 6,
    messages: [],
  },
  {
    id: 'thread-algebra-pace',
    title: 'Is the algebra plan too slow?',
    scopeId: 'learning',
    startedAt: '2026-09-13T16:40:00-07:00',
    updatedAt: '2026-09-13T16:49:00-07:00',
    messageCount: 3,
    messages: [],
  },
  {
    id: 'thread-sublet',
    title: 'Sublet paperwork — what did Maya say?',
    scopeId: 'everything',
    startedAt: '2026-09-12T11:20:00-07:00',
    updatedAt: '2026-09-12T11:44:00-07:00',
    messageCount: 8,
    messages: [],
  },
  {
    id: 'thread-readings',
    title: 'Which readings did I actually finish?',
    scopeId: 'school',
    startedAt: '2026-09-05T14:02:00-07:00',
    updatedAt: '2026-09-05T14:15:00-07:00',
    messageCount: 5,
    messages: [],
  },
  {
    id: 'thread-petition',
    title: 'Draft the petition email',
    scopeId: 'everything',
    startedAt: '2026-08-28T09:31:00-07:00',
    updatedAt: '2026-08-28T09:58:00-07:00',
    messageCount: 9,
    messages: [],
  },
]

function threadOf(seed: ChatThreadSeed): ChatThread {
  const scope = scopeFor(seed.scopeId)
  return {
    id: seed.id,
    title: seed.title,
    scopeId: seed.scopeId,
    scopeLabel: scope?.label ?? 'Everything',
    messageCount: seed.messageCount,
    startedAt: seed.startedAt,
    updatedAt: seed.updatedAt,
  }
}

// ── CHAT v3 in demo — proposals that really create, and pictures that really attach ─────
//
// ASKING STILL REFUSES (see `sendChatMessage` below — there is no model here and writing an
// answer by hand would mean writing its citations too). Everything AFTER the answer is real:
// the two proposals above are cards, Accept adds a block to the same `calendarBlocks` array the
// Calendar screen reads, and Reject mutes the card without writing anything. That is the half
// worth exercising without a login, because it is the half that touches the owner's week.

/** Decisions made this session, keyed by proposal id. The seeds themselves stay untouched. */
const chatProposalDecisions = new Map<
  string,
  { status: 'accepted' | 'rejected'; resultId: string | null; decidedAt: ISOTimestamp }
>()

/** The seeded clock as an instant. The only "now" this dataset has — nothing here reads a clock. */
const SEED_NOW: ISOTimestamp = `${SEED_TODAY}T${SEED_CLOCK_HHMM}:00-07:00`

/** 'Thu Sep 3 · 1:00 PM' — the same shape the Worker's `whenLabel` produces. */
function demoWhenLabel(iso: ISOTimestamp): string {
  const date = iso.slice(0, 10) as ISODate
  const minutes = Number(iso.slice(11, 13)) * 60 + Number(iso.slice(14, 16))
  return `${weekdayName(date, true)} ${formatShortDate(date)} · ${minutesToClock(minutes)}`
}

/**
 * WHAT A PROPOSED BLOCK WOULD LAND ON TOP OF, computed against the fixture's own week rather
 * than written into the seed. A hand-written conflict would be the demo asserting a collision
 * nobody checked — the exact kind of decoration the rest of this file refuses.
 *
 * Half-open on both sides, so back-to-back is not a clash. Same rule as the Worker's.
 */
function demoConflicts(payload: ChatProposalPayload): ChatProposalConflict[] {
  if (payload.kind !== 'calendar_block') return []
  const from = Date.parse(payload.startsAt)
  const to = Date.parse(payload.endsAt)
  const day = payload.startsAt.slice(0, 10) as ISODate
  const candidates: { kind: ChatProposalConflict['kind']; title: string; startsAt: string; endsAt: string }[] = [
    ...calendarBlocks
      .filter((b) => b.state !== 'suggested')
      .map((b) => ({ kind: 'calendar_block' as const, title: b.title, startsAt: b.startsAt, endsAt: b.endsAt })),
    ...canvasBlocksOn(day).map((b) => ({
      kind: 'calendar_block' as const,
      title: b.title,
      startsAt: b.startsAt,
      endsAt: b.endsAt,
    })),
    ...GOOGLE_BLOCKS.map((b) => ({
      kind: 'google_event' as const,
      title: b.title,
      startsAt: b.startsAt,
      endsAt: b.endsAt,
    })),
  ]
  return candidates
    .filter((c) => Date.parse(c.startsAt) < to && Date.parse(c.endsAt) > from)
    .map((c) => ({ kind: c.kind, title: c.title, whenLabel: demoWhenLabel(c.startsAt as ISOTimestamp) }))
}

function messagesOf(seed: ChatThreadSeed): ChatMessage[] {
  return seed.messages.map((m, i) => {
    const sources = (m.sources ?? []).map(resolveSource)
    return {
      id: `${seed.id}-msg-${i + 1}`,
      role: m.role,
      text: m.text,
      sources,
      scopeLimit: m.scopeLimit ?? null,
      // The demo dataset ships no sent pictures. An uploaded one lives in the composer until
      // `sendChatMessage` refuses, which is where demo mode's honesty about answering stops.
      attachments: [],
      proposals: (m.proposals ?? []).map((proposal) => {
        const decision = chatProposalDecisions.get(proposal.id)
        const evidence: ChatProposalEvidence[] = [
          // HIS ASK FIRST when there is one — it is the strongest thing on the line, because it
          // is the one piece of evidence he does not have to take anybody's word for.
          ...(proposal.evidenceRequest
            ? [{ kind: 'request' as const, quote: proposal.evidenceRequest }]
            : []),
          ...proposal.evidenceCites
            .map((cite) => ({ cite, source: sources[cite - 1] }))
            .filter((entry): entry is { cite: number; source: ChatSource } => entry.source !== undefined)
            .map((entry): ChatProposalEvidence => ({ kind: 'source', cite: entry.cite, source: entry.source })),
        ]
        return {
          id: proposal.id,
          kind: proposal.payload.kind,
          status: decision?.status ?? 'proposed',
          payload: proposal.payload,
          evidence,
          conflicts: demoConflicts(proposal.payload),
          resultId: decision?.resultId ?? null,
          decidedAt: decision?.decidedAt ?? null,
          createdAt: seed.updatedAt,
        }
      }),
    }
  })
}

/** Every proposal in the dataset, flattened — what accept and reject look an id up in. */
function findSeededProposal(
  proposalId: string,
): { payload: ChatProposalPayload; message: ChatMessage } | null {
  for (const seed of CHAT_THREAD_SEEDS) {
    const messages = messagesOf(seed)
    for (let i = 0; i < messages.length; i += 1) {
      const raw = seed.messages[i]?.proposals?.find((p) => p.id === proposalId)
      if (raw) return { payload: raw.payload, message: messages[i]! }
    }
  }
  return null
}

/** Pictures pasted into the composer this session. Object URLs, revoked by nothing — demo only. */
let createdAttachments = 0
const chatAttachmentUrls = new Map<string, string>()

/**
 * THE THREE STARTERS on an empty thread (11b).
 *
 * The prompts are the mockup's own; every number under them is counted here, and the second one
 * even takes its SUBJECT from the corpus — the biggest cluster, whatever it currently is — so a
 * starter can never suggest a topic the library no longer has.
 */
function chatStarters(): ChatStarter[] {
  const transcribed = LECTURES.filter((l) => l.status === 'transcribed').length
  const topics = buildTopics(SAVES)
  const biggest = [...topics].sort((a, b) => b.count - a.count)[0]

  // "Last week" is the seven days ending today, today included — the same window the Journal
  // index's `entriesThisWeek` uses, because two "this week"s that disagree is a bug in waiting.
  const from = addDays(SEED_TODAY, -6)
  const week = [...journal.values()].filter((e) => e.date >= from && e.date <= SEED_TODAY)
  const weekMemos = week.reduce((n, e) => n + e.voiceMemoCount, 0)

  const out: ChatStarter[] = [
    {
      id: 'starter-lectures',
      prompt: 'What did the professor say about the final project?',
      scopeId: 'school',
      reads: sized(part(transcribed, 'transcript')),
      domain: 'school',
    },
  ]
  if (biggest) {
    out.push({
      id: 'starter-saves',
      prompt: `Summarize everything I’ve saved about ${biggest.label}`,
      scopeId: 'interests',
      reads: sized(part(biggest.count, 'save')),
      domain: 'interests',
    })
  }
  out.push({
    id: 'starter-journal',
    prompt: 'What was on my mind last week?',
    scopeId: 'journal',
    reads: sized(part(week.length, 'entry'), part(weekMemos, 'memo')),
    domain: 'journal',
  })
  return out
}

// ── The ⌘K capture palette ──────────────────────────────────────────────────

/**
 * WHAT THE V5 CAPTURE ENDPOINT WILL FETCH, standing in for the fetch itself.
 *
 * A pasted link's title cannot be derived — it has to be read off the page — so the demo holds the
 * titles it knows and returns null for everything else. The palette then shows the link, the
 * source it PARSED out of the URL, and where it will file, and says the title has not been
 * fetched. Deriving "Wan22 Lora Stack" from a URL slug would be the app inventing a headline.
 */
const CLIP_PREVIEWS: Record<string, string> = {
  // The mockup's own pasted URL and its own fetched title (11c).
  'reddit.com/r/comfyui/comments/wan22_lora_stack':
    'Stacking two motion LoRAs without melting the faces',
}

/** Scheme and www. are noise; the rest of the URL is the key. Trailing slash normalised away. */
const clipKey = (url: string) =>
  url.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '')

function clipSource(url: string): { source: SaveSource; sourceDetail: string } {
  const key = clipKey(url)
  const sub = /^(?:old\.|new\.)?reddit\.com\/r\/([a-z0-9_]+)/i.exec(key)
  if (sub) return { source: 'reddit', sourceDetail: `r/${sub[1]}` }
  if (/^(youtube\.com|youtu\.be)/.test(key)) return { source: 'youtube', sourceDetail: 'YouTube' }
  if (/^(x\.com|twitter\.com)/.test(key)) return { source: 'x', sourceDetail: 'x.com' }
  return { source: 'article', sourceDetail: key.split('/')[0] ?? key }
}

/** The palette's own idea of a link, kept in ONE place so detection and saving cannot disagree. */
const looksLikeUrl = (text: string) =>
  /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i.test(text.trim())

let captureSeq = 0

function captureInDemo(input: CaptureInput): CaptureResult {
  const text = input.text.trim()
  captureSeq += 1
  const stamp = `${SEED_TODAY}T${SEED_CLOCK_HHMM}:00-07:00`

  // A LINK BECOMES A SAVE. It lands unfiled and unread, which is exactly what the Interests
  // screen counts as its inbox — so the number on that screen moves the moment this returns.
  if (input.type === 'clip') {
    const { source, sourceDetail } = clipSource(text)
    const save: InterestSave = {
      id: `save-capture-${captureSeq}`,
      title: CLIP_PREVIEWS[clipKey(text)] ?? text,
      url: /^https?:\/\//i.test(text) ? text : `https://${text}`,
      source,
      sourceDetail,
      topic: null,
      savedAt: stamp,
      unread: true,
      note: null,
      noteEditedAt: null,
      excerpt: null,
      // Null, not a placeholder: no model has read this, and a summary is an artifact that cannot
      // exist without provenance (packages/shared/src/school.ts).
      summary: null,
      durationSec: null,
    }
    SAVES.unshift(save)
    SAVES_BY_ID.set(save.id, save)
    return { id: save.id, type: 'clip', destination: 'Interests · Inbox', href: '/interests' }
  }

  // EVERYTHING ELSE BECOMES AN INBOX ROW. LifeOS has exactly one Inbox — the tasks list of that
  // name — so a note, a task and an unset capture all land there. The palette reports the
  // destination in words rather than implying a notes app that does not exist.
  const task: Task = {
    id: `task-capture-${captureSeq}`,
    title: text,
    done: false,
    due: input.due ?? null,
    list: 'Inbox',
    priority: 'none',
    // Captured from the palette, not proposed by the chat.
    proposalId: null,
    // A captured thought has no estimate. Nothing reads a length out of a sentence.
    estimateMin: null,
    rrule: null,
    // A reminder is an ambient task: `getReminders` selects on exactly this field, so setting it
    // is what actually puts the row on the Reminders card rather than a second kind of row.
    reminderAt:
      input.type === 'reminder'
        ? (input.reminderAt ?? `${input.due ?? SEED_TODAY}T09:00:00-07:00`)
        : null,
    createdAt: stamp,
    completedAt: null,
  }
  tasks = [task, ...tasks]
  return {
    id: task.id,
    type: input.type,
    destination: input.type === 'reminder' ? 'Tasks · Reminders' : 'Tasks · Inbox',
    href: '/tasks',
  }
}

// ── The implementation ──────────────────────────────────────────────────────

// ════════════════════════════════════════════════════════════════════════════
// SEARCH (10a/10b) — a REAL literal search over this dataset, plus a curated
// neighbour list that admits to being one.
//
// APPENDED as its own block; nothing above it was reorganised.
//
// WHAT IS REAL HERE. The literal half is a genuine search: every journal entry
// (including the backfilled year), every transcript segment, every save, task,
// assignment, announcement, syllabus fact, lecture summary, learning item and
// session note is scanned, terms are matched at word starts, hits are ranked and
// snippets are cut from the item's own words. Nothing about the exact block is
// staged — change the query and the answers change, because they are answers.
//
// WHAT CANNOT BE REAL HERE, AND SAYS SO. Semantic similarity needs an embedding,
// and demo mode has no model to run one with. Faking it — "related" rows picked
// by shared tags, or by the same cluster, or by nearness in time — would be the
// app claiming to have understood something it never read, which is the one
// thing this product is not allowed to do (CLAUDE.md → Product identity rules).
// So the related block is a HAND-WRITTEN neighbour list for the two queries the
// handoff designs, `relatedBasis` returns 'curated' for those and 'unavailable'
// for every other query, and the screen prints the difference. Real neighbours
// arrive with V4, when Vectorize can answer.
//
// A CURATED NEIGHBOUR THAT TURNS OUT TO CONTAIN THE WORDS IS NOT A NEIGHBOUR: it
// is an answer, and it is dropped from the related block rather than shown
// twice. That filter runs below, on every result, not by hand on this list.
// ════════════════════════════════════════════════════════════════════════════

/** One searchable thing, flattened out of whatever domain shape it came from. */
interface SearchRecord {
  id: string
  domain: SearchDomain
  kind: SearchHitKind
  locator: string
  title: string | null
  body: string | null
  date: ISODate | null
  href: string
  timestampMs: number | null
  note: string | null
  chips: string[]
  /** Right-aligned fact when this record appears as a RELATED row: '18 min'. */
  badge: string | null
}

/** 'Wednesday entry' — the demo journal has no titles, so the day names the entry. */
const journalTitle = (date: ISODate) => `${weekdayName(date)} entry`

const ASSIGNMENT_KIND_WORD: Record<Assignment['kind'], string> = {
  reading_response: 'reading response',
  essay: 'essay',
  quiz: 'quiz',
  problem_set: 'problem set',
  exam: 'exam',
  project: 'project',
  // Canvas has no assignment-type field; nothing in the demo dataset is unclassified, but the
  // word has to exist because a real synced assignment almost always is.
  unclassified: 'assignment',
}

/**
 * THE CORPUS, rebuilt per query rather than cached at module load.
 *
 * `saveJournalEntry` mutates the journal map, so a corpus frozen at import time would let a
 * search miss a line the owner wrote two seconds ago — the exact failure that teaches someone
 * search cannot be trusted. Seven hundred records of string work per keystroke (debounced) is
 * not a cost worth a stale index.
 */
function searchCorpus(): SearchRecord[] {
  const rows: SearchRecord[] = []

  // ── journal ───────────────────────────────────────────────────────────────
  for (const entry of journal.values()) {
    rows.push({
      id: `journal:${entry.date}`,
      domain: 'journal',
      kind: 'journal_entry',
      locator: journalTitle(entry.date),
      title: null,
      body: entry.text,
      date: entry.date,
      href: `/journal/${entry.date}`,
      timestampMs: null,
      note: null,
      chips: entry.tags,
      badge: null,
    })
  }

  // A queued memo has no transcript, so there is nothing to search. It is absent from the index
  // rather than present and empty — an empty hit would read as "this memo says nothing".
  for (const memo of VOICE_MEMOS) {
    if (memo.transcriptText === null) continue
    rows.push({
      id: `memo:${memo.id}`,
      domain: 'journal',
      kind: 'voice_memo',
      locator: `Voice memo · ${msToTimestamp(memo.durationSec * 1000)}`,
      title: null,
      body: memo.transcriptText,
      date: memo.date,
      href: `/journal/${memo.date}`,
      timestampMs: null,
      note: 'transcribed on your PC',
      chips: [],
      badge: msToTimestamp(memo.durationSec * 1000),
    })
  }

  // ── school ────────────────────────────────────────────────────────────────
  for (const [lectureId, t] of TRANSCRIPTS) {
    const lecture = LECTURES.find((l) => l.id === lectureId)
    if (!lecture) continue
    const slug = courseSlug(lecture.courseCode)
    for (const seg of t.segments) {
      rows.push({
        id: `seg:${seg.id}`,
        domain: 'school',
        kind: 'transcript',
        locator: `${lecture.courseCode} · ${lectureCitation(lecture)} · ${msToTimestamp(seg.startMs)}`,
        title: null,
        body: seg.text,
        date: lecture.date,
        // `?t=` is the citation convention arriving in the URL: the row promises "Jump to 12:31"
        // and the link has to be able to keep that promise. `?q=` is appended per query below,
        // which is what seeds the lecture page's own transcript search.
        href: `/school/${slug}/lecture/${lectureRef(lecture)}?t=${seg.startMs}`,
        timestampMs: seg.startMs,
        note:
          lecture.audioLocation === 'archived_on_pc'
            ? 'audio on desktop · transcript available'
            : null,
        chips: [],
        badge: msToTimestamp(seg.startMs),
      })
    }
  }

  for (const a of ANNOUNCEMENTS) {
    rows.push({
      id: `ann:${a.id}`,
      domain: 'school',
      kind: 'announcement',
      locator: `${a.courseCode} · ${a.author}`,
      title: a.title,
      body: a.body,
      date: a.postedAt.slice(0, 10),
      href: `/school/${courseSlug(a.courseCode)}`,
      timestampMs: null,
      note: null,
      chips: [],
      badge: null,
    })
  }

  for (const a of ASSIGNMENTS) {
    rows.push({
      id: `asg:${a.id}`,
      domain: 'school',
      kind: 'assignment',
      locator: `${a.courseCode} · ${ASSIGNMENT_KIND_WORD[a.kind]}`,
      title: a.title,
      body: null,
      date: dueDateOf(a),
      href: `/school/${courseSlug(a.courseCode)}`,
      timestampMs: null,
      note: null,
      chips: [],
      badge: null,
    })
  }

  // The syllabus is indexed in its three READABLE sections rather than as one blob, so a hit can
  // say WHERE in the document it was — 'COGSCI 175 · grading breakdown' is a locator; 'COGSCI 175
  // · syllabus' is a shrug. Every one of them wears the provenance line, because a syllabus fact
  // in this app came out of a PDF a model read, not out of Canvas.
  for (const s of SYLLABUS_EXTRACTS) {
    const course = COURSES.find((c) => c.id === s.courseId)
    if (!course) continue
    const slug = courseSlug(course.code)
    const on = s.provenance.generatedAt.slice(0, 10)
    const section = (name: string, body: string, suffix: string) =>
      rows.push({
        id: `syl:${s.courseId}:${suffix}`,
        domain: 'school',
        kind: 'syllabus',
        locator: `${course.code} · ${name}`,
        title: null,
        body,
        date: on,
        href: `/school/${slug}`,
        timestampMs: null,
        note: 'extracted from PDF',
        chips: [],
        badge: null,
      })
    section(
      'grading breakdown',
      s.gradingBreakdown.map((g) => `${g.label} counts for ${g.weightPct}% of the final grade.`).join(' '),
      'grading',
    )
    section(
      'exam schedule',
      s.exams.map((e) => `${e.label} — ${e.date}${e.detail ? `, ${e.detail}` : ''}.`).join(' '),
      'exams',
    )
    section(
      'weekly topics',
      s.weeklyTopics.map((w) => `Week ${w.week}: ${w.title}.`).join(' '),
      'weeks',
    )
  }

  for (const sum of LECTURE_SUMMARIES) {
    const lecture = LECTURES.find((l) => l.id === sum.lectureId)
    if (!lecture) continue
    rows.push({
      id: `sum:${sum.lectureId}`,
      domain: 'school',
      kind: 'lecture_summary',
      locator: `${lecture.courseCode} · ${lectureCitation(lecture)} · summary`,
      title: null,
      body: sum.bullets.join(' '),
      date: lecture.date,
      href: `/school/${courseSlug(lecture.courseCode)}/lecture/${lectureRef(lecture)}`,
      timestampMs: null,
      // An AI artifact cannot render without naming what wrote it (packages/shared/src/school.ts).
      note: `summarised by ${sum.provenance.model}`,
      chips: [],
      badge: null,
    })
  }

  // ── interests ─────────────────────────────────────────────────────────────
  //
  // THE AI TWO-LINER IS NOT IN THE HAYSTACK. A save's `summary` is a model's sentence about the
  // save, and matching a query against it would let the exact block claim the AUTHOR wrote words
  // that a model did. Title, excerpt and source are the save's own; those are what is searched.
  const SAVE_KIND: Record<SaveSource, SearchHitKind> = {
    reddit: 'save_reddit',
    youtube: 'save_youtube',
    article: 'save_article',
    x: 'save_x',
  }
  for (const save of SAVES) {
    const cluster = save.topic ? `topic-${save.topic.toLowerCase().replace(/\s+/g, '-')}` : null
    rows.push({
      id: `save:${save.id}`,
      domain: 'interests',
      kind: SAVE_KIND[save.source],
      locator: save.sourceDetail,
      title: save.title,
      body: save.excerpt,
      date: save.savedAt.slice(0, 10),
      // The cluster rides along so the save is actually in the list the library renders; without
      // it the pane opens on a save the grid behind it is not showing.
      href: cluster
        ? `/interests?cluster=${cluster}&save=${save.id}`
        : `/interests?save=${save.id}`,
      timestampMs: null,
      note: save.note ? `your note: ${save.note}` : null,
      chips: save.note ? ['1 note'] : [],
      badge: save.durationSec !== null ? `${Math.round(save.durationSec / 60)} min` : null,
    })
  }

  // ── tasks ─────────────────────────────────────────────────────────────────
  for (const task of TASK_SEEDS) {
    rows.push({
      id: `task:${task.id}`,
      domain: 'tasks',
      kind: 'task',
      locator: task.list,
      title: task.title,
      body: null,
      date: task.due,
      href: '/tasks',
      timestampMs: null,
      note: task.done ? 'done' : null,
      chips: [],
      badge: null,
    })
  }

  // ── learning ──────────────────────────────────────────────────────────────
  for (const plan of PLANS) {
    for (const item of PLAN_ITEMS[plan.id] ?? []) {
      rows.push({
        id: `li:${item.id}`,
        domain: 'learning',
        kind: 'learning_item',
        locator: `${plan.name} · ${item.unitName}`,
        title: item.title,
        body: null,
        date: item.plannedDate,
        href: `/learning/${plan.id}`,
        timestampMs: null,
        note: null,
        chips: [],
        badge: null,
      })
    }
    for (const session of sessionsOf(plan)) {
      if (session.note === null) continue
      rows.push({
        id: `ls:${plan.id}:${session.date}`,
        domain: 'learning',
        kind: 'learning_session',
        locator: `${plan.name} · session note`,
        title: null,
        body: session.note,
        date: session.date,
        href: `/learning/${plan.id}`,
        timestampMs: null,
        note: `${session.minutes} min logged`,
        chips: [],
        badge: null,
      })
    }
  }

  return rows
}

// ── Matching ────────────────────────────────────────────────────────────────
//
// TERMS MATCH AT WORD STARTS, not anywhere in the string. Plain `includes` makes 'art' find
// 'start' and 'model' find 'remodel', and a search that returns things the owner cannot see the
// reason for is a search he stops using. A prefix match is still allowed on purpose — 'model'
// finding 'models' is what makes the handoff's own 10b query work, and nothing here stems.

const isWordChar = (ch: string) => /[a-z0-9]/.test(ch)

/** How many times `term` starts a word inside `text`. Both must already be lowercase. */
function countTerm(text: string, term: string): number {
  if (!term) return 0
  let count = 0
  let from = 0
  for (;;) {
    const at = text.indexOf(term, from)
    if (at === -1) return count
    if (at === 0 || !isWordChar(text[at - 1]!)) count++
    from = at + 1
  }
}

/** The first word-start position of any term, or -1. */
function firstTermAt(text: string, terms: string[]): number {
  let best = -1
  for (const term of terms) {
    let from = 0
    for (;;) {
      const at = text.indexOf(term, from)
      if (at === -1) break
      if (at === 0 || !isWordChar(text[at - 1]!)) {
        if (best === -1 || at < best) best = at
        break
      }
      from = at + 1
    }
  }
  return best
}

const SNIPPET_BEFORE = 70
const SNIPPET_AFTER = 190

/**
 * The matched window of an item's own text, cut at word boundaries, with '…' where it was cut —
 * the mockup's '… and that is the part I would put on the midterm, because …'. Never a summary.
 */
function snippetOf(body: string, terms: string[]): string {
  const at = firstTermAt(body.toLowerCase(), terms)
  if (at === -1) return body.length > SNIPPET_AFTER ? `${body.slice(0, SNIPPET_AFTER).trimEnd()}…` : body
  let start = Math.max(0, at - SNIPPET_BEFORE)
  let end = Math.min(body.length, at + SNIPPET_AFTER)
  if (start > 0) {
    const space = body.indexOf(' ', start)
    if (space !== -1 && space < at) start = space + 1
  }
  if (end < body.length) {
    const space = body.lastIndexOf(' ', end)
    if (space !== -1 && space > at) end = space
  }
  return `${start > 0 ? '…' : ''}${body.slice(start, end).trim()}${end < body.length ? '…' : ''}`
}

/** The query, split the way the index matches it. Empty terms are dropped, not searched for. */
export function searchTerms(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 0)
}

// ── The curated neighbour lists ─────────────────────────────────────────────
//
// One per query the handoff designs, and NOTHING ELSE gets one. Written as pickers over the
// corpus rather than as a list of ids so that a neighbour which stops existing simply drops out,
// instead of leaving a row whose link goes nowhere.
//
// These are a person's opinion about what is near the question. They are labelled as such in the
// UI, they are never mixed into the ranked answers, and they go away the day a real index can
// answer instead (V4).

type RelatedPick = (records: SearchRecord[]) => SearchRecord | undefined

const byId = (id: string): RelatedPick => (rs) => rs.find((r) => r.id === id)

const CURATED_RELATED: Record<string, RelatedPick[]> = {
  // 10a. Two things about being examined that never say the word: the professor telling a class
  // what the marks are actually for, and the plan's own checkpoint sitting in the same weeks.
  midterm: [
    byId('seg:transcript-lecture-course-neuc61-2026-09-14-seg-16'),
    (rs) => rs.find((r) => r.kind === 'learning_item' && r.title === '3.24 Unit 3 checkpoint'),
  ],
  // 10b. The five saves the mockup lists, in its order. This is the state the whole option
  // exists to show: two literal answers, and most of what the owner wanted below the divider.
  'video model motion quality': [
    byId('save:save-hands'),
    byId('save:save-camera-control'),
    byId('save:save-prompt-adherence'),
    byId('save:save-img2vid-workflow'),
    byId('save:save-cost-per-second'),
  ],
}

function runSearch(query: string): SearchResults {
  const terms = searchTerms(query)
  const counts: Record<SearchDomain, number> = {
    journal: 0,
    school: 0,
    interests: 0,
    tasks: 0,
    learning: 0,
  }
  if (terms.length === 0) {
    return { query, terms, exact: [], related: [], relatedBasis: 'unavailable', counts }
  }

  const records = searchCorpus()
  const hits: SearchHit[] = []

  for (const r of records) {
    const title = (r.title ?? '').toLowerCase()
    const locator = r.locator.toLowerCase()
    const body = (r.body ?? '').toLowerCase()
    const chips = r.chips.join(' ').toLowerCase()

    // EVERY term has to be somewhere in the item. The handoff's own 10b subtitle is "2 items
    // contain all four words", which is an AND, and an OR would have buried those two under
    // everything that merely says 'video'.
    let score = 0
    let bodyMatches = 0
    let all = true
    for (const term of terms) {
      const inTitle = countTerm(title, term)
      const inLocator = countTerm(locator, term)
      const inBody = countTerm(body, term)
      const inChips = countTerm(chips, term)
      if (inTitle + inLocator + inBody + inChips === 0) {
        all = false
        break
      }
      bodyMatches += inBody
      // A word in the thing's name says more than the same word buried in its middle.
      score += inTitle * 3 + inLocator * 2 + inBody + inChips
    }
    if (!all) continue

    const snippet = r.body ? snippetOf(r.body, terms) : null
    const shown = snippet ? terms.reduce((n, t) => n + countTerm(snippet.toLowerCase(), t), 0) : 0

    hits.push({
      id: r.id,
      domain: r.domain,
      kind: r.kind,
      locator: r.locator,
      title: r.title,
      snippet,
      date: r.date,
      href:
        r.kind === 'transcript'
          ? `${r.href}&q=${encodeURIComponent(query.trim())}`
          : r.href,
      timestampMs: r.timestampMs,
      note: r.note,
      chips: r.chips,
      otherMatches: Math.max(0, bodyMatches - shown),
      score,
    })
  }

  // Ranked by relevance, and NOT grouped by type (the mockup's own subtitle). Recency breaks a
  // tie because two equally-relevant things are not equally useful, and a dateless record sorts
  // last rather than being given a date it does not have.
  hits.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score
    if (a.date === b.date) return a.id < b.id ? -1 : 1
    if (a.date === null) return 1
    if (b.date === null) return -1
    return a.date < b.date ? 1 : -1
  })

  const key = query.trim().toLowerCase().replace(/\s+/g, ' ')
  const picks = CURATED_RELATED[key]
  const exactIds = new Set(hits.map((h) => h.id))
  const related: SearchRelatedItem[] = []
  if (picks) {
    for (const pick of picks) {
      const r = pick(records)
      // A "neighbour" that contains the words is an answer, and it is already above the divider.
      if (!r || exactIds.has(r.id)) continue
      related.push({
        id: r.id,
        domain: r.domain,
        kind: r.kind,
        locator: r.locator,
        title: r.title ?? (r.body ? snippetOf(r.body, terms) : r.locator),
        date: r.date,
        href: r.href,
        badge: r.badge,
      })
    }
  }

  // THE CHIPS COUNT EVERY ROW ON THE PAGE, both blocks. 10b's own chip row reads `All 7` over
  // two exact answers and five neighbours, and it has to: the chip is a lens on this screen, and
  // a lens that silently excluded five of the seven rows under it would be lying about what
  // filtering it away removes.
  for (const hit of hits) counts[hit.domain]++
  for (const item of related) counts[item.domain]++

  return {
    query,
    terms,
    exact: hits,
    related,
    relatedBasis: picks && related.length > 0 ? 'curated' : 'unavailable',
    counts,
  }
}

export const demo: DataAPI = {
  isDemo: true,

  async getClock(): Promise<Clock> {
    return { date: SEED_TODAY, minutesIntoDay: SEED_CLOCK_MINUTES }
  },

  async getSyncStatus() {
    return clone(SYNC_STATUS)
  },

  async getJournalEntry(date: ISODate) {
    const e = journal.get(date)
    return e ? clone(e) : null
  },

  async getJournalEntries(range: DateRange) {
    return [...journal.values()]
      .filter((e) => e.date >= range.from && e.date <= range.to)
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .map(clone)
  },

  async saveJournalEntry(input: JournalEntryInput) {
    const existing = journal.get(input.date)
    const text = input.text ?? existing?.text ?? ''
    const next: JournalEntry = {
      date: input.date,
      prompt: existing?.prompt ?? promptFor(input.date),
      text,
      wordCount: wordCount(text),
      mood: input.mood !== undefined ? input.mood : (existing?.mood ?? null),
      energy: input.energy !== undefined ? input.energy : (existing?.energy ?? null),
      tags: input.tags ?? existing?.tags ?? [],
      voiceMemoCount: existing?.voiceMemoCount ?? 0,
      // Still deterministic: the seeded clock, not the wall clock.
      updatedAt: `${input.date}T${String(Math.floor(SEED_CLOCK_MINUTES / 60)).padStart(2, '0')}:${String(SEED_CLOCK_MINUTES % 60).padStart(2, '0')}:00-07:00`,
    }
    journal.set(input.date, next)
    return clone(next)
  },

  async getVoiceMemos(date: ISODate) {
    return clone(VOICE_MEMOS.filter((m) => m.date === date).sort((a, b) =>
      a.recordedAt < b.recordedAt ? -1 : 1,
    ))
  },

  /**
   * DEMO MODE HAS NO MICROPHONE PATH, and refuses rather than pretending.
   *
   * The rest of demo mode can be real because it is data. This one cannot: a memo's whole
   * lifecycle is R2 plus a desktop worker that transcribes on a GPU, and neither exists without an
   * account. Accepting the recording and adding a row that flips to "transcribed" would be the
   * dataset inventing a transcript of something the owner actually said — the exact thing the
   * product forbids. So the composer's record button is inert in demo mode and says why, and this
   * method exists only so the contract is complete and this sentence has somewhere to live.
   */
  async uploadVoiceMemo(): Promise<JournalVoiceMemo> {
    throw new Error(
      'Demo mode has no microphone path: recordings upload to R2 and are transcribed by the ' +
        'desktop worker, which needs a signed-in account. The memos shown here are sample data.',
    )
  },

  async getJournalStats(): Promise<JournalStats> {
    return clone(journalStats())
  },

  async getJournalDayContext(date: ISODate): Promise<JournalDayContext> {
    return clone(dayContext(date))
  },

  async getTerm() {
    return clone(TERM)
  },

  async getCourses() {
    return clone(COURSES).map(withUnreadCount)
  },

  async getClassOccurrences(date: ISODate) {
    return classOccurrences(date)
  },

  async getAssignments(filter?: AssignmentFilter) {
    let rows = ASSIGNMENTS.filter((a) => (filter?.courseId ? a.courseId === filter.courseId : true))
      .filter((a) => (filter?.status ? a.status === filter.status : true))
      .filter((a) => (filter?.openOnly ? a.status === 'upcoming' || a.status === 'missing' : true))
      .filter((a) => {
        if (!filter?.dueBefore) return true
        const day = dueDateOf(a)
        // No date is not "before" anything. An undated assignment is excluded from a dueBefore
        // filter rather than swept in by a null comparison.
        return day !== null && day <= filter.dueBefore
      })
      .sort((a, b) => {
        if (!a.dueAt || !b.dueAt) return a.dueAt ? -1 : b.dueAt ? 1 : 0
        return a.dueAt < b.dueAt ? -1 : 1
      })
    if (filter?.limit) rows = rows.slice(0, filter.limit)
    return clone(rows)
  },

  async getLectures(courseId?: string) {
    return clone(
      LECTURES.filter((l) => (courseId ? l.courseId === courseId : true)).sort((a, b) =>
        a.date < b.date ? 1 : -1,
      ),
    )
  },

  async getLectureTranscript(lectureId: string): Promise<LectureTranscript | null> {
    const lecture = LECTURES.find((l) => l.id === lectureId)
    const t = TRANSCRIPTS.get(lectureId)
    if (!lecture || !t) return null
    return clone({ lecture, transcript: t.transcript, segments: t.segments })
  },

  // ── phone lecture upload (`/record`) ──────────────────────────────────────
  //
  // Reading and filing are REAL here; uploading is not, for the same reason the voice recorder
  // refuses: a recording's life is R2 plus a GPU on a desktop machine, and demo mode has neither.
  // Accepting the file would mean either throwing it away silently or claiming a transcription
  // that no machine will ever run.

  async uploadLecture(): Promise<CapturedLecture> {
    throw new Error(NO_UPLOAD_PATH)
  },

  // The durable queue's three calls refuse for the SAME reason `uploadLecture` does, and they
  // refuse rather than pretending because the queue is built to keep a recording until a server
  // says it has it. A demo that answered "registered" would have the queue delete the owner's
  // only copy of a lecture on the word of a fixture.

  async registerUpload(): Promise<UploadRegistration> {
    throw new UploadApiError(0, 'demo_mode', NO_UPLOAD_PATH)
  },

  async putUploadBlob(): Promise<UploadBlobResult> {
    throw new UploadApiError(0, 'demo_mode', NO_UPLOAD_PATH)
  },

  async getUploadState(): Promise<UploadState> {
    throw new UploadApiError(0, 'demo_mode', NO_UPLOAD_PATH)
  },

  async getCapturedLectures(limit = 20): Promise<CapturedLecture[]> {
    return clone(CAPTURED_LECTURES.slice(0, limit))
  },

  async getCapturedLecture(id: string): Promise<CapturedLecture | null> {
    return clone(CAPTURED_LECTURES.find((lecture) => lecture.id === id) ?? null)
  },

  async assignLectureCourse(id: string, courseCode: string | null): Promise<CapturedLecture> {
    const lecture = CAPTURED_LECTURES.find((row) => row.id === id)
    if (!lecture) throw new Error('No lecture with that id.')
    // Same rule as the Worker's PATCH: blank collapses to unfiled, so nothing is ever filed
    // under a course whose code is whitespace.
    lecture.courseCode = courseCode?.trim() ? courseCode.trim() : null
    return clone(lecture)
  },

  // ── school · the three School screens ─────────────────────────────────────

  async getCourse(slug: string) {
    const course = COURSES.find((c) => courseSlug(c.code) === slug.toLowerCase())
    return course ? withUnreadCount(clone(course)) : null
  },

  async getAnnouncements(filter?: AnnouncementFilter) {
    let rows = ANNOUNCEMENTS.filter((a) => (filter?.courseId ? a.courseId === filter.courseId : true))
      .filter((a) => (filter?.unreadOnly ? !a.read : true))
      .sort((a, b) => (a.postedAt < b.postedAt ? 1 : -1))
    if (filter?.limit) rows = rows.slice(0, filter.limit)
    return clone(rows)
  },

  /**
   * The demo half of the read latch: sets `read`, never clears it, and answers only the ids that
   * actually changed — the same contract the Worker keeps, so the card cannot learn one
   * behaviour here and meet another in production.
   */
  async markAnnouncementsRead(ids: string[]) {
    const wanted = new Set(ids)
    const changed = ANNOUNCEMENTS.filter((a) => wanted.has(a.id) && !a.read).map((a) => a.id)
    if (changed.length === 0) return []
    ANNOUNCEMENTS = ANNOUNCEMENTS.map((a) => (wanted.has(a.id) ? { ...a, read: true } : a))
    return changed
  },

  async getSyllabus(courseId: string) {
    const found = SYLLABUS_EXTRACTS.find((s) => s.courseId === courseId)
    // Null, not an empty extract. An empty grading breakdown reads as "this course has no
    // requirements", which is a claim the dataset is in no position to make.
    return found ? clone(found) : null
  },

  /**
   * The syllabus FILE, which is a different question from `getSyllabus`.
   *
   * The same rule the Worker applies (apps/worker/src/routes/syllabus.ts → `pickSyllabusFile`):
   * of this course's files, the ones whose name or folder says "syllabus", preferring one that
   * was actually read and then the most recently updated. Null when there is none — which is a
   * real state and the one where the Canvas-typed flow is all there is.
   */
  async getSyllabusDocument(courseId: string): Promise<SyllabusDocument | null> {
    const candidates = COURSE_FILES.filter(
      (file) =>
        file.courseId === courseId &&
        /syllabus/i.test([file.displayName, file.filename, file.folderPath ?? ''].join(' ')),
    ).sort((a, b) => {
      const read = Number(b.extraction === 'extracted') - Number(a.extraction === 'extracted')
      if (read !== 0) return read
      return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')
    })
    const file = candidates[0]
    if (!file) return null
    return {
      file: clone(file),
      // Null unless the row genuinely says `extracted`. An empty string would render as a
      // syllabus with nothing in it, which is a claim about the document rather than the reading.
      text: file.extraction === 'extracted' ? (COURSE_FILE_TEXT[file.id] ?? null) : null,
    }
  },

  /**
   * REFUSES, and says why. Demo mode has no Anthropic key and no Canvas syllabus HTML to read —
   * it has the RESULT of an extraction, hand-written, which is a different thing entirely.
   *
   * The alternative was to hand back the stored extract and call it a generation. That would be
   * the demo dataset claiming a model just ran, on the one screen whose whole subject is telling
   * machine-made claims from synced facts. A refusal that names the missing piece is honest and
   * costs the demo nothing: the panel it would have filled is already full.
   */
  async generateSyllabus(courseId: string): Promise<SyllabusExtract> {
    const course = COURSES.find((c) => c.id === courseId)
    const has = SYLLABUS_EXTRACTS.some((s) => s.courseId === courseId)
    throw new Error(
      'Demo mode cannot read a syllabus: extracting one calls a model against the HTML Canvas ' +
        'stores for a course, and demo mode has neither an API key nor a Canvas connection. ' +
        // Two different true sentences, and picking the wrong one would be this refusal telling
        // the owner a sample extract exists on a panel that is empty.
        (has
          ? `${course?.code ?? 'This course'}'s panel already shows a hand-written sample extract, ` +
            'labelled with its provenance exactly as a real one would be.'
          : `Nothing has been written for ${course?.code ?? 'this course'} in the sample data, so ` +
            'this panel stays empty here — which is also what a real course with no syllabus ' +
            'page in bCourses looks like.'),
    )
  },

  async getLectureSummary(lectureId: string) {
    const found = LECTURE_SUMMARIES.find((s) => s.lectureId === lectureId)
    return found ? clone(found) : null
  },

  async getFlashcards(lectureId: string) {
    return clone(FLASHCARDS.filter((f) => f.lectureId === lectureId))
  },

  async getPracticeQuestions(lectureId: string) {
    return clone(PRACTICE_QUESTIONS.filter((q) => q.lectureId === lectureId))
  },

  /** Refuses for the same reason `generateSyllabus` does — there is no model on this side. */
  async generateStudySet(): Promise<StudySet> {
    throw new Error(
      'Demo mode cannot generate study material: it is made by a model reading a real ' +
        'transcript, and demo mode has no API key. The summary, flashcards and questions on this ' +
        'screen are sample data, carrying the same provenance labels a generated set would.',
    )
  },

  async getTasks(filter?: TaskFilter) {
    return clone(
      tasks
        .filter((t) => (filter?.done === undefined ? true : t.done === filter.done))
        .filter((t) => (filter?.list ? t.list === filter.list : true))
        .filter((t) => (filter?.dueBefore ? t.due !== null && t.due <= filter.dueBefore : true))
        .sort((a, b) => {
          if (a.due === b.due) return a.title < b.title ? -1 : 1
          if (a.due === null) return 1
          if (b.due === null) return -1
          return a.due < b.due ? -1 : 1
        }),
    )
  },

  async createTask(input: TaskInput) {
    // Nothing typed in was proposed by the chat. `acceptChatProposal` is the one path that is.
    return clone(addTask(input, null))
  },

  async toggleTask(id: string, done: boolean) {
    tasks = tasks.map((t) =>
      t.id === id
        ? { ...t, done, completedAt: done ? `${SEED_TODAY}T12:00:00-07:00` : null }
        : t,
    )
    const found = tasks.find((t) => t.id === id)
    if (!found) throw new Error(`demo: no task ${id}`)
    return clone(found)
  },

  /**
   * The manual edit the row menu makes: a due date set or cleared, a list changed.
   *
   * ABSENT AND NULL MEAN DIFFERENT THINGS, exactly as they do on the Worker's PATCH — `due:
   * null` clears the date, an omitted `due` leaves it alone. `in` rather than `!== undefined`
   * would also be correct here; `!== undefined` is what remote.ts uses, so both sides of the
   * contract read the same.
   */
  async updateTask(id: string, patch: TaskPatch) {
    tasks = tasks.map((t) =>
      t.id === id
        ? {
            ...t,
            title: patch.title !== undefined ? patch.title : t.title,
            due: patch.due !== undefined ? patch.due : t.due,
            list: patch.list !== undefined ? patch.list : t.list,
            reminderAt: patch.reminderAt !== undefined ? patch.reminderAt : t.reminderAt,
            estimateMin: patch.estimateMin !== undefined ? patch.estimateMin : t.estimateMin,
            rrule: patch.rrule !== undefined ? patch.rrule : t.rrule,
          }
        : t,
    )
    const found = tasks.find((t) => t.id === id)
    if (!found) throw new Error(`demo: no task ${id}`)
    return clone(found)
  },

  /** Really removes it. Demo mode's dataset is mutable, so this is a real delete, not a no-op. */
  async deleteTask(id: string) {
    const found = tasks.find((t) => t.id === id)
    if (!found) throw new Error(`demo: no task ${id}`)
    tasks = tasks.filter((t) => t.id !== id)
    return clone(found)
  },

  /**
   * REMINDERS ARE THE AMBIENT TASKS — the ones the Today card labels `NO DEADLINE · AMBIENT`.
   * A task qualifies if it carries a time-of-day nudge (`reminderAt`) or no date at all
   * (someday). School deadlines are not reminders; they are the Due soon card's business.
   *
   * A reminder finished in the last week stays in the list, carrying its `completedAt`, so the
   * card can render it struck through with a "done Sat" stamp. Dropping it the instant it is
   * ticked is what makes a list feel like it is hiding things from you.
   *
   * No limit here on purpose: this is the API, and the API answers completely. The card decides
   * how many rows fit.
   */
  async getReminders() {
    const isAmbient = (t: Task) => t.reminderAt !== null || t.due === null
    const recentlyDone = (t: Task) =>
      t.completedAt !== null && daysBetween(t.completedAt.slice(0, 10) as ISODate, SEED_TODAY) <= 7
    return clone(
      tasks
        .filter((t) => isAmbient(t) && (!t.done || recentlyDone(t)))
        .sort((a, b) => {
          if (a.done !== b.done) return a.done ? 1 : -1 // open first, always
          if (a.due === b.due) return a.title < b.title ? -1 : 1
          if (a.due === null) return 1 // dated before someday
          if (b.due === null) return -1
          return a.due < b.due ? -1 : 1
        }),
    )
  },

  // ── calendar ───────────────────────────────────────────────────────────────
  //
  // Three layers, one read (see the CALENDAR seeds block above for who owns each). The blocks
  // come back in time order because that is the only order a grid can be assembled in without
  // the view re-sorting — and two implementations of the contract that sort differently is how
  // a screen and an API start disagreeing about what "the first thing today" is.

  async getCalendarRange(range: DateRange): Promise<CalendarRange> {
    const blocks: CalendarBlock[] = []
    for (let d = range.from; d <= range.to; d = addDays(d, 1)) blocks.push(...canvasBlocksOn(d))
    blocks.push(...calendarBlocks.filter((b) => b.date >= range.from && b.date <= range.to))
    // Timed Google events ride in `blocks`, exactly as they do on the wire — a mirrored event
    // with a start and an end is the same kind of thing as a block, and `editable: false` is the
    // only difference that matters.
    blocks.push(...GOOGLE_BLOCKS.filter((b) => b.date >= range.from && b.date <= range.to))
    blocks.sort((a, b) =>
      a.date === b.date ? a.startMinutes - b.startMinutes : a.date < b.date ? -1 : 1,
    )
    return clone({
      from: range.from,
      to: range.to,
      blocks,
      dueItems: dueItemsBetween(range.from, range.to),
      // Overlap, not start-within: a multi-day event belongs to every range it touches.
      allDayEvents: GOOGLE_ALL_DAY.filter((e) => e.date <= range.to && e.endDate >= range.from),
      // The same rolling window the real connector mirrors (30 back, 180 forward from the last
      // sync), so the sources list's "outside the synced range" note is reachable in demo too.
      googleWindow: { from: addDays(SEED_TODAY, -30), to: addDays(SEED_TODAY, 180) },
    })
  },

  async createCalendarBlock(input: CalendarBlockInput) {
    // Nothing typed in was proposed by the chat, so the link back is null. Accepting a proposal
    // is the one path that sets it — see `acceptChatProposal`.
    return clone(addCalendarBlock(input, null))
  },

  async updateCalendarBlock(id: string, patch: CalendarBlockPatch) {
    // A Canvas block has no id in this list, so this is where the read-only rule stops being a
    // UI convention: even a caller that constructs the request by hand cannot move a lecture.
    if (id.startsWith('class-')) throw new ReadOnlyBlockError(id)
    const existing = calendarBlocks.find((b) => b.id === id)
    if (!existing) throw new Error(`demo: no calendar block ${id}`)
    const startsAt = patch.startsAt ?? existing.startsAt
    const endsAt = patch.endsAt ?? existing.endsAt
    const minutesOf = (ts: string) => Number(ts.slice(11, 13)) * 60 + Number(ts.slice(14, 16))
    const next: CalendarBlock = {
      ...existing,
      title: patch.title ?? existing.title,
      source: patch.source ?? existing.source,
      state: patch.state ?? existing.state,
      startsAt,
      endsAt,
      date: startsAt.slice(0, 10) as ISODate,
      startMinutes: minutesOf(startsAt),
      endMinutes: minutesOf(endsAt),
      location: patch.location !== undefined ? patch.location : existing.location,
      taskId: patch.taskId !== undefined ? patch.taskId : existing.taskId,
      // REPLACED, NEVER MERGED — the Worker's own contract. An absent key leaves them alone, so a
      // drag that moves a block cannot silently drop the reminder on it; `[]` clears them, which
      // is how the picker removes the last one.
      reminders: patch.reminders !== undefined ? patch.reminders : (existing.reminders ?? []),
    }
    calendarBlocks = calendarBlocks.map((b) => (b.id === id ? next : b))
    return clone(next)
  },

  async deleteCalendarBlock(id: string) {
    if (id.startsWith('class-')) throw new ReadOnlyBlockError(id)
    const existing = calendarBlocks.find((b) => b.id === id)
    if (!existing) throw new Error(`demo: no calendar block ${id}`)
    calendarBlocks = calendarBlocks.filter((b) => b.id !== id)
    return clone(existing)
  },

  async getLearningTracks() {
    return clone(PLANS.map(trackOf))
  },

  async getLearningItems(date: ISODate) {
    // Only the tracks whose plan actually covers that day. A track that had not started yet does
    // not get an empty row standing in for one — it has nothing to say about that date.
    return PLANS.map((p) => itemOnDate(p, date)).filter((i) => i !== null)
  },

  async toggleLearningItem(id: string, done: boolean, session?: LearningSessionInput) {
    const plan = PLANS.find((p) => id.startsWith(`item-${p.id}-`))
    const item = plan && PLAN_ITEMS[plan.id]!.find((i) => i.id === id)
    if (!plan || !item) throw new Error(`No learning item ${id}.`)
    if (done) {
      // Checked off TODAY, not on the day the plan wanted it: the completion date is a fact about
      // when the work happened, and back-dating it to the plan would be the app inventing history.
      //
      // The MINUTES the workspace supplied win over the seeded ones, and calling again with only a
      // note keeps whatever duration is already there — the same idempotent-on-(owner, item) write
      // the Worker does, so a check followed by "log 40 minutes" is one session in both modes.
      const existing = doneItems.get(id)
      doneItems.set(id, {
        date: existing?.date ?? SEED_TODAY,
        minutes:
          session?.minutes ??
          existing?.minutes ??
          18 + Math.round(seededUnit(`min:${plan.id}:${item.index}`) * 42),
        note: session?.note ?? existing?.note ?? null,
      })
    } else {
      doneItems.delete(id)
    }
    return toLearningItem(plan, item)
  },

  async getLearningStreak() {
    return clone(computeStreak(doneItems))
  },

  // ── learning · the two Learning screens (9a/9b) ───────────────────────────

  async getLearningTrackSummaries() {
    return clone(PLANS.map(summaryOf))
  },

  async getLearningTrackDetail(trackId: string) {
    const plan = planOf(trackId)
    return plan ? clone(detailOf(plan)) : null
  },

  async getLearningResources() {
    return clone(PLANS.map(libraryOf))
  },

  // THE SAME PAGE THE WORKER SERVES, off demo data — one pure formatter in @app/shared, handed
  // the same derived shapes (`detailOf`, `computeStreak`) the two Learning screens already read.
  // Demo mode must keep working (CLAUDE.md → Established patterns), and a "Copy for AI" button
  // that produced nothing with zero config would be the app pretending the feature exists.
  //
  async exportLearning() {
    return formatLearningExport({
      generatedDate: SEED_TODAY,
      tracks: PLANS.map(detailOf),
      streak: computeStreak(doneItems),
    })
  },

  async getInterestSaves(filter?: SaveFilter) {
    let rows = SAVES.filter((s) => (filter?.topic ? s.topic === filter.topic : true))
      .filter((s) => (filter?.unread === undefined ? true : s.unread === filter.unread))
      .sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1))
    if (filter?.limit) rows = rows.slice(0, filter.limit)
    return clone(rows)
  },

  async getInterestTopics() {
    return clone(buildTopics(SAVES))
  },

  async getInterestsOverview(): Promise<InterestsOverview> {
    return {
      totalSaves: SAVES.length,
      // Arrived and not dealt with: unfiled AND unread. A merely-unread save that the nightly
      // job has already placed is in a cluster, and a cluster is not an inbox.
      inboxCount: SAVES.filter((s) => s.topic === null && s.unread).length,
      backlogImport: { source: 'Reddit', items: REDDIT_BACKLOG_ITEMS, done: true },
    }
  },

  async getInterestSave(id: string): Promise<InterestSaveDetail | null> {
    const save = SAVES_BY_ID.get(id)
    if (!save) return null
    return clone({ save, related: relatedSaves(save) })
  },

  // ── connections ───────────────────────────────────────────────────────────

  async getConnectors(scenario: ConnectionsScenario = DEFAULT_SCENARIO) {
    return clone(withDemoGmail(CONNECTIONS[scenario].connectors))
  },

  async getDesktopWorker(scenario: ConnectionsScenario = DEFAULT_SCENARIO) {
    return clone(CONNECTIONS[scenario].worker)
  },

  async getCaptureTokens(scenario: ConnectionsScenario = DEFAULT_SCENARIO) {
    return clone(CONNECTIONS[scenario].tokens)
  },

  // A made-up token would be worse than none: the phone would store it and every upload would
  // fail against a server that never issued it. So demo mode refuses, in words.
  async createCaptureToken(): Promise<CreatedCaptureToken> {
    throw new Error(
      'Demo mode has no server, so there is no token a phone could upload with. Point the app at a ' +
        'real Worker deployment (VITE_API_BASE_URL) and this mints one for real.',
    )
  },

  async getDataStatus(scenario: ConnectionsScenario = DEFAULT_SCENARIO) {
    return clone(CONNECTIONS[scenario].data)
  },

  // Every connector on this screen is a fixture, so there is no real cloud behind any of them for
  // a sync to reach. The refusal is IN WORDS rather than a silent no-op — the same rule as every
  // other demo write that has no honest equivalent (README §State Management → hidden ≠ missing).
  async syncConnector(id: string): Promise<SyncResult> {
    throw new Error(
      `Syncing “${id}” on demand would mean reaching a real cloud account, and demo mode has none ` +
        `— every connector here is a fixture, not a live connection. Point the app at a real ` +
        `Supabase project and Worker deployment (VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY, ` +
        `VITE_API_BASE_URL) and this syncs Canvas for real.`,
    )
  },

  // ── search (10a/10b) ──────────────────────────────────────────────────────
  async search(query: string): Promise<SearchResults> {
    return clone(runSearch(query))
  },

  // ── chat (11a/11b) and capture (11c) ──────────────────────────────────────

  async getChatThreads(): Promise<ChatThread[]> {
    return clone(
      CHAT_THREAD_SEEDS.map(threadOf).sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)),
    )
  },

  async getChatThread(threadId: string): Promise<ChatThreadDetail | null> {
    const seed = CHAT_THREAD_SEEDS.find((t) => t.id === threadId)
    if (!seed) return null
    return clone({ thread: threadOf(seed), messages: messagesOf(seed) })
  },

  async getChatScopes(): Promise<ChatScope[]> {
    return clone(allScopes())
  },

  async getChatStarters(): Promise<ChatStarter[]> {
    return clone(chatStarters())
  },

  /**
   * ASKING, IN DEMO MODE, REFUSES — and that is the same refusal the six unwritten threads make.
   *
   * There is no model here and there is no key here, so the only way to return an answer would be
   * to write one. An answer on this screen has to carry the entries and transcripts it read; a
   * written one would carry citations a person chose to make it look right, which is precisely the
   * fabricated knowledge this whole screen exists to refuse (see CHAT_THREAD_SEEDS above, and
   * CLAUDE.md → Product identity rules).
   *
   * So it throws a sentence the composer prints. Everything else on the screen stays real: the
   * threads, the scopes and their derived sizes, the exemplar conversation with every source card
   * resolving to a row that exists.
   */
  async sendChatMessage(): Promise<never> {
    throw new Error(
      'Demo mode cannot answer. There is no model behind this dataset, and writing an answer by ' +
        'hand would mean writing the entries and transcripts it claims to have read — the one ' +
        'thing this screen exists to not do. Sign in to ask for real; the scope you set and the ' +
        'conversation above are both real either way.',
    )
  },

  /**
   * DELETING, UNLIKE ASKING, IS REAL IN DEMO MODE — and it has to be. Removing a thread invents
   * nothing: it takes a row out of a dataset the demo already owns. A refusal here would leave the
   * one destructive act on this screen untestable and unreachable without a login, which is how a
   * confirm step ships without anyone ever having pressed it.
   *
   * The seeds are spliced in place rather than reassigned so `__chatInternals.CHAT_THREAD_SEEDS`
   * keeps pointing at the list the API actually answers from.
   */
  async deleteChatThread(threadId: string): Promise<ChatThread> {
    const index = CHAT_THREAD_SEEDS.findIndex((t) => t.id === threadId)
    if (index === -1) throw new Error(`demo: no chat thread ${threadId}`)
    const [seed] = CHAT_THREAD_SEEDS.splice(index, 1)
    return clone(threadOf(seed!))
  },

  /**
   * SENDING A PICTURE IS REAL IN DEMO MODE, and ASKING about it still is not.
   *
   * The picture is kept as an object URL in this tab — nothing is uploaded, because there is
   * nowhere to upload to — so the composer's thumbnail, its remove button and the four-image
   * ceiling are all exercisable with no login. `sendChatMessage` then refuses for the reason it
   * always has: there is no model here to look at it.
   */
  async uploadChatAttachment(file: Blob): Promise<ChatAttachment> {
    const known: readonly string[] = ['image/png', 'image/jpeg', 'image/webp']
    if (!known.includes(file.type)) {
      throw new Error(
        `That is not a picture this can take (${file.type || 'no type'}). PNG, JPEG or WebP — ` +
          'which is what a paste, a drop or a file picker produces.',
      )
    }
    const id = `chat-attachment-demo-${++createdAttachments}`
    chatAttachmentUrls.set(id, URL.createObjectURL(file))
    return { id, mime: file.type as ChatAttachmentMime, bytes: file.size }
  },

  async getChatAttachmentUrl(attachmentId: string): Promise<string> {
    const url = chatAttachmentUrls.get(attachmentId)
    if (!url) {
      throw new Error(
        'That picture is not in this tab any more. Demo mode keeps an attachment in memory only, ' +
          'so a reload loses it — nothing was uploaded anywhere.',
      )
    }
    return url
  },

  /**
   * ACCEPTING IS REAL, and it has to be — for the same reason deleting a thread is.
   *
   * It writes through `addCalendarBlock` / `addTask`, the same two functions the Calendar and
   * Reminders screens write with, so the block that appears is a block in every way the rest of
   * this fixture can tell. Nothing is invented: the title, the time and the list all come off
   * the proposal, which came off a source the answer cites.
   *
   * ACCEPTING TWICE REFUSES, in the same words the Worker uses. A double-tap that quietly made a
   * second block is the bug this whole path is shaped to prevent, and a demo that could not
   * reproduce it would be the wrong place to find that out.
   */
  async acceptChatProposal(proposalId: string): Promise<ChatProposalDecision> {
    const found = findSeededProposal(proposalId)
    if (!found) throw new Error(`demo: no chat proposal ${proposalId}`)
    const decided = chatProposalDecisions.get(proposalId)
    if (decided) {
      throw new Error(
        decided.status === 'accepted'
          ? 'You already accepted this one, and it was added. Accepting again would make a second copy.'
          : 'You already said no to this one. Nothing was created and nothing will be.',
      )
    }

    const { payload } = found
    let created: ChatProposalResult
    if (payload.kind === 'calendar_block') {
      const block = addCalendarBlock(
        {
          title: payload.title,
          // The LANE the proposal named. "It came from chat" is `proposalId`, not a lane.
          source: payload.lane,
          startsAt: payload.startsAt,
          endsAt: payload.endsAt,
          location: payload.location,
          state: 'accepted',
        },
        proposalId,
      )
      created = {
        kind: 'calendar_block',
        id: block.id,
        title: block.title,
        href: `/calendar?date=${block.date}&view=day`,
        whenLabel: demoWhenLabel(block.startsAt),
      }
    } else {
      const task = addTask(
        {
          title: payload.title,
          due: payload.due as ISODate | null,
          reminderAt: payload.reminderAt,
          list: payload.list ?? undefined,
          rrule: payload.rrule ?? null,
        },
        proposalId,
      )
      created = {
        kind: 'task',
        id: task.id,
        title: task.title,
        href: '/tasks',
        whenLabel: task.reminderAt ? demoWhenLabel(task.reminderAt) : null,
      }
    }

    chatProposalDecisions.set(proposalId, {
      status: 'accepted',
      resultId: created.id,
      decidedAt: SEED_NOW,
    })
    const proposal = findSeededProposal(proposalId)!.message.proposals.find((p) => p.id === proposalId)!
    return clone({ proposal, created })
  },

  /** Saying no writes nothing, which is the whole reason a proposal is a proposal. */
  async rejectChatProposal(proposalId: string): Promise<ChatProposal> {
    const found = findSeededProposal(proposalId)
    if (!found) throw new Error(`demo: no chat proposal ${proposalId}`)
    const decided = chatProposalDecisions.get(proposalId)
    if (decided?.status === 'accepted') {
      throw new Error(
        'You accepted this one, so it exists on your calendar. Delete it there — saying no here ' +
          'would not remove it, and the card would stop matching what is real.',
      )
    }
    chatProposalDecisions.set(proposalId, { status: 'rejected', resultId: null, decidedAt: SEED_NOW })
    return clone(findSeededProposal(proposalId)!.message.proposals.find((p) => p.id === proposalId)!)
  },

  async getClipPreview(url: string): Promise<ClipPreview | null> {
    if (!looksLikeUrl(url)) return null
    const { sourceDetail } = clipSource(url)
    return {
      url: url.trim(),
      // Null until something has actually read the page. Never derived from the slug.
      title: CLIP_PREVIEWS[clipKey(url)] ?? null,
      sourceDetail,
      filesUnder: 'Interests',
    }
  },

  async captureQuick(input: CaptureInput): Promise<CaptureResult> {
    if (!input.text.trim()) throw new Error('Nothing to capture — the field was empty.')
    return captureInDemo(input)
  },

  // ── notifications · Web Push (NOTIF-1b) ───────────────────────────────────

  async getPushPublicKey() {
    return DEMO_VAPID_PUBLIC_KEY
  },

  async getPushSubscriptions() {
    return clone(pushDevices)
  },

  async subscribeToPush(input: PushSubscribeInput) {
    return clone(addPushDevice(input))
  },

  async unsubscribeFromPush(id: string) {
    const existing = pushDevices.find((d) => d.id === id)
    if (!existing) throw new Error(`demo: no push subscription ${id}`)
    pushDevices = pushDevices.filter((d) => d.id !== id)
    return clone(existing)
  },

  async sendTestNotification(): Promise<PushTestResult> {
    return clone(sendDemoTest())
  },

  async getNotifications(filter?: NotificationFilter) {
    let rows = notificationLog
    if (filter?.status) rows = rows.filter((n) => n.status === filter.status)
    if (filter?.since) rows = rows.filter((n) => n.createdAt >= filter.since!)
    // Newest first, exactly as the API answers — the screen does not sort what it is given.
    return clone(rows.slice(0, filter?.limit ?? rows.length))
  },

  async getNotificationSettings() {
    return { ...clone(notificationSettings), ...demoHeartbeat() }
  },

  async updateNotificationSettings(patch: NotificationSettingsPatch) {
    // MERGED KEY BY KEY, like the Worker's PUT: turning Google events off must not take class
    // reminders with it, and a demo that overwrote the object would hide that bug rather than
    // reproduce it.
    notificationSettings = {
      classLeadMinutes: patch.classLeadMinutes ?? notificationSettings.classLeadMinutes,
      gcalLeadMinutes: patch.gcalLeadMinutes ?? notificationSettings.gcalLeadMinutes,
      kindsEnabled: { ...notificationSettings.kindsEnabled, ...(patch.kindsEnabled ?? {}) },
      dayDigest: patch.dayDigest ? { ...patch.dayDigest } : notificationSettings.dayDigest,
    }
    // The Worker's PUT answers with the heartbeat too, so the cache it replaces keeps its line.
    return { ...clone(notificationSettings), ...demoHeartbeat() }
  },

  // ── mail · Google accounts for read-only Gmail (Day page, Lane 1a) ────────

  async listMailAccounts(): Promise<MailAccount[]> {
    // The count is DERIVED from the seeded messages, the way the Worker counts `mail_messages`.
    return clone(mailAccounts.map((a) => ({ ...a, messageCount: demoMessagesOf(a.id).length })))
  },

  // THERE IS NO GOOGLE BEHIND A FIXTURE, so "connecting" adds the next demo address and answers an
  // IN-APP path carrying the same `?mail=` result the Worker's callback would — the Gmail card
  // treats a path as a navigation rather than a page load, which is what keeps the new account in
  // this in-memory list. A fourth answers the Worker's own refusal.
  async startGoogleMailConnect(): Promise<string> {
    if (mailAccounts.length >= DEMO_MAX_MAIL_ACCOUNTS) {
      return '/settings/connections?mail=error&reason=too_many_accounts'
    }
    const account = addDemoMailAccount()
    return `/settings/connections?mail=connected&account=${encodeURIComponent(account.address)}`
  },

  async disconnectMailAccount(id: string): Promise<MailDisconnectResult> {
    if (!mailAccounts.some((a) => a.id === id)) {
      throw new Error('No connected Google account with that id, for this owner.')
    }
    mailAccounts = mailAccounts.filter((a) => a.id !== id)
    // The Worker's `on delete cascade`: the account's mail goes with it.
    demoMailMessages = demoMailMessages.filter((m) => m.accountId !== id)
    // `revoked: false` and not true: nothing in demo mode can confirm anything with Google.
    return { deleted: true, revoked: false }
  },

  // Lane 1b. The same newest-first order, filters and keyset cursor as GET /api/mail/messages.
  async listMailMessages(filter: MailMessageFilter = {}): Promise<MailMessagePage> {
    return clone(pageDemoMessages(filter))
  },

  // ── mail · triage, grading and rules (Day page prototype) ─────────────────
  // In memory and interactive: every write changes what the next `getMailTriage` answers.

  async getMailTriage(query?: MailTriageQuery): Promise<MailTriage> {
    // THE LOADING BOARD (7): a read that never answers, so every section stays a skeleton.
    if (query?.scenario === 'loading') return new Promise<MailTriage>(() => {})
    return clone(demoTriage(query))
  },

  async getMailAudit(weekStart: ISODate, scenario?: DayScenario): Promise<MailAudit> {
    if (scenario === 'loading') return new Promise<MailAudit>(() => {})
    return clone(demoAudit(weekStart, scenario))
  },

  async setMailVerdict(id: string, verdict: MailVerdict, reason?: MailCorrectionReason): Promise<MailTriageItem> {
    const item = demoTriageItem(id)
    item.ownerVerdict = verdict
    item.verdict = verdict
    if (reason) demoMailFeedback.push({ id, verdict, reason })
    return clone(item)
  },

  async setMailState(id: string, state: MailItemState, snoozedUntil?: string): Promise<MailTriageItem> {
    const item = demoTriageItem(id)
    if (state === 'snoozed' && !snoozedUntil) throw new Error('A snooze needs a time to come back.')
    item.state = state
    item.snoozedUntil = state === 'snoozed' ? snoozedUntil! : null
    // "Handled today" is done + dismissed, stamped at the seeded clock (never the wall clock).
    if (state === 'done' || state === 'dismissed') demoHandledAt.set(id, DEMO_NOW)
    else demoHandledAt.delete(id)
    return clone(item)
  },

  async listMailRules(): Promise<MailRule[]> {
    return clone(demoMailRules.map((r) => ({ ...r, messageCountOpen: demoRuleOpenCount(r) })))
  },

  async createMailRule(input: MailRuleInput): Promise<void> {
    const value = input.matchValue.trim().toLowerCase()
    if (!value) throw new Error('A rule needs an address or a domain.')
    demoMailRules = [
      ...demoMailRules.filter((r) => !(r.matchType === input.matchType && r.matchValue === value)),
      {
        id: `mail-rule-demo-${++demoMailRuleSeq}`,
        matchType: input.matchType,
        matchValue: value,
        verdict: input.verdict,
        createdAt: calTimestamp(SEED_TODAY, SEED_CLOCK_MINUTES),
      },
    ]
    applyDemoRules()
  },

  async deleteMailRule(id: string): Promise<void> {
    if (!demoMailRules.some((r) => r.id === id)) throw new Error('No mail rule with that id.')
    demoMailRules = demoMailRules.filter((r) => r.id !== id)
  },

  // ── mail · next steps (Lane 6) ──
  // The Worker's three decisions, on the same in-memory items the triage reads. An accept writes
  // through the fixture's ONE add path (`addCalendarBlock` / `addTask`), so the block or reminder
  // shows on the Calendar and Reminders screens like any other; undo deletes exactly that row.

  async acceptMailProposal(id: string): Promise<MailProposalDecision> {
    const { item, proposal } = demoProposal(id)
    if (proposal.status !== 'proposed') throw new Error(demoProposalRefusal(proposal))
    const p = proposal.payload
    if (proposal.kind === 'calendar_block' && 'startAt' in p) {
      const block = addCalendarBlock(
        { title: p.title, source: 'personal', startsAt: p.startAt, endsAt: p.endAt, location: p.location ?? null, state: 'accepted' },
        null,
      )
      proposal.result = { kind: 'calendar_block', id: block.id }
    } else if ((proposal.kind === 'reminder' || proposal.kind === 'waiting_on') && 'dueDate' in p) {
      const task = addTask({ title: p.title, due: p.dueDate as ISODate }, null)
      proposal.result = { kind: 'task', id: task.id }
    } else if (proposal.kind === 'mark_done') {
      item.state = 'done'
      item.snoozedUntil = null
      demoHandledAt.set(item.id, DEMO_NOW)
    }
    proposal.status = 'accepted'
    proposal.acceptedAt = DEMO_NOW
    return clone({ proposal, item })
  },

  async skipMailProposal(id: string): Promise<MailProposalDecision> {
    const { item, proposal } = demoProposal(id)
    if (proposal.status !== 'proposed') throw new Error(demoProposalRefusal(proposal))
    proposal.status = 'skipped'
    proposal.skippedAt = DEMO_NOW
    return clone({ proposal, item })
  },

  async undoMailProposal(id: string): Promise<MailProposalDecision> {
    const { item, proposal } = demoProposal(id)
    if (proposal.status !== 'accepted') throw new Error('Only an accepted next step can be undone.')
    if (proposal.result?.kind === 'calendar_block') {
      const blockId = proposal.result.id
      calendarBlocks = calendarBlocks.filter((b) => b.id !== blockId)
    } else if (proposal.result?.kind === 'task') {
      const taskId = proposal.result.id
      tasks = tasks.filter((t) => t.id !== taskId)
    } else if (proposal.kind === 'mark_done') {
      item.state = 'open'
      demoHandledAt.delete(item.id)
    }
    proposal.status = 'proposed'
    proposal.acceptedAt = null
    proposal.result = null
    return clone({ proposal, item })
  },

  async runMailTriage(limit = 50): Promise<MailTriageRunResult> {
    const waiting = demoTriageItems.filter((i) => i.verdict === null && i.state === 'open')
    const batch = waiting.slice(0, limit)
    for (const item of batch) {
      const would = DEMO_WOULD_SORT_AS[item.id] ?? 'can_wait'
      item.verdict = would
      item.modelVerdict = would
      item.reason = DEMO_WOULD_SORT_REASON[item.id] ?? null
      item.triagedAt = calTimestamp(SEED_TODAY, SEED_CLOCK_MINUTES)
      item.triageModel = DEMO_TRIAGE_MODEL
      item.triageSource = 'model'
    }
    return { sorted: batch.length, failed: 0, remaining: waiting.length - batch.length }
  },
}

// ════════════════════════════════════════════════════════════════════════════
// NOTIFICATIONS, IN DEMO — appended as its own block, the convention this file keeps.
//
// WHAT DEMO MODE CAN HONESTLY BE HERE. There is no push service on the other end of a fixture, so
// the one thing this cannot do is deliver a banner. Everything up to that point is real: the
// settings merge the way the Worker's PUT merges, subscribing writes a row, unsubscribing removes
// it, and the log is the same shape `GET /api/notifications` answers.
//
// THE LOG STARTS EMPTY, AND THAT IS THE POINT. Seeding it would be inventing a history of
// notifications LifeOS never sent — and the screen's "nothing yet" state is the state a real
// owner sees on day one, so it is the one worth having in front of anyone reviewing the design.
// Pressing "Send a test notification" populates it, which is also how the real thing works.
//
// DETERMINISTIC, like everything else in this file: every timestamp is derived from SEED_TODAY
// and a counter, never from the wall clock.
// ════════════════════════════════════════════════════════════════════════════

/**
 * A key-shaped string, and NOT a real VAPID key. 87 base64url characters is the length of an
 * uncompressed P-256 point, so anything that validates the shape is satisfied; nothing in demo
 * mode can encrypt to it, because there is nothing in demo mode that sends.
 */
const DEMO_VAPID_PUBLIC_KEY = `BDemoKeyForTheFixtureOnly${'0'.repeat(62)}`

/** One subscribed browser, so the device list renders as a list rather than as an empty state. */
let pushDevices: PushDevice[] = [
  {
    id: 'push-demo-s24',
    endpoint: 'https://fcm.googleapis.com/fcm/send/demo-galaxy-s24',
    label: 'Galaxy S24 · Chrome',
    userAgent: 'Mozilla/5.0 (Linux; Android 15; SM-S921B) Chrome/141.0.0.0 Mobile Safari/537.36',
    createdAt: calTimestamp(addDays(SEED_TODAY, -9), 19 * 60 + 12),
    // Null, not a date: nothing has ever been delivered in demo mode, and a fabricated "last
    // success" would be the fixture claiming a push the app never made.
    lastSuccessAt: null,
    disabledAt: null,
    disabledReason: null,
  },
]

let createdPushDevices = 0

function addPushDevice(input: PushSubscribeInput): PushDevice {
  const existing = pushDevices.find((d) => d.endpoint === input.endpoint)
  if (existing) {
    // Idempotent on the endpoint, and re-subscribing RE-ENABLES — the Worker's rule, kept here so
    // the screen's "press Enable again" copy is true in both modes.
    const next: PushDevice = {
      ...existing,
      label: input.label ?? existing.label,
      userAgent: input.userAgent ?? existing.userAgent,
      disabledAt: null,
      disabledReason: null,
    }
    pushDevices = pushDevices.map((d) => (d.id === existing.id ? next : d))
    return next
  }
  const device: PushDevice = {
    id: `push-demo-new-${++createdPushDevices}`,
    endpoint: input.endpoint,
    label: input.label ?? null,
    userAgent: input.userAgent ?? null,
    createdAt: calTimestamp(SEED_TODAY, SEED_CLOCK_MINUTES),
    lastSuccessAt: null,
    disabledAt: null,
    disabledReason: null,
  }
  pushDevices = [...pushDevices, device]
  return device
}

let notificationSettings: NotificationSettings = {
  // The Worker's own defaults (`DEFAULT_SETTINGS`), not a second opinion about them.
  classLeadMinutes: 10,
  gcalLeadMinutes: 30,
  kindsEnabled: {
    task_reminder: true,
    calendar_block: true,
    gcal_event: true,
    class_start: true,
  },
  // The Day page's one daily notification: 1:00 PM, on (docs/design/handoff/day-page/README.md).
  dayDigest: { enabled: true, hour: 13, minute: 0 },
}

/**
 * THE HEARTBEAT, IN DEMO: a checker that ran 40 seconds before the seeded clock and found nothing
 * to send — which is what a healthy minute looks like. Seeded, never the wall clock (the
 * determinism rule demo.test.ts enforces): the screen measures the age against `getClock()`, so
 * the seeded 8:41 AM minus 40 s reads as fresh on every render of every run.
 */
function demoHeartbeat(): Pick<NotificationSettings, 'schedulerLastRunAt' | 'schedulerLastResult'> {
  const minute = SEED_CLOCK_MINUTES - 1
  const hh = String(Math.floor(minute / 60)).padStart(2, '0')
  const mm = String(minute % 60).padStart(2, '0')
  return {
    schedulerLastRunAt: `${SEED_TODAY}T${hh}:${mm}:20${CAL_OFFSET}`,
    // 37 is what an empty minute really leaves: 45 budgeted, minus the settings read, the five
    // parallel reads, the carried-rows read and the heartbeat itself (lib/notifications.ts).
    schedulerLastResult: { considered: 0, sent: 0, failed: 0, skipped: 0, budgetLeft: 37 },
  }
}

/** Newest first, exactly as the API answers. Empty until something is sent. */
let notificationLog: NotificationRecord[] = []

let demoTestSends = 0

/** The sentence every demo send carries. It names the fixture, not a failure of the feature. */
const DEMO_NO_PUSH =
  'Demo mode has no push service to send to — every browser in this list is a fixture. Point the ' +
  'app at a real Supabase project and Worker deployment and this sends for real.'

function sendDemoTest(): PushTestResult {
  // A minute per press, off the seeded clock: two tests are two distinguishable rows, and no
  // reading of the wall clock is involved.
  const at = calTimestamp(SEED_TODAY, SEED_CLOCK_MINUTES + ++demoTestSends)
  const row: NotificationRecord = {
    id: `notif-demo-test-${demoTestSends}`,
    kind: 'test',
    sourceId: 'manual',
    fireAt: at,
    title: 'LifeOS test notification',
    body: 'If you can read this, notifications are working on this device.',
    url: '/',
    dedupeKey: `test:${at}`,
    // SKIPPED, NOT SENT, and not failed either: nothing refused it — there was nothing to
    // refuse it. `error` carries the reason in words, which is the same contract the Worker
    // keeps for a skipped row.
    status: 'skipped',
    sentAt: null,
    error: DEMO_NO_PUSH,
    attempts: 0,
    createdAt: at,
  }
  notificationLog = [row, ...notificationLog]
  return {
    notificationId: row.id,
    sent: 0,
    outcomes: pushDevices
      .filter((d) => !d.disabledAt)
      .map((d) => ({
        subscriptionId: d.id,
        label: d.label,
        ok: false,
        error: DEMO_NO_PUSH,
        disabled: false,
      })),
  }
}

// Exported for the unit tests — they assert the invariants of the dataset itself
// (weekday coherence, streak length, segment ordering), not just the API surface.
export const __demoInternals = {
  COURSES,
  LECTURES,
  TRANSCRIPTS,
  ASSIGNMENTS,
  ANNOUNCEMENTS,
  SYLLABUS_EXTRACTS,
  COURSE_FILES,
  COURSE_FILE_TEXT,
  LECTURE_SUMMARIES,
  FLASHCARDS,
  PRACTICE_QUESTIONS,
  TASK_SEEDS,
  VOICE_MEMOS,
  SAVES,
  JOURNAL_SEEDS,
  ACTIVITY_DAYS,
  LEARNING_GAPS,
  PLAN_START,
  PY_PLAN_START,
  PLAN_ITEMS,
  TERM,
  toDate,
}

/**
 * Chat and capture, exported for their own unit tests — appended rather than merged into
 * `__demoInternals` so the parallel screen ports stay out of each other's diffs.
 *
 * `resolveSource` is here because the test that matters most on this screen is the one that walks
 * every fixture citation and proves it resolves to a row that exists: an uncited (or miscited)
 * claim about the owner's life is a design bug, and this is the mechanical half of catching it.
 */
export const __chatInternals = {
  CHAT_THREAD_SEEDS,
  allScopes,
  resolveSource,
  clipSource,
  clipKey,
  looksLikeUrl,
  CLIP_PREVIEWS,
}

// ════════════════════════════════════════════════════════════════════════════
// MAIL, IN DEMO — appended as its own block, the convention this file keeps.
//
// TWO CONNECTED ACCOUNTS, on reserved `.example` domains so no fixture address can ever be a real
// person's inbox. Connecting adds a third; disconnecting removes one. The Gmail card is DERIVED
// from this list exactly as the Worker derives it from `mail_accounts` — so connecting in demo
// changes the card, and a card that disagreed with its own account list could not happen.
//
// THE TWO SEEDED ACCOUNTS HAVE BEEN SYNCED (Lane 1b): they carry a `lastSyncAt` and a dozen
// seeded messages between them, so the card's "last sync · N messages" lines and
// `listMailMessages` have something true-to-the-fixture to show. An account connected IN demo has
// never synced — `lastSyncAt` null, no messages — because demo mode has no Gmail to read.
// ════════════════════════════════════════════════════════════════════════════

const DEMO_MAX_MAIL_ACCOUNTS = 3

/** The addresses "Connect" hands out, in order, once the seeded two are taken. */
const DEMO_MAIL_ADDRESSES = ['kaz@school.example', 'kaz@personal.example', 'kaz@work.example']

/** When the seeded accounts last synced: the half hour before the seed clock. */
const DEMO_MAIL_SYNCED_AT = calTimestamp(SEED_TODAY, SEED_CLOCK_MINUTES - 11)

let mailAccounts: MailAccount[] = DEMO_MAIL_ADDRESSES.slice(0, 2).map((address, i) => ({
  id: `mail-demo-${i + 1}`,
  address,
  connectedAt: calTimestamp(addDays(SEED_TODAY, -2 + i), 20 * 60 + 5),
  lastSyncAt: DEMO_MAIL_SYNCED_AT,
  lastError: null,
  status: 'connected' as const,
  // Derived on read (listMailAccounts) from the seeded messages, never stored.
  messageCount: null,
}))

/** One seeded message: which account, how many days ago, at what minute, and who said what. */
interface DemoMailSeed {
  account: 1 | 2
  daysAgo: number
  minutes: number
  fromName: string | null
  from: string
  subject: string
  snippet: string
  labels: string[]
}

// Realistic school and personal mail, every address on a reserved `.example` domain.
const DEMO_MAIL_SEEDS: DemoMailSeed[] = [
  { account: 1, daysAgo: 0, minutes: 8 * 60 + 12, fromName: 'COGSCI C142 (bCourses)', from: 'notifications@bcourses.example', subject: 'Problem Set 3 is due Friday at 11:59 PM', snippet: 'A reminder that Problem Set 3 closes Friday night. Late submissions lose 10% per day.', labels: ['INBOX', 'UNREAD', 'CATEGORY_UPDATES'] },
  { account: 1, daysAgo: 0, minutes: 7 * 60 + 48, fromName: 'Prof. Ada Lindqvist', from: 'lindqvist@berkeley.example', subject: 'Office hours moved to Thursday this week', snippet: 'Hi all — I am travelling Wednesday, so office hours move to Thursday 2–4 PM in Dwinelle 3401.', labels: ['INBOX', 'UNREAD', 'IMPORTANT'] },
  { account: 2, daysAgo: 0, minutes: 7 * 60 + 2, fromName: 'Maya', from: 'maya@personal.example', subject: 'Dinner Saturday?', snippet: 'Are you free Saturday around 7? Thinking the ramen place on Shattuck.', labels: ['INBOX', 'UNREAD'] },
  { account: 1, daysAgo: 0, minutes: 6 * 60 + 30, fromName: 'Cal Student Billing', from: 'billing@calcentral.example', subject: 'Your tuition statement is ready', snippet: 'A new statement is available in CalCentral. Amount due by October 1.', labels: ['INBOX', 'UNREAD', 'IMPORTANT', 'CATEGORY_UPDATES'] },
  { account: 2, daysAgo: 1, minutes: 21 * 60 + 15, fromName: 'Bay Utilities', from: 'no-reply@utility.example', subject: 'Your September bill is $42.18', snippet: 'Your bill is ready. Autopay will charge your card on September 24.', labels: ['INBOX', 'CATEGORY_UPDATES'] },
  { account: 1, daysAgo: 1, minutes: 16 * 60 + 40, fromName: 'NEU C61 GSI', from: 'gsi.neuro@berkeley.example', subject: 'Section 104 room change', snippet: 'Starting this week section 104 meets in VLSB 2050 instead of 2040.', labels: ['INBOX'] },
  { account: 2, daysAgo: 1, minutes: 12 * 60 + 5, fromName: null, from: 'orders@bookshop.example', subject: 'Your order has shipped', snippet: 'Order #10442 (two paperbacks) is on its way. Expected Thursday.', labels: ['INBOX', 'CATEGORY_UPDATES'] },
  { account: 1, daysAgo: 2, minutes: 10 * 60 + 20, fromName: 'Transfer Student Center', from: 'tsc@berkeley.example', subject: 'Transfer mixer next Tuesday', snippet: 'Meet other transfer students over pizza, Tuesday 5 PM at the Transfer Center.', labels: ['INBOX', 'CATEGORY_PROMOTIONS'] },
  { account: 2, daysAgo: 2, minutes: 9 * 60, fromName: 'Mom', from: 'mom@family.example', subject: 'Photos from the weekend', snippet: 'Here are the pictures from Sunday — the one with the dog is the best.', labels: ['INBOX'] },
  { account: 1, daysAgo: 3, minutes: 15 * 60 + 55, fromName: 'Library Services', from: 'library@berkeley.example', subject: 'Your hold is ready for pickup', snippet: 'The item you requested is waiting at Moffitt Library until September 20.', labels: ['INBOX'] },
  { account: 2, daysAgo: 4, minutes: 18 * 60 + 30, fromName: 'Climbing Gym', from: 'hello@climb.example', subject: 'Your membership renews soon', snippet: 'Your monthly membership renews on September 20. No action needed.', labels: ['INBOX', 'CATEGORY_UPDATES'] },
  { account: 1, daysAgo: 5, minutes: 11 * 60 + 10, fromName: 'COGSCI 1 (bCourses)', from: 'notifications@bcourses.example', subject: 'New announcement: Midterm review session', snippet: 'A review session for the midterm will be held next Monday at 6 PM in Li Ka Shing 245.', labels: ['INBOX', 'CATEGORY_UPDATES'] },
]

let demoMailMessages: MailMessage[] = DEMO_MAIL_SEEDS.map((seed, i) => {
  const accountId = `mail-demo-${seed.account}`
  const address = DEMO_MAIL_ADDRESSES[seed.account - 1]!
  const externalId = `demo-gmail-${String(i + 1).padStart(3, '0')}`
  return {
    id: `mail-msg-demo-${String(i + 1).padStart(3, '0')}`,
    accountId,
    accountAddress: address,
    externalId,
    threadId: `${externalId}-thread`,
    from: { name: seed.fromName, address: seed.from },
    to: [address],
    subject: seed.subject,
    receivedAt: calTimestamp(addDays(SEED_TODAY, -seed.daysAgo), seed.minutes),
    snippet: seed.snippet,
    labelIds: seed.labels,
    isUnread: seed.labels.includes('UNREAD'),
    url: `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(address)}#all/${externalId}-thread`,
  }
})

/** Messages of one account that still exists (a disconnected account's mail went with it). */
function demoMessagesOf(accountId: string): MailMessage[] {
  return demoMailMessages.filter((m) => m.accountId === accountId)
}

/** Newest first, ties broken by id — the Worker's `received_at desc, id desc`. */
function demoMessageOrder(a: MailMessage, b: MailMessage): number {
  const at = Date.parse(b.receivedAt) - Date.parse(a.receivedAt)
  return at !== 0 ? at : a.id < b.id ? 1 : a.id > b.id ? -1 : 0
}

/** GET /api/mail/messages, over the fixture: filters, limit, and an opaque keyset cursor. */
function pageDemoMessages(filter: MailMessageFilter): MailMessagePage {
  const limit = Math.min(200, Math.max(1, filter.limit ?? 50))
  const live = new Set(mailAccounts.map((a) => a.id))
  let rows = demoMailMessages.filter((m) => live.has(m.accountId)).sort(demoMessageOrder)
  if (filter.account) rows = rows.filter((m) => m.accountId === filter.account)
  if (filter.since) {
    const since = Date.parse(filter.since)
    rows = rows.filter((m) => Date.parse(m.receivedAt) >= since)
  }
  if (filter.cursor) {
    let after: [string, string]
    try {
      after = JSON.parse(atob(filter.cursor)) as [string, string]
    } catch {
      throw new Error('That cursor is not one this API issued. Start from the first page.')
    }
    const pivot = { receivedAt: after[0], id: after[1] } as MailMessage
    rows = rows.filter((m) => demoMessageOrder(pivot, m) < 0)
  }
  const page = rows.slice(0, limit)
  const last = page[page.length - 1]
  return {
    messages: page,
    nextCursor: rows.length > limit && last ? btoa(JSON.stringify([last.receivedAt, last.id])) : null,
  }
}

let createdMailAccounts = 0

function addDemoMailAccount(): MailAccount {
  const address =
    DEMO_MAIL_ADDRESSES.find((a) => !mailAccounts.some((m) => m.address === a)) ??
    `kaz+${createdMailAccounts + 1}@extra.example`
  const account: MailAccount = {
    id: `mail-demo-new-${++createdMailAccounts}`,
    address,
    connectedAt: calTimestamp(SEED_TODAY, SEED_CLOCK_MINUTES),
    lastSyncAt: null,
    lastError: null,
    status: 'connected',
    messageCount: null,
  }
  mailAccounts = [...mailAccounts, account]
  return account
}

/** The Worker's `gmailConnector`, applied to the demo list. Same sentences, same states. */
function demoGmailConnector(): Connector {
  const connected = mailAccounts.length > 0
  const synced = mailAccounts
    .map((a) => a.lastSyncAt)
    .filter((t): t is string => Boolean(t))
    .sort((a, b) => Date.parse(a) - Date.parse(b))
  const lastSyncAt = synced.length > 0 ? synced[synced.length - 1]! : null
  const held = mailAccounts.reduce((n, a) => n + demoMessagesOf(a.id).length, 0)
  return {
    id: 'gmail',
    name: 'Gmail',
    shortName: 'Gmail',
    status: connected ? 'ok' : 'disconnected',
    scope: connected
      ? `${mailAccounts.map((a) => a.address).join(' · ')} · read-only`
      : 'No Google account connected yet',
    note: connected
      ? {
          tone: 'neutral',
          text:
            'Read-only: every 30 minutes LifeOS reads the sender, subject and a short snippet of new mail. ' +
            'It reads a message’s full text only to propose a next step and never stores it, and it can never send, delete or change mail.',
        }
      : {
          tone: 'neutral',
          text: 'Connect up to three Google accounts. Access is read-only: LifeOS can never send, delete or change mail.',
        },
    outage: null,
    lastSyncAt,
    // What the fixture's last sync would have brought in: the mail of the half hour before it.
    newItemsLastSync: lastSyncAt
      ? demoMailMessages.filter((m) => {
          const at = Date.parse(m.receivedAt)
          return at <= Date.parse(lastSyncAt) && at > Date.parse(lastSyncAt) - 30 * 60_000
        }).length
      : null,
    itemsPulledAllTime: connected ? held : null,
    retryAt: null,
    plannedFor: null,
  }
}

/** Gmail goes where the Worker puts it: after the built connectors, before the planned ones. */
function withDemoGmail(connectors: Connector[]): Connector[] {
  const at = connectors.findIndex((c) => c.id === 'gcal')
  const out = [...connectors]
  out.splice(at === -1 ? out.length : at + 1, 0, demoGmailConnector())
  return out
}

// ════════════════════════════════════════════════════════════════════════════
// MAIL TRIAGE, IN DEMO (Day page prototype) — appended as its own block.
//
// Sixteen open messages across the two seeded accounts, spread over all four piles plus one not
// sorted yet; two carried over from earlier days; one security alert; one sign-in code that has
// expired and is counted, never listed. Every write mutates this list, so the page works with
// zero config. The groups and counts are DERIVED on read, the way the Worker derives them.
// ════════════════════════════════════════════════════════════════════════════

const DEMO_TRIAGE_MODEL = 'claude-haiku-4-5'
const DEMO_NOW = calTimestamp(SEED_TODAY, SEED_CLOCK_MINUTES)

interface DemoTriageSeed {
  account: 1 | 2
  /** Minutes before the seed clock it arrived. */
  minutesAgo: number
  fromName: string | null
  from: string
  subject: string
  snippet: string
  verdict: MailVerdict | null
  modelVerdict?: MailVerdict | null
  ownerVerdict?: MailVerdict | null
  reason: string | null
  deadline?: ISODate | null
  action: MailTriageItem['action']
  security?: boolean
  isCode?: boolean
  ageDays?: number
  source?: 'model' | 'rule'
}

const DEMO_TRIAGE_SEEDS: DemoTriageSeed[] = [
  // ── needs you today ──
  { account: 1, minutesAgo: 35, fromName: 'Prof. Ada Lindqvist', from: 'lindqvist@berkeley.example', subject: 'Confirm your COGSCI C142 project group by tonight', snippet: 'Please reply with your group members by 11:59 PM so I can post the roster tomorrow.', verdict: 'needs_you_today', reason: 'Asks you to reply with your project group by tonight.', deadline: SEED_TODAY, action: 'reply' },
  { account: 1, minutesAgo: 3 * 1440 + 200, fromName: 'Cal Student Billing', from: 'billing@calcentral.example', subject: 'Tuition installment due Friday', snippet: 'Your next installment of $1,240.00 is due Friday, September 18.', verdict: 'needs_you_today', reason: 'A tuition installment is due Friday.', deadline: addDays(SEED_TODAY, 2), action: 'pay', ageDays: 3 },
  { account: 2, minutesAgo: 70, fromName: 'Account security', from: 'security@accounts.example', subject: 'New sign-in on a Windows device', snippet: 'We noticed a new sign-in to your account from Windows in Oakland, CA. If this was you, no action is needed.', verdict: 'needs_you_today', reason: 'A new sign-in to your account; check it was you.', action: 'read', security: true },
  { account: 1, minutesAgo: 1440 + 300, fromName: 'NEU C61 GSI', from: 'gsi.neuro@berkeley.example', subject: 'Lab 2 resubmission closes today', snippet: 'If you want regrading on Lab 2, resubmit on bCourses before 5 PM today.', verdict: 'needs_you_today', reason: 'The Lab 2 resubmission window closes at 5 PM today.', deadline: SEED_TODAY, action: 'submit', ageDays: 1 },
  // ── important ──
  { account: 2, minutesAgo: 99, fromName: 'Maya', from: 'maya@personal.example', subject: 'Dinner Saturday?', snippet: 'Are you free Saturday around 7? Thinking the ramen place on Shattuck.', verdict: 'important', modelVerdict: 'can_wait', ownerVerdict: 'important', reason: 'A friend asking about Saturday dinner.', action: 'reply' },
  { account: 1, minutesAgo: 53, fromName: 'Prof. Ada Lindqvist', from: 'lindqvist@berkeley.example', subject: 'Office hours moved to Thursday this week', snippet: 'I am travelling Wednesday, so office hours move to Thursday 2–4 PM in Dwinelle 3401.', verdict: 'important', reason: 'Office hours move to Thursday this week.', action: 'read' },
  { account: 2, minutesAgo: 5 * 60, fromName: 'Northlight Studio Talent', from: 'talent@northlight.example', subject: 'Interview availability next week', snippet: 'Thanks for applying to the research assistant role. Could you share three times that work next week?', verdict: 'important', reason: 'Asks for interview times next week.', deadline: addDays(SEED_TODAY, 5), action: 'schedule' },
  { account: 1, minutesAgo: 9 * 60, fromName: 'Transfer Advising', from: 'advising@berkeley.example', subject: 'Spring petition: next steps', snippet: 'Your petition was received. A decision is expected within two weeks.', verdict: 'important', reason: 'From an address you always mark important.', action: 'read', source: 'rule' },
  // ── not sorted yet ──
  { account: 2, minutesAgo: 12, fromName: 'Jordan Park', from: 'jordan@housing.example', subject: 'Re: sublet for October', snippet: 'The room is still available if you want it — can you let me know by the weekend?', verdict: null, reason: null, action: null },
  // ── can wait ──
  { account: 1, minutesAgo: 2 * 1440 + 100, fromName: 'Transfer Student Center', from: 'tsc@berkeley.example', subject: 'Transfer mixer next Tuesday', snippet: 'Meet other transfer students over pizza, Tuesday 5 PM at the Transfer Center.', verdict: 'can_wait', reason: 'An optional event next Tuesday.', action: 'none' },
  { account: 1, minutesAgo: 1440 + 600, fromName: 'Library Services', from: 'library@berkeley.example', subject: 'Your hold is ready for pickup', snippet: 'The item you requested is waiting at Moffitt Library until September 20.', verdict: 'can_wait', reason: 'A library hold is waiting until Sep 20.', deadline: addDays(SEED_TODAY, 4), action: 'none' },
  { account: 2, minutesAgo: 1440 + 120, fromName: null, from: 'orders@bookshop.example', subject: 'Your order has shipped', snippet: 'Order 10442 (two paperbacks) is on its way. Expected Thursday.', verdict: 'can_wait', reason: 'A shipping notice; nothing to do.', action: 'none' },
  { account: 2, minutesAgo: 4 * 60, fromName: 'Mind Matters Weekly', from: 'newsletter@mindmatters.example', subject: 'This week in cognitive science', snippet: 'Five papers worth your time, plus a long read on predictive processing.', verdict: 'can_wait', reason: 'A newsletter you subscribed to.', action: 'read' },
  // ── noise ──
  { account: 2, minutesAgo: 6 * 60, fromName: 'Climbing Gym', from: 'hello@climb.example', subject: 'Bring a friend for free this weekend', snippet: 'Members can bring one guest free on Saturday and Sunday.', verdict: 'noise', reason: 'A promotion.', action: 'none' },
  { account: 2, minutesAgo: 1440 + 30, fromName: 'Tempo', from: 'no-reply@tempo.example', subject: 'Welcome to Tempo — confirm your email', snippet: 'Thanks for signing up. Confirm your email to finish creating your account.', verdict: 'noise', reason: 'A sign-up confirmation for an app.', action: 'none' },
  { account: 2, minutesAgo: 8 * 60, fromName: 'Campus Store', from: 'deals@store.example', subject: '20% off hoodies until Sunday', snippet: 'Show your colours: every hoodie is 20% off through Sunday.', verdict: 'noise', reason: 'A sale.', action: 'none' },
  // ── an expired sign-in code: counted, not listed ──
  { account: 2, minutesAgo: 2 * 1440, fromName: 'Tempo', from: 'no-reply@tempo.example', subject: 'Your sign-in code is 482913', snippet: 'Use 482913 to sign in. It expires in 10 minutes.', verdict: 'noise', reason: 'A one-time sign-in code.', action: 'none', isCode: true },
]

/** What a run of the sorter files the unsorted demo message as. */
const DEMO_WOULD_SORT_AS: Record<string, MailVerdict> = { 'mail-tri-demo-009': 'important' }
const DEMO_WOULD_SORT_REASON: Record<string, string> = {
  'mail-tri-demo-009': 'Asks you to decide about the October sublet by the weekend.',
}

function demoMinutesBefore(minutesAgo: number): string {
  const total = SEED_CLOCK_MINUTES - minutesAgo
  const days = Math.floor(total / 1440)
  return calTimestamp(addDays(SEED_TODAY, days), total - days * 1440)
}

function demoTriageSeedItems(): MailTriageItem[] {
  const proposals = demoProposalSeeds()
  return DEMO_TRIAGE_SEEDS.map((s, i) => withDemoProposals(demoTriageSeedItem(s, i), proposals))
}

function demoTriageSeedItem(s: DemoTriageSeed, i: number): MailTriageItem {
  const id = `mail-tri-demo-${String(i + 1).padStart(3, '0')}`
  const address = DEMO_MAIL_ADDRESSES[s.account - 1]!
  const ageDays = s.ageDays ?? 0
  const sorted = s.verdict !== null
  const source = sorted ? (s.source ?? 'model') : null
  return {
    id,
    accountId: `mail-demo-${s.account}`,
    accountAddress: address,
    from: { name: s.fromName, address: s.from },
    subject: s.subject,
    snippet: s.snippet,
    receivedAt: demoMinutesBefore(s.minutesAgo),
    url: `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(address)}#all/${id}`,
    verdict: s.verdict,
    modelVerdict: source === 'model' ? (s.modelVerdict ?? s.verdict) : null,
    ownerVerdict: s.ownerVerdict ?? null,
    reason: s.reason,
    deadline: s.deadline ?? null,
    action: s.action,
    security: s.security ?? false,
    isCode: s.isCode ?? false,
    state: 'open',
    snoozedUntil: null,
    firstSurfacedOn: addDays(SEED_TODAY, -ageDays),
    ageDays,
    triagedAt: sorted ? demoMinutesBefore(Math.max(0, s.minutesAgo - 2)) : null,
    triageModel: source === 'model' ? DEMO_TRIAGE_MODEL : null,
    triageSource: source,
    proposals: [],
    proposalsAt: null,
  }
}

let demoTriageItems: MailTriageItem[] = demoTriageSeedItems()

const DEMO_SEED_RULES: MailRule[] = [
  {
    id: 'mail-rule-demo-1',
    matchType: 'address',
    matchValue: 'advising@berkeley.example',
    verdict: 'important',
    createdAt: calTimestamp(addDays(SEED_TODAY, -3), 20 * 60),
  },
]
let demoMailRules: MailRule[] = [...DEMO_SEED_RULES]
let demoMailRuleSeq = 1

/** Corrections with a reason chip — kept so a test can see the second call landed. */
const demoMailFeedback: { id: string; verdict: MailVerdict; reason: MailCorrectionReason }[] = []

function demoTriageItem(id: string): MailTriageItem {
  const item =
    demoTriageItems.find((i) => i.id === id) ??
    Object.values(demoDayStores).flatMap((st) => st?.items ?? []).find((i) => i.id === id)
  if (!item) throw new Error('No message with that id, for this owner.')
  return item
}

/** A code older than a day is expired: counted, never listed. */
function demoCodeExpired(item: MailTriageItem): boolean {
  return item.isCode && Date.parse(DEMO_NOW) - Date.parse(item.receivedAt) > 1440 * 60_000
}

/** A rule re-files every open message from its sender that the owner has not moved himself. */
function applyDemoRules(): void {
  for (const item of demoTriageItems) {
    if (item.ownerVerdict) continue
    const address = item.from.address.toLowerCase()
    const domain = address.split('@')[1] ?? ''
    const rule = demoMailRules.find(
      (r) => (r.matchType === 'address' && r.matchValue === address) || (r.matchType === 'domain' && r.matchValue === domain),
    )
    if (!rule) continue
    item.verdict = rule.verdict
    item.triageSource = 'rule'
    item.triageModel = null
    item.modelVerdict = null
  }
}

function demoTriage(query?: MailTriageQuery): MailTriage {
  const scenario = query?.scenario
  const store = scenario && scenario !== 'loading' ? demoDayStore(scenario) : null
  const accounts: MailTriageAccount[] = store
    ? store.accounts
    : mailAccounts.map((a) => ({ id: a.id, address: a.address, lastSyncAt: a.lastSyncAt, lastError: a.lastError }))
  const live = new Set(accounts.map((a) => a.id))
  const now = Date.parse(DEMO_NOW)
  const all = (store ? store.items : demoTriageItems).filter((i) => live.has(i.accountId))
  const open = all
    .filter((i) => i.state === 'open' || (i.state === 'snoozed' && i.snoozedUntil !== null && Date.parse(i.snoozedUntil) <= now))
    .sort((a, b) => Date.parse(b.receivedAt) - Date.parse(a.receivedAt))
  const expired = open.filter(demoCodeExpired)
  const listed = open.filter((i) => !demoCodeExpired(i))
  // `?days=` folds the two quiet piles to that window, the way the Worker does; what needs you and
  // what is important are never cut off by age.
  const since = query?.days !== undefined ? now - query.days * 1440 * 60_000 : null
  const inWindow = (i: MailTriageItem) => since === null || Date.parse(i.receivedAt) >= since
  const pile = (v: MailVerdict | null) => listed.filter((i) => i.verdict === v)
  const snoozed = all
    .filter((i) => i.state === 'snoozed' && i.snoozedUntil !== null && Date.parse(i.snoozedUntil) > now)
    .sort((a, b) => Date.parse(a.snoozedUntil!) - Date.parse(b.snoozedUntil!))
  const groups = {
    needsYouToday: pile('needs_you_today'),
    important: pile('important'),
    canWait: pile('can_wait').filter(inWindow),
    noise: pile('noise').filter(inWindow),
    unsorted: pile(null),
    snoozed,
  }
  const handledToday =
    all.filter((i) => {
      const at = demoHandledAt.get(i.id)
      return at !== undefined && at.slice(0, 10) === SEED_TODAY && (i.state === 'done' || i.state === 'dismissed')
    }).length + (store?.handledEarlier ?? 0)
  return {
    generatedAt: store?.generatedAt ?? calTimestamp(SEED_TODAY, SEED_CLOCK_MINUTES - 9),
    accounts,
    counts: {
      needsYouToday: groups.needsYouToday.length,
      important: groups.important.length,
      canWait: groups.canWait.length,
      noise: groups.noise.length,
      unsorted: groups.unsorted.length,
      expiredCodes: expired.length,
      snoozed: snoozed.length,
      handledToday,
    },
    groups,
    nextSnoozedAt: snoozed[0]?.snoozedUntil ?? null,
  }
}

/** Test-only: put the triage fixture back as it was seeded. */
export function __resetDemoTriage(): void {
  demoTriageItems = demoTriageSeedItems()
  demoHandledAt.clear()
  for (const key of Object.keys(demoDayStores) as DayScenario[]) delete demoDayStores[key]
  // The seed rule comes back even when a test deleted it (the Gmail card's Delete does).
  demoMailRules = [...DEMO_SEED_RULES]
  demoMailFeedback.length = 0
}

/** Last correction reasons recorded (test-only read). */
export function __demoMailFeedback(): readonly { id: string; verdict: MailVerdict; reason: MailCorrectionReason }[] {
  return demoMailFeedback
}

// ════════════════════════════════════════════════════════════════════════════
// THE DESIGNED DAY PAGE, IN DEMO (Lane 4a) — appended as its own block.
//
// `/day?state=<scenario>` in demo mode selects one of the board states in
// docs/design/handoff/day-page/README.md, so the FIDELITY side-by-side proof can photograph every
// one of them without an account. Each scenario is its own in-memory store with the SAME shapes
// the Worker answers, and every write works on it (`demoTriageItem` looks in every store).
//
// THE CONTENT IS INVENTED, ON PURPOSE. The boards were drawn over the owner's real inbox (a
// colleague's project, his health provider, his brokerage). This file ships inside the web
// bundle, so those become invented stand-ins on `.example` domains — the same convention the rest
// of the demo dataset keeps. Company newsletters keep their public names; nobody's person does.
// ════════════════════════════════════════════════════════════════════════════

/** When each message was marked done or dismissed — what "Handled today" counts. */
const demoHandledAt = new Map<string, string>()

interface DemoDayStore {
  accounts: MailTriageAccount[]
  items: MailTriageItem[]
  /** Handled before the seeded clock, in the scenario's story. Counted, never listed. */
  handledEarlier: number
  generatedAt: string
}

const demoDayStores: Partial<Record<DayScenario, DemoDayStore>> = {}

type DayAccountKey = 'work' | 'school' | 'personal'

/** Professional · Berkeley · Flexible — the three inbox roles, on reserved `.example` addresses. */
const DAY_ACCOUNTS: Record<DayAccountKey, { id: string; address: string }> = {
  work: { id: 'mail-day-work', address: 'kaz@work.example' },
  school: { id: 'mail-day-school', address: 'kaz@school.example' },
  personal: { id: 'mail-day-personal', address: 'kaz@personal.example' },
}

/** The sentence the Worker writes onto a revoked account (apps/worker/src/lib/gmail-adapter.ts). */
const DEMO_GMAIL_RECONNECT = 'Google no longer accepts this connection. Disconnect and connect it again.'

interface DaySeed {
  account: DayAccountKey
  daysAgo: number
  /** Minutes past midnight it arrived. */
  at: number
  name: string | null
  from: string
  subject: string
  verdict: MailVerdict | null
  reason?: string | null
  deadline?: ISODate | null
  security?: boolean
  isCode?: boolean
  ageDays?: number
  /** When it was sorted: [days ago, minutes]. Defaults to the scenario's sort run. */
  sortedAt?: [number, number]
  snoozedUntil?: string
}

function daySeedItems(scenario: string, seeds: DaySeed[], sortRun: [number, number]): MailTriageItem[] {
  return seeds.map((s, i): MailTriageItem => {
    const account = DAY_ACCOUNTS[s.account]
    const id = `mail-day-${scenario}-${String(i + 1).padStart(3, '0')}`
    const sorted = s.verdict !== null
    const [sd, sm] = s.sortedAt ?? sortRun
    const ageDays = s.ageDays ?? 0
    return {
      id,
      accountId: account.id,
      accountAddress: account.address,
      from: { name: s.name, address: s.from },
      subject: s.subject,
      snippet: '',
      receivedAt: calTimestamp(addDays(SEED_TODAY, -s.daysAgo), s.at),
      url: `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(account.address)}#all/${id}`,
      verdict: s.verdict,
      modelVerdict: s.verdict,
      ownerVerdict: null,
      reason: s.reason ?? null,
      deadline: s.deadline ?? null,
      action: null,
      security: s.security ?? false,
      isCode: s.isCode ?? false,
      state: s.snoozedUntil ? 'snoozed' : 'open',
      snoozedUntil: s.snoozedUntil ?? null,
      firstSurfacedOn: addDays(SEED_TODAY, -ageDays),
      ageDays,
      triagedAt: sorted ? calTimestamp(addDays(SEED_TODAY, -sd), sm) : null,
      triageModel: sorted ? DEMO_TRIAGE_MODEL : null,
      triageSource: sorted ? 'model' : null,
      proposals: [],
      proposalsAt: null,
    }
  }).map((item) => withDemoProposals(item, demoProposalSeeds()))
}

/** A folded-pile row: sender, subject, days ago. */
type FoldSeed = [account: DayAccountKey, name: string, from: string, subject: string, daysAgo: number]

function foldSeeds(rows: FoldSeed[], verdict: MailVerdict): DaySeed[] {
  return rows.map(([account, name, from, subject, daysAgo], i) => ({
    account,
    daysAgo,
    at: 10 * 60 - i,
    name,
    from,
    subject,
    verdict,
    reason: verdict === 'noise' ? 'A promotion or a feed you did not ask for today.' : 'Worth keeping; nothing to do.',
  }))
}

const MORNING_LOW: FoldSeed[] = [
  ['personal', 'CalSCUBA on Slack', 'feedback@slack.example', 'Weekly digest', 1],
  ['personal', 'Uber Eats', 'uber@uber.example', 'Your receipt', 2],
  ['work', 'Amazon', 'auto-confirm@amazon.example', 'Ordered: 1 Kitchen item', 3],
  ['work', 'AGI House', 'events@agihouse.example', 'The Living Internet and Open Source AI', 3],
  ['work', 'Stripe', 'notifications@stripe.example', "Updates to Stripe's legal terms and Privacy Policy", 5],
  ['work', 'Amazon', 'shipment-tracking@amazon.example', 'Delivered: 1 Book item', 6],
  ['work', 'Amazon', 'shipment-tracking@amazon.example', 'Shipped: 1 Book item', 6],
  ['work', 'Amazon', 'auto-confirm@amazon.example', 'Ordered: 1 Book item', 7],
  ['school', 'L&S Events', 'lsevents@school.example', "Join L&S at Homecoming — Events You Don't Want to Miss", 7],
]

const MORNING_NOISE: FoldSeed[] = [
  ['personal', 'Pinterest', 'ideas@pinterest.example', 'Ideas for you: small apartment studio setups', 0],
  ['personal', 'CVS Pharmacy', 'offers@cvs.example', 'Flu shots are here — walk in today', 0],
  ['work', 'LinkedIn', 'messages-noreply@linkedin.example', 'Kaz, you appeared in 12 searches this week', 1],
  ['personal', 'The Berkeley Group', 'residents@berkeleygroup.example', 'Pumpkin carving in the lobby Friday', 1],
  ['work', 'Patreon', 'no-reply@patreon.example', 'Weekly Lions Led By Robots 33', 1],
  ['work', 'bunny.net', 'news@bunny.example', 'Introducing Edge Scripting', 2],
  ['personal', 'findacrew', 'alerts@findacrew.example', 'New crew positions near you', 2],
  ['work', 'Cloudflare', 'noreply@cloudflare.example', 'Your Cloudflare Connect 2026 discount ends soon', 2],
  ['personal', 'Pinterest', 'ideas@pinterest.example', 'Saved: video lighting setups', 2],
  ['work', 'LinkedIn', 'jobs-noreply@linkedin.example', 'Video Editor Intern at Descript', 2],
  ['personal', 'mpb', 'hello@mpb.example', 'Price drop: Sony FX3', 2],
  ['work', 'Patreon', 'no-reply@patreon.example', "h3 Seed Hunter v2.5's got a crazy little upgrade", 3],
  ['work', 'LinkedIn', 'jobs-noreply@linkedin.example', 'Internship in AI and media roles: PhD Machine Learning Internship 2027', 3],
  ['personal', 'The Berkeley Group', 'residents@berkeleygroup.example', 'PRIVATE PARKING GARAGE AVAILABLE', 3],
  ['personal', 'The Berkeley Group', 'residents@berkeleygroup.example', 'Run Into Someone New', 3],
  ['work', 'Patreon', 'no-reply@patreon.example', 'Lions Led By Robots 32: Full Metal Alchemist Brotherhood: Part 6', 4],
  ['personal', 'The Berkeley Group', 'residents@berkeleygroup.example', 'Homecoming Weekend: Cal vs. Virginia Tech!', 4],
  ['work', 'LinkedIn', 'jobs-noreply@linkedin.example', 'Internship in AI and media roles: Machine Learning Engineer Intern', 4],
  ['work', 'Patreon', 'no-reply@patreon.example', 'Introducing: Weekly Lions Led By Robots', 4],
  ['work', 'LinkedIn', 'jobs-noreply@linkedin.example', 'Social Media Intern at Nuro', 5],
  ['work', 'Patreon', 'no-reply@patreon.example', 'My BEST Video Editing Techniques for AI Filmmakers', 6],
  ['work', 'LinkedIn', 'jobs-noreply@linkedin.example', 'Machine Learning Intern at SwingVision', 6],
  ['work', 'Patreon', 'no-reply@patreon.example', 'Back to working on Tutorials & Effy Ep 3', 6],
  ['personal', 'mpb', 'hello@mpb.example', 'Back in stock: DJI Ronin 4D-6K', 6],
  ['work', 'Cloudflare', 'noreply@cloudflare.example', 'Boost performance with these tools', 7],
  ['work', 'Cloudflare', 'noreply@cloudflare.example', 'Welcome to Birthday Week 2026!', 7],
  ['work', 'Amazon', 'account-update@amazon.example', 'Check if a message is really from Amazon', 7],
  ['work', 'LinkedIn', 'jobs-noreply@linkedin.example', 'AI Engineer Intern at Jobright.ai', 7],
]

const ROWAN_SUBJECT = 'Subtitles – October screening – translations needed'

function morningStore(): DemoDayStore {
  const sortRun: [number, number] = [0, 6 * 60 + 42]
  const tonight = calTimestamp(SEED_TODAY, 20 * 60)
  // The Monday after the seed day, 8:00 AM — what "Next week" would have said.
  const nextMonday = calTimestamp(addDays(SEED_TODAY, 5), 8 * 60)
  const seeds: DaySeed[] = [
    { account: 'work', daysAgo: 5, at: 16 * 60 + 26, name: 'Rowan', from: 'rowan@festival.example', subject: ROWAN_SUBJECT, verdict: 'needs_you_today', reason: 'Rowan asks for the translated subtitle files by today and notes a few scenes the first pass skipped.', deadline: SEED_TODAY, ageDays: 5 },
    { account: 'work', daysAgo: 2, at: 9 * 60 + 5, name: 'Clinic', from: 'reminders@clinic.example', subject: 'Reminder: Get ready for your upcoming appointments', verdict: 'needs_you_today', reason: "Check-in for today's 3:15 PM appointment is not done yet.", ageDays: 2 },
    { account: 'work', daysAgo: 6, at: 11 * 60 + 40, name: 'Bay Health billing', from: 'billing@bayhealth.example', subject: 'Kaz, your statement is ready', verdict: 'needs_you_today', reason: 'A statement has a balance to pay. The mail gives no due date.', ageDays: 6 },
    { account: 'work', daysAgo: 3, at: 17 * 60 + 41, name: 'Google', from: 'no-reply@accounts.example', subject: 'Security alert', verdict: 'important', reason: 'Google reports that a new app was given access to this account.', security: true },
    { account: 'work', daysAgo: 3, at: 9 * 60 + 12, name: 'Brokerage', from: 'statements@brokerage.example', subject: 'Your Statement Is Now Available', verdict: 'important', reason: 'A brokerage statement is ready to view online.' },
    { account: 'work', daysAgo: 0, at: 7 * 60 + 2, name: 'NAS', from: 'no-reply@nas.example', subject: 'Verification code from your NAS', verdict: 'important', isCode: true },
    { account: 'school', daysAgo: 1, at: 15 * 60 + 20, name: 'Prof. Ada Lindqvist', from: 'lindqvist@school.example', subject: 'Office hours moved to Tuesday this week', verdict: 'important', reason: 'Office hours move to Tuesday this week.', snoozedUntil: tonight },
    { account: 'personal', daysAgo: 3, at: 13 * 60, name: 'PayPal', from: 'service@paypal.example', subject: "We're making changes to our terms and conditions", verdict: 'can_wait', reason: 'A terms update; nothing to do.', snoozedUntil: nextMonday },
    ...foldSeeds(MORNING_LOW, 'can_wait'),
    ...foldSeeds(MORNING_NOISE, 'noise'),
  ]
  return {
    accounts: (['work', 'school', 'personal'] as const).map((k) => ({
      id: DAY_ACCOUNTS[k].id,
      address: DAY_ACCOUNTS[k].address,
      lastSyncAt: calTimestamp(SEED_TODAY, 6 * 60 + 42),
      lastError: null,
    })),
    items: daySeedItems('morning', seeds, sortRun),
    handledEarlier: 2,
    generatedAt: calTimestamp(SEED_TODAY, 6 * 60 + 42),
  }
}

function quietStore(): DemoDayStore {
  const sortRun: [number, number] = [0, 7 * 60 + 10]
  const low: FoldSeed[] = [
    ['work', 'Amazon', 'auto-confirm@amazon.example', 'Ordered: 1 Kitchen item', 1],
    ['work', 'Amazon', 'shipment-tracking@amazon.example', 'Shipped: 1 Kitchen item', 1],
    ['work', 'Amazon', 'shipment-tracking@amazon.example', 'Delivered: 1 Kitchen item', 2],
    ['work', 'Amazon', 'auto-confirm@amazon.example', 'Ordered: 1 Book item', 3],
    ['work', 'AGI House', 'events@agihouse.example', 'The Living Internet and Open Source AI', 4],
    ['school', 'L&S Events', 'lsevents@school.example', 'Homecoming week schedule', 5],
  ]
  const noise: FoldSeed[] = [
    ...Array.from({ length: 11 }, (_, i): FoldSeed => ['work', 'Patreon', 'no-reply@patreon.example', `Lions Led By Robots ${34 - i}`, Math.min(6, Math.floor(i / 2))]),
    ['work', 'LinkedIn', 'jobs-noreply@linkedin.example', 'Video Editor Intern at Descript', 1],
    ['work', 'LinkedIn', 'jobs-noreply@linkedin.example', 'Social Media Intern at Nuro', 3],
    ['work', 'LinkedIn', 'jobs-noreply@linkedin.example', 'Machine Learning Intern at SwingVision', 5],
    ['personal', 'Pinterest', 'ideas@pinterest.example', 'Saved: video lighting setups', 1],
    ['personal', 'CVS Pharmacy', 'offers@cvs.example', 'Flu shots are here — walk in today', 2],
    ['personal', 'findacrew', 'alerts@findacrew.example', 'New crew positions near you', 2],
    ['personal', 'mpb', 'hello@mpb.example', 'Price drop: Sony FX3', 3],
    ['work', 'Cloudflare', 'noreply@cloudflare.example', 'Boost performance with these tools', 4],
    ['work', 'bunny.net', 'news@bunny.example', 'Introducing Edge Scripting', 4],
    ['personal', 'The Berkeley Group', 'residents@berkeleygroup.example', 'Run Into Someone New', 5],
    ['work', 'Stripe', 'notifications@stripe.example', 'Your monthly receipt', 6],
  ]
  const seeds: DaySeed[] = [
    { account: 'work', daysAgo: 2, at: 9 * 60 + 12, name: 'Brokerage', from: 'statements@brokerage.example', subject: 'Your Statement Is Now Available', verdict: 'important', reason: 'A brokerage statement is ready to view online.' },
    ...foldSeeds(low, 'can_wait'),
    ...foldSeeds(noise, 'noise'),
  ]
  return {
    accounts: (['work', 'school', 'personal'] as const).map((k) => ({
      id: DAY_ACCOUNTS[k].id,
      address: DAY_ACCOUNTS[k].address,
      lastSyncAt: calTimestamp(SEED_TODAY, 7 * 60 + 10),
      lastError: null,
    })),
    items: daySeedItems('quiet', seeds, sortRun),
    handledEarlier: 0,
    generatedAt: calTimestamp(SEED_TODAY, 7 * 60 + 10),
  }
}

function degradedStore(): DemoDayStore {
  const sortRun: [number, number] = [0, 6 * 60 + 42]
  const seeds: DaySeed[] = [
    { account: 'work', daysAgo: 5, at: 16 * 60 + 26, name: 'Rowan', from: 'rowan@festival.example', subject: ROWAN_SUBJECT, verdict: 'needs_you_today', reason: 'Rowan asks for the translated subtitle files by today and notes a few scenes the first pass skipped.', deadline: SEED_TODAY, ageDays: 5 },
    { account: 'school', daysAgo: 1, at: 21 * 60 + 58, name: 'Prof. Ada Lindqvist', from: 'lindqvist@school.example', subject: 'Office hours moved to Thursday this week', verdict: 'needs_you_today', reason: "Prof. Lindqvist moves this week's office hours to Thursday and asks you to confirm your slot.", deadline: addDays(SEED_TODAY, 1), sortedAt: [1, 22 * 60 + 14] },
    { account: 'work', daysAgo: 0, at: 6 * 60 + 31, name: 'Robin', from: 'robin@friends.example', subject: `Re: ${ROWAN_SUBJECT}`, verdict: null },
    { account: 'work', daysAgo: 0, at: 6 * 60 + 2, name: 'Brokerage', from: 'statements@brokerage.example', subject: 'Your Statement Is Now Available', verdict: null },
    { account: 'work', daysAgo: 0, at: 5 * 60 + 12, name: 'Amazon', from: 'auto-confirm@amazon.example', subject: 'Ordered: 1 Kitchen item', verdict: null },
    ...foldSeeds(MORNING_LOW.slice(0, 4), 'can_wait'),
    ...foldSeeds(MORNING_NOISE.slice(0, 9), 'noise'),
  ]
  return {
    accounts: [
      { id: DAY_ACCOUNTS.work.id, address: DAY_ACCOUNTS.work.address, lastSyncAt: calTimestamp(SEED_TODAY, 6 * 60 + 42), lastError: null },
      {
        id: DAY_ACCOUNTS.school.id,
        address: DAY_ACCOUNTS.school.address,
        lastSyncAt: calTimestamp(addDays(SEED_TODAY, -1), 22 * 60 + 10),
        lastError: 'Google rate-limited the last two syncs. The next sync, in 30 minutes, will try again.',
      },
      {
        id: DAY_ACCOUNTS.personal.id,
        address: DAY_ACCOUNTS.personal.address,
        lastSyncAt: calTimestamp(addDays(SEED_TODAY, -2), 14 * 60 + 5),
        lastError: DEMO_GMAIL_RECONNECT,
      },
    ],
    items: daySeedItems('degraded', seeds, sortRun),
    handledEarlier: 0,
    generatedAt: calTimestamp(SEED_TODAY, 6 * 60 + 42),
  }
}

function demoDayStore(scenario: Exclude<DayScenario, 'loading'>): DemoDayStore {
  const existing = demoDayStores[scenario]
  if (existing) return existing
  const made =
    scenario === 'morning'
      ? morningStore()
      : scenario === 'quiet'
        ? quietStore()
        : scenario === 'degraded'
          ? degradedStore()
          : { accounts: [], items: [], handledEarlier: 0, generatedAt: DEMO_NOW }
  demoDayStores[scenario] = made
  return made
}

/** How many open messages a rule files right now (`messageCountOpen`). */
function demoRuleOpenCount(rule: MailRule): number {
  return demoTriageItems.filter((i) => {
    if (i.state !== 'open') return false
    const address = i.from.address.toLowerCase()
    return rule.matchType === 'address' ? address === rule.matchValue : (address.split('@')[1] ?? '') === rule.matchValue
  }).length
}

/**
 * THE WEEKLY CHECK, IN DEMO. The numbers are the scenario's story; the sample is ten (or fewer)
 * real items from the folded piles of the same store, so grading one writes to an item the page
 * also shows, and the rules are proposed from senders that are really in those piles.
 */
function demoAudit(weekStart: ISODate, scenario?: DayScenario): MailAudit {
  const store = scenario && scenario !== 'loading' ? demoDayStore(scenario) : null
  const items = (store ? store.items : demoTriageItems).filter(
    (i) => i.state === 'open' && (i.verdict === 'can_wait' || i.verdict === 'noise') && !i.isCode,
  )
  // A fixed spread rather than Math.random: demo mode is deterministic (demo.test.ts's rule).
  const sample = items
    .filter((_, i) => i % 3 === 0)
    .concat(items.filter((_, i) => i % 3 !== 0))
    .slice(0, 10)
  const firstRun = scenario === 'first-run'
  return {
    weekStart,
    weekEnd: addDays(weekStart, 6),
    generatedAt: calTimestamp(SEED_TODAY, 6 * 60),
    counts: firstRun
      ? { sorted: 0, moved: 0, movedByPile: { needs_you_today: 0, important: 0, can_wait: 0, noise: 0 }, dismissed: 0, done: 0 }
      : { sorted: 212, moved: 9, movedByPile: { needs_you_today: 2, important: 4, can_wait: 1, noise: 2 }, dismissed: 31, done: 14 },
    sample: firstRun ? [] : sample,
    proposedRules: firstRun
      ? []
      : [
          {
            matchType: 'address',
            matchValue: 'rowan@festival.example',
            verdict: 'important',
            evidence: 'You moved their mail up from low priority twice this week.',
            example: { subject: ROWAN_SUBJECT, fromAddress: 'rowan@festival.example' },
          },
          {
            matchType: 'address',
            matchValue: 'no-reply@patreon.example',
            verdict: 'noise',
            evidence: '14 sorted as noise this week. You opened none and moved none out.',
            example: { subject: 'Weekly Lions Led By Robots 33', fromAddress: 'no-reply@patreon.example' },
          },
        ],
  }
}

/**
 * THE QUIET BOARD'S OTHER HALF. A quiet day is quiet everywhere — no classes, nothing overdue,
 * two things coming — and the demo calendar's seeded Wednesday is not that day. So the quiet
 * scenario carries its own classes / reminders / assignments, exactly the shapes those reads
 * answer, and the Day page uses them in place of the live demo reads ONLY in demo mode with
 * `?state=quiet`. Every other scenario reads the demo calendar as it is.
 */
export function demoDayContext(
  scenario: DayScenario | null,
): { classes: ClassOccurrence[]; tasks: Task[]; assignments: Assignment[] } | null {
  if (scenario !== 'quiet') return null
  const due = (days: number) => `${addDays(SEED_TODAY, days)}T23:59:00${CAL_OFFSET}`
  const assignment = (id: string, title: string, days: number): Assignment => ({
    id,
    courseId: 'course-cogsci-c142',
    courseCode: 'COGSCI C142',
    title,
    kind: 'unclassified',
    dueAt: due(days),
    assignedAt: null,
    status: 'upcoming',
    score: null,
    pointsPossible: 1,
    lockAt: null,
    gradingType: null,
    submissionTypes: [],
    htmlUrl: null,
    description: '',
  })
  const someday = (id: string, title: string): Task => ({
    id,
    title,
    done: false,
    due: null,
    list: 'Inbox',
    priority: 'none',
    rrule: null,
    reminderAt: null,
    createdAt: calTimestamp(addDays(SEED_TODAY, -4), 9 * 60),
    completedAt: null,
    estimateMin: null,
    proposalId: null,
  })
  return {
    classes: [],
    tasks: [someday('task-quiet-1', 'Look into a bike light'), someday('task-quiet-2', 'Return the library book')],
    assignments: [
      assignment('a-quiet-1', 'Discussion check-in: September 21', 5),
      assignment('a-quiet-2', 'Quiz #7', 6),
    ],
  }
}

// ════════════════════════════════════════════════════════════════════════════
// NEXT STEPS, IN DEMO (Day page, Lane 6) — appended as its own block.
//
// What the Worker would have proposed in its cron run after triage, for the two top piles only.
// Every kind and every status appears somewhere, plus messages the Worker has not reached
// (`proposalsAt: null`) and ones it reached and found nothing for (`proposalsAt` set, no rows).
// Seeded accepts are kinds that create nothing (opened, noted), so no seeded `result` points at
// a block or reminder the fixture does not have.
// ════════════════════════════════════════════════════════════════════════════

interface DemoProposalSeed {
  kind: MailProposalKind
  title: string
  detail: string
  payload: MailProposal['payload']
  status?: MailProposalStatus
  source?: 'body' | 'snippet'
}

/** By message id. An empty list = the Worker ran and proposed nothing. Absent = it has not run. */
function demoProposalSeeds(): Record<string, DemoProposalSeed[]> {
  const day = (n: number) => addDays(SEED_TODAY, n)
  return {
    // ── the default demo inbox ──
    'mail-tri-demo-001': [
      {
        kind: 'draft_reply',
        title: 'Draft a reply with your group',
        detail: 'Prof. Lindqvist asks for your group members by 11:59 PM tonight.',
        payload: { text: 'Hi Prof. Lindqvist,\n\nMy COGSCI C142 project group is: [names].\n\nThanks,\nKaz' },
        source: 'body',
      },
      {
        kind: 'reminder',
        title: 'Reply to Prof. Lindqvist with your group',
        detail: 'The mail sets the deadline at 11:59 PM today.',
        payload: { title: 'Reply to Prof. Lindqvist with your project group', dueDate: day(0) },
        status: 'skipped',
        source: 'body',
      },
    ],
    'mail-tri-demo-002': [
      {
        kind: 'reminder',
        title: 'Pay the tuition installment by Friday',
        detail: 'The snippet says the installment is due Friday, September 18.',
        payload: { title: 'Pay the tuition installment', dueDate: day(2) },
      },
      {
        kind: 'open_link',
        title: 'Open CalCentral billing',
        detail: 'Payments are made in CalCentral; LifeOS cannot pay.',
        payload: { url: 'https://calcentral.example/finances', label: 'CalCentral' },
        status: 'accepted',
      },
    ],
    'mail-tri-demo-003': [
      {
        kind: 'verify_official',
        title: 'Check the sign-in from your account settings',
        detail: 'The mail reports a new Windows sign-in from Oakland, CA.',
        payload: { step: 'Open your account’s security page yourself, not from the mail, and review recent devices.' },
        source: 'body',
      },
    ],
    // Reached, and nothing to propose: the snippet names no link and no time beyond the deadline.
    'mail-tri-demo-004': [],
    'mail-tri-demo-005': [
      {
        kind: 'calendar_block',
        title: 'Add dinner with Maya on Saturday at 7 PM',
        detail: 'Maya asks if you are free Saturday around 7; nothing is on your calendar then.',
        payload: { title: 'Dinner with Maya', startAt: calTimestamp(day(3), 19 * 60), endAt: calTimestamp(day(3), 21 * 60), location: 'Ramen place on Shattuck' },
      },
      {
        kind: 'draft_reply',
        title: 'Draft a yes to Maya',
        detail: 'She suggests the ramen place on Shattuck.',
        payload: { text: 'Saturday at 7 works — see you at the ramen place!' },
        status: 'skipped',
      },
    ],
    'mail-tri-demo-006': [
      {
        kind: 'calendar_block',
        title: 'Add Thursday office hours, 2–4 PM',
        detail: 'Office hours move to Thursday 2–4 PM in Dwinelle 3401 this week.',
        payload: { title: 'Office hours — Prof. Lindqvist', startAt: calTimestamp(day(1), 14 * 60), endAt: calTimestamp(day(1), 16 * 60), location: 'Dwinelle 3401' },
      },
    ],
    'mail-tri-demo-007': [
      {
        kind: 'draft_reply',
        title: 'Draft a reply with three times',
        detail: 'Northlight asks for three times that work next week.',
        payload: { text: 'Hello,\n\nThank you for considering me. These times work next week: [three times].\n\nBest,\nKaz' },
        source: 'body',
      },
      {
        kind: 'waiting_on',
        title: 'Follow up if Northlight has not replied by Monday',
        detail: 'Once you send times, the next move is theirs.',
        payload: { title: 'Follow up with Northlight about the interview', dueDate: day(5) },
        source: 'body',
      },
    ],
    'mail-tri-demo-008': [
      {
        kind: 'already_in_lifeos',
        title: 'Already a reminder',
        detail: 'Your reminder to email your advisor about the spring petition covers this.',
        payload: { what: 'task', id: 'task-advisor', title: 'Email advisor about the spring petition' },
      },
      {
        kind: 'mark_done',
        title: 'Mark done: nothing to do yet',
        detail: 'The mail only confirms the petition was received; a decision is expected within two weeks.',
        payload: { reason: 'Receipt only; nothing to do until the decision.' },
      },
    ],
    // ── /day?state=morning ──
    'mail-day-morning-001': [
      {
        kind: 'reminder',
        title: 'Send Rowan the translated subtitles today',
        detail: 'Rowan asks for the translated files by today.',
        payload: { title: 'Send Rowan the translated subtitle files', dueDate: day(0) },
        source: 'body',
      },
      {
        kind: 'draft_reply',
        title: 'Draft a status reply to Rowan',
        detail: 'The mail lists scenes the first pass skipped.',
        payload: { text: 'Hi Rowan,\n\nThe translations are nearly done; I am finishing the skipped scenes and will send the files today.\n\nKaz' },
        source: 'body',
      },
    ],
    'mail-day-morning-002': [
      {
        kind: 'open_link',
        title: 'Open the check-in',
        detail: "Check-in for today's 3:15 PM appointment is not done yet.",
        payload: { url: 'https://clinic.example/check-in', label: 'Clinic check-in' },
      },
    ],
    'mail-day-morning-005': [],
  }
}

/** The seeded proposals for one message, stamped a minute after it was sorted. */
function withDemoProposals(item: MailTriageItem, seeds: Record<string, DemoProposalSeed[]>): MailTriageItem {
  const list = seeds[item.id]
  if (!list || !item.triagedAt) return item
  const at = new Date(Date.parse(item.triagedAt) + 60_000).toISOString()
  const proposals: MailProposal[] = list.map((p, i) => {
    const status = p.status ?? 'proposed'
    return {
      id: `${item.id}-p${i + 1}`,
      messageId: item.id,
      kind: p.kind,
      title: p.title,
      detail: p.detail,
      payload: p.payload,
      status,
      source: p.source ?? 'snippet',
      createdAt: at,
      model: DEMO_TRIAGE_MODEL,
      promptVersion: 'proposals-demo-1',
      acceptedAt: status === 'accepted' ? at : null,
      skippedAt: status === 'skipped' ? at : null,
      result: null,
    }
  })
  return { ...item, proposals, proposalsAt: at }
}

function demoProposal(id: string): { item: MailTriageItem; proposal: MailProposal } {
  const all = [...demoTriageItems, ...Object.values(demoDayStores).flatMap((st) => st?.items ?? [])]
  for (const item of all) {
    const proposal = item.proposals.find((p) => p.id === id)
    if (proposal) return { item, proposal }
  }
  throw new Error('No next step with that id, for this owner.')
}

/** A second decision on the same proposal refuses, the way a double-tap must. */
function demoProposalRefusal(p: MailProposal): string {
  return p.status === 'accepted' ? 'You already accepted this one.' : 'You already skipped this one.'
}
